import { styled, alpha, keyframes } from '@mui/material/styles';
import { Box, Button, Typography } from '@mui/material';

// ============================================================================
// ACCENT COLORS - PowerPoint warm orange theme
// ============================================================================

const ORANGE_ACCENT = '#B83B1D';       // PowerPoint primary (warm terracotta)
const EMERALD_ACCENT = '#B83B1D';      // Unified accent (same as primary)
const ORANGE_ACCENT_LIGHT = '#D4694E'; // Light variant for dark mode
const ORANGE_ACCENT_RGB = '184, 59, 29';

const getAccentColor = (theme) => theme.palette.accentBubble?.warm || (theme.palette.mode === 'dark' ? ORANGE_ACCENT_LIGHT : ORANGE_ACCENT);
const getAccentRgb = (theme) => theme.palette.accentBubble?.warmRgb || (theme.palette.mode === 'dark' ? '212, 105, 78' : ORANGE_ACCENT_RGB);

// ============================================================================
// GRADIENT COLORS - Professional warm gradients
// ============================================================================

// Primary accent gradient (PP orange → warm light) - used for CTAs, highlights, text
const ACCENT_GRADIENT = `linear-gradient(135deg, #B83B1D 0%, #D4694E 100%)`;
const ACCENT_GRADIENT_HOVER = `linear-gradient(135deg, #D4694E 0%, #B83B1D 100%)`;
const ACCENT_GRADIENT_SOFT = 'linear-gradient(135deg, rgba(184, 59, 29, 0.12) 0%, rgba(212, 105, 78, 0.12) 100%)';

// Gradient text helper styles.
// inline-block + small horizontal padding fixes the italic-clipping bug
// you'd otherwise see on any italic glyph using background-clip: text —
// the browser masks the gradient to the typography metrics box, which
// the leading edge of italic capitals (A, T, V) overshoots. Applying
// this on the helper means every gradient-text usage on the page gets
// the fix automatically rather than us having to remember to add it
// per-component.
const gradientTextStyles = {
  background: ACCENT_GRADIENT,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  display: 'inline-block',
  paddingInline: '0.06em',
};

// Brand-unification pass: previously the page mixed rust, cyan
// (#06B6D4 / #3B82F6), purple (#8B5CF6 / #6366F1), and lime (#65A30D).
// Collapsed to a single icon treatment — rust icon on a soft salmon
// (rust-tinted) surface — across every Bento, Security, and capability
// card. Legacy variant names (cyan/purple/green/orange) remain as
// semantic labels but all map to the same visual.
const GRADIENT_CYAN = ACCENT_GRADIENT;
const GRADIENT_CYAN_SOFT = ACCENT_GRADIENT_SOFT;

const GRADIENT_GREEN = ACCENT_GRADIENT;
const GRADIENT_GREEN_SOFT = ACCENT_GRADIENT_SOFT;

const GRADIENT_PURPLE = ACCENT_GRADIENT;
const GRADIENT_PURPLE_SOFT = ACCENT_GRADIENT_SOFT;

// Orange/Amber gradient (for action/CTA) - legacy alias
const GRADIENT_ORANGE = ACCENT_GRADIENT;
const GRADIENT_ORANGE_SOFT = ACCENT_GRADIENT_SOFT;

// ============================================================================
// KEYFRAME ANIMATIONS
// ============================================================================

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const rotateIn = keyframes`
  from {
    opacity: 0;
    transform: rotate(-5deg) scale(0.95);
  }
  to {
    opacity: 1;
    transform: rotate(0) scale(1);
  }
`;

// Subtle icon pulse for capability icons
const iconPulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

// Security shield glow effect
const shieldGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(184, 59, 29, 0.2);
  }
  50% {
    box-shadow: 0 0 20px 4px rgba(184, 59, 29, 0.15);
  }
`;

// Card subtle float
const cardFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

// Chart line drawing animation
const drawLine = keyframes`
  0% { stroke-dashoffset: 400; }
  100% { stroke-dashoffset: 0; }
`;

// Chart area fade in
const fillArea = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;

// Export button pop
const buttonPop = keyframes`
  0% { opacity: 0; transform: scale(0.8) translateY(10px); }
  60% { transform: scale(1.05) translateY(-2px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
`;

// RFP arrow flow
const arrowFlow = keyframes`
  0% { opacity: 0.3; }
  50% { opacity: 1; }
  100% { opacity: 0.3; }
`;

// Target card slide in
const slideInRight = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;

// Quote typing cursor
const quoteCursor = keyframes`
  0%, 50% { border-right-color: currentColor; }
  51%, 100% { border-right-color: transparent; }
`;

// Security checkmark appear
const checkmarkAppear = keyframes`
  0% { transform: scale(0) rotate(-45deg); opacity: 0; }
  50% { transform: scale(1.2) rotate(0deg); }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

// Pricing feature reveal
const featureReveal = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;

// ============================================================================
// HOW IT WORKS ANIMATION KEYFRAMES
// ============================================================================

// Stage 1: Typing animation
const typeText = keyframes`
  0% { width: 0; }
  80% { width: 100%; }
  100% { width: 100%; }
`;

const cursorBlink = keyframes`
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
`;

// Stage 2: Research dots pulse
const researchPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.4);
    opacity: 1;
  }
`;

// Stage 3: Wireframe drawing
const drawWireframe = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const wireframeLineGrow = keyframes`
  0% { transform: scaleX(0); }
  100% { transform: scaleX(1); }
`;

// Stage 4: Slides materialize
const slidePopIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.8) translateY(15px);
  }
  70% {
    opacity: 1;
    transform: scale(1.03) translateY(-3px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const slideGlow = keyframes`
  0%, 100% { box-shadow: 0 4px 16px rgba(184, 59, 29, 0.08); }
  50% { box-shadow: 0 6px 24px rgba(184, 59, 29, 0.18); }
`;

// Stage visibility transitions
const stageIn = keyframes`
  0% { opacity: 0; transform: scale(0.96); }
  100% { opacity: 1; transform: scale(1); }
`;

const stageOut = keyframes`
  0% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(1.04); }
`;

// Animated gradient border rotation for How It Works container
const gradientBorderRotate = keyframes`
  0% { --gradient-angle: 0deg; }
  100% { --gradient-angle: 360deg; }
`;

// Progress bar animation for How It Works (5 stages)
const stageProgress = keyframes`
  0% { width: 0%; }
  15% { width: 20%; }
  30% { width: 40%; }
  50% { width: 60%; }
  70% { width: 80%; }
  90% { width: 100%; }
  100% { width: 100%; }
`;

// ============================================================================
// LAYOUT COMPONENTS
// ============================================================================

export const LandingContainer = styled(Box)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  color: theme.palette.text.primary,
  lineHeight: 1.6,
  // Enable scrolling for landing page (overrides global overflow: hidden)
  height: '100vh',
  overflowY: 'auto',
  overflowX: 'hidden',
  WebkitFontSmoothing: 'antialiased',
  '& *, & *::before, & *::after': {
    boxSizing: 'border-box',
  },
  // Background styling - matches HomeScreen texture
  ...(theme.palette.mode === 'dark' ? {
    // Dark mode: warm consulting-style ambient glows (matching HomeScreen)
    background: [
      // Primary warm glow - top center (stronger)
      'radial-gradient(ellipse 100% 55% at 50% -15%, rgba(184, 59, 29, 0.12) 0%, transparent 50%)',
      // Secondary warm glow - top left
      'radial-gradient(ellipse 70% 45% at 5% 0%, rgba(184, 59, 29, 0.06) 0%, transparent 45%)',
      // Tertiary warm glow - top right
      'radial-gradient(ellipse 55% 40% at 95% 5%, rgba(212, 105, 78, 0.05) 0%, transparent 40%)',
      // Soft middle ambient - subtle warmth in center
      'radial-gradient(ellipse 120% 60% at 50% 50%, rgba(184, 59, 29, 0.025) 0%, transparent 60%)',
      // Bottom warm ambient
      'radial-gradient(ellipse 70% 35% at 50% 105%, rgba(212, 105, 78, 0.04) 0%, transparent 45%)',
      // Base gradient - warm brown-black
      'linear-gradient(180deg, #1a1612 0%, #1a1612 100%)',
    ].join(', '),
  } : {
    // Light mode: sophisticated warm consulting-style gradients (matching HomeScreen)
    background: [
      // Primary warm ambient glow - top center
      'radial-gradient(ellipse 90% 50% at 50% -15%, rgba(184, 59, 29, 0.055) 0%, transparent 50%)',
      // Secondary warm glow - top left
      'radial-gradient(ellipse 60% 40% at 5% 0%, rgba(184, 59, 29, 0.04) 0%, transparent 45%)',
      // Tertiary warm glow - top right
      'radial-gradient(ellipse 50% 35% at 95% 5%, rgba(212, 105, 78, 0.035) 0%, transparent 40%)',
      // Soft middle ambient - subtle warmth in center
      'radial-gradient(ellipse 120% 60% at 50% 50%, rgba(184, 59, 29, 0.018) 0%, transparent 60%)',
      // Subtle cool accent - bottom for balance
      'radial-gradient(ellipse 60% 40% at 50% 105%, rgba(100, 116, 139, 0.02) 0%, transparent 45%)',
      // Base gradient - warm ivory tones with refined paper feel
      'linear-gradient(180deg, #fafaf9 0%, #f8f7f5 35%, #f5f4f1 65%, #f3f2ef 100%)',
    ].join(', '),
  }),
  // Premium grain/noise texture overlay
  '&::after': {
    content: '""',
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    opacity: theme.palette.mode === 'dark' ? 0.025 : 0.018,
    mixBlendMode: theme.palette.mode === 'dark' ? 'overlay' : 'multiply',
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
    zIndex: 9999,
  },
}));

export const Section = styled('section', {
  shouldForwardProp: (prop) => prop !== 'withGrid',
})(({ theme, withGrid }) => ({
  padding: '84px 0',
  position: 'relative',
  // Alternating subtle backgrounds + hairline dividers for readability
  borderTop: `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.05) : alpha('#1e1612', 0.05)}`,
  '&:nth-of-type(even)': {
    background: theme.palette.mode === 'dark'
      ? alpha('#ffffff', 0.022)
      : alpha('#1e1612', 0.022),
  },
  ...(withGrid && {
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundImage: `
        linear-gradient(to right, ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.05) : alpha('#1e1612', 0.04)} 1px, transparent 1px),
        linear-gradient(to bottom, ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.05) : alpha('#1e1612', 0.04)} 1px, transparent 1px)
      `,
      backgroundSize: '52px 52px',
      WebkitMaskImage: 'radial-gradient(ellipse 75% 60% at 50% 50%, #000 15%, transparent 75%)',
      maskImage: 'radial-gradient(ellipse 75% 60% at 50% 50%, #000 15%, transparent 75%)',
      pointerEvents: 'none',
      zIndex: 0,
    },
  }),
  [theme.breakpoints.down('md')]: {
    padding: '60px 0',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '44px 0',
  },
}));

export const Container = styled(Box)(({ theme }) => ({
  maxWidth: 1280,
  margin: '0 auto',
  padding: '0 24px',
  boxSizing: 'border-box',
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.down('sm')]: {
    padding: '0 16px',
    overflow: 'hidden',  // Prevent content bleeding on mobile
    maxWidth: '100vw',
  },
}));

// ============================================================================
// TYPOGRAPHY COMPONENTS
// ============================================================================

export const SectionEyebrow = styled(Typography)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: '0.8125rem',
  fontWeight: 600,
  ...gradientTextStyles,
  marginBottom: theme.spacing(2),
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
}));

export const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(24px, 3.6vw, 38px)',
  fontWeight: 600,
  lineHeight: 1.18,
  marginBottom: theme.spacing(2),
  letterSpacing: '-0.025em',
  color: theme.palette.text.primary,
  // Highlighted words inside section titles. Previously serif italic
  // (Playfair); switched to the same sans (Inter) in the brand accent
  // color, which reads cleaner and highlights more clearly than the
  // serif did.
  '& em, & .serif': {
    fontFamily: 'inherit',
    fontStyle: 'normal',
    fontWeight: 700,
    color: '#B83B1D',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 'clamp(22px, 5.5vw, 28px)',
    marginBottom: theme.spacing(1.75),
  },
}));

export const SectionSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: theme.palette.text.secondary,
  maxWidth: 600,
  lineHeight: 1.65,
  fontWeight: 400,
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9375rem',
    lineHeight: 1.6,
    padding: '0 8px',
  },
}));

// ============================================================================
// HERO SECTION
// ============================================================================

export const HeroSection = styled('section')(({ theme }) => ({
  minHeight: '90vh',
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  padding: '120px 0 80px',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    minHeight: 'auto',
    padding: '100px 0 60px',
  },
}));

export const HeroContent = styled(Box)(({ theme }) => ({
  maxWidth: 800,
  margin: '0 auto',
  textAlign: 'center',
  position: 'relative',
  zIndex: 1,
}));

export const HeroEyebrow = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: `${theme.spacing(1)} ${theme.spacing(2.5)}`,
  background: alpha(EMERALD_ACCENT, 0.1),
  borderRadius: 100,
  marginBottom: theme.spacing(4),
  animation: `${fadeInUp} 0.6s ease`,
}));

export const HeroEyebrowText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 600,
  ...gradientTextStyles,
}));

export const HeroTitle = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(40px, 6vw, 72px)',
  fontWeight: 700,
  lineHeight: 1.05,
  marginBottom: theme.spacing(3),
  letterSpacing: '-0.03em',
  ...gradientTextStyles,
  animation: `${fadeInUp} 0.6s ease 0.1s both`,
}));

export const HeroSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(1.125rem, 2vw, 1.375rem)',
  color: theme.palette.text.secondary,
  maxWidth: 600,
  margin: '0 auto',
  marginBottom: theme.spacing(5),
  lineHeight: 1.7,
  animation: `${fadeInUp} 0.6s ease 0.2s both`,
}));

export const HeroActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  justifyContent: 'center',
  flexWrap: 'wrap',
  marginBottom: theme.spacing(6),
  animation: `${fadeInUp} 0.6s ease 0.3s both`,
}));

export const HeroPrimaryButton = styled(Button)(({ theme }) => ({
  padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
  borderRadius: 999,
  fontWeight: 600,
  fontSize: '1rem',
  textTransform: 'none',
  background: ACCENT_GRADIENT,
  color: '#ffffff',
  border: 'none',
  boxShadow: `0 8px 32px ${alpha(EMERALD_ACCENT, 0.25)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 40px ${alpha(EMERALD_ACCENT, 0.3)}`,
  },
  '& .arrow': {
    marginLeft: theme.spacing(1),
    transition: 'transform 0.3s ease',
  },
  '&:hover .arrow': {
    transform: 'translateX(4px)',
  },
}));

export const HeroSecondaryButton = styled(Button)(({ theme }) => ({
  padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
  borderRadius: 999,
  fontWeight: 600,
  fontSize: '1rem',
  textTransform: 'none',
  background: 'transparent',
  color: theme.palette.text.primary,
  border: `1px solid ${alpha(getAccentColor(theme), 0.2)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: EMERALD_ACCENT,
    background: alpha(EMERALD_ACCENT, 0.05),
  },
}));

export const HeroTrust = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  animation: `${fadeInUp} 0.6s ease 0.4s both`,
}));

export const HeroTrustText = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  fontWeight: 500,
}));

export const HeroTrustLogos = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(4),
  flexWrap: 'wrap',
  justifyContent: 'center',
  opacity: 0.6,
  '& span': {
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    letterSpacing: '-0.01em',
  },
}));

// ============================================================================
// CARD COMPONENTS
// ============================================================================

export const BaseCard = styled(Box)(({ theme }) => ({
  // Glassmorphism effect
  background: theme.palette.mode === 'dark'
    ? 'rgba(30, 30, 30, 0.7)'
    : 'rgba(255, 255, 255, 0.75)',
  backdropFilter: 'blur(16px) saturate(180%)',
  WebkitBackdropFilter: 'blur(16px) saturate(180%)',
  border: `1px solid ${theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.08)'
    : 'rgba(0, 0, 0, 0.06)'}`,
  borderRadius: theme.restack?.tokens?.radii?.lg || 20,
  padding: theme.spacing(4),
  transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
  position: 'relative',
  height: '100%',
  '&:hover': {
    borderColor: alpha(EMERALD_ACCENT, 0.4),
    boxShadow: `0 12px 40px ${alpha(EMERALD_ACCENT, 0.15)}`,
    transform: 'translateY(-4px)',
    background: theme.palette.mode === 'dark'
      ? 'rgba(35, 35, 35, 0.8)'
      : 'rgba(255, 255, 255, 0.85)',
  },
  // Fallback for browsers without backdrop-filter support
  '@supports not (backdrop-filter: blur(16px))': {
    background: theme.palette.background.paper,
  },
}));

export const AgentCard = styled(BaseCard)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  minHeight: 200,
}));

export const AgentIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme, variant }) => {
  // Warm color palette to match the orange theme
  const variants = {
    blue: { bg: alpha('#D97706', 0.12), color: '#D97706' },      // Warm amber (research)
    purple: { bg: alpha('#DC2626', 0.12), color: '#DC2626' },    // Warm coral/rose (framework)
    green: { bg: alpha('#B83B1D', 0.12), color: '#B83B1D' },     // Warm olive/sage (design)
    orange: { bg: alpha(EMERALD_ACCENT, 0.12), color: EMERALD_ACCENT },
  };

  const v = variants[variant] || variants.blue;

  return {
    width: 52,
    height: 52,
    borderRadius: theme.restack?.tokens?.radii?.md || 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2.5),
    background: v.bg,
    transition: 'transform 0.3s ease',
    '& svg': {
      fontSize: 26,
      color: v.color,
    },
    '.MuiBox-root:hover &': {
      transform: 'scale(1.05)',
    },
  };
});

export const AgentTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  marginBottom: theme.spacing(1),
  color: theme.palette.text.primary,
}));

export const AgentDesc = styled(Typography)(({ theme }) => ({
  fontSize: '0.9rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
  flex: 1,
}));

// ============================================================================
// GRID LAYOUTS
// ============================================================================

export const AgentsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(10),
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),
  },
}));

// ============================================================================
// PROCESS ACCORDION
// ============================================================================

export const ProcessSection = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1.2fr',
  gap: theme.spacing(7.5),
  alignItems: 'start',
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const ProcessAccordion = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
});

export const ProcessItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})(({ theme, isActive }) => ({
  borderRadius: theme.restack?.tokens?.radii?.md || 16,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  padding: `0 ${theme.spacing(2.5)}`,
  marginBottom: theme.spacing(1),
  border: `1px solid transparent`,
  ...(isActive && {
    background: alpha(EMERALD_ACCENT, 0.04),
    borderLeftWidth: 4,
    borderLeftStyle: 'solid',
    borderLeftColor: EMERALD_ACCENT,
    borderColor: alpha(EMERALD_ACCENT, 0.12),
  }),
  '&:hover': {
    background: alpha(getAccentColor(theme), 0.03),
  },
}));

export const ProcessHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2.5),
  padding: `${theme.spacing(3)} 0`,
  transition: 'all 0.3s ease',
}));

export const ProcessNumber = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})(({ theme, isActive }) => ({
  fontSize: '1rem',
  fontWeight: 600,
  color: isActive ? EMERALD_ACCENT : alpha(theme.palette.text.secondary, 0.6),
  minWidth: 36,
  flexShrink: 0,
  transition: 'all 0.3s ease',
  fontFamily: 'monospace',
  letterSpacing: '-0.02em',
}));

export const ProcessTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})(({ theme, isActive }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  color: isActive ? EMERALD_ACCENT : theme.palette.text.primary,
  transition: 'color 0.3s ease',
}));

export const ProcessContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})(({ theme, isActive }) => ({
  maxHeight: isActive ? 200 : 0,
  overflow: 'hidden',
  transition: 'max-height 0.5s cubic-bezier(0.16, 1, 0.3, 1), padding 0.3s ease',
  padding: isActive ? `0 0 ${theme.spacing(3)} ${theme.spacing(6)}` : `0 0 0 ${theme.spacing(6)}`,
}));

// Mobile-only visual that shows inline within each ProcessItem
export const ProcessMobileVisual = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})(({ theme, isActive }) => ({
  display: 'none',
  [theme.breakpoints.down('lg')]: {
    display: isActive ? 'block' : 'none',
    maxHeight: isActive ? 400 : 0,
    overflow: 'hidden',
    transition: 'max-height 0.5s cubic-bezier(0.16, 1, 0.3, 1), padding 0.3s ease, opacity 0.3s ease',
    opacity: isActive ? 1 : 0,
    padding: isActive ? `${theme.spacing(3)} ${theme.spacing(2)}` : 0,
    marginLeft: 0,
    marginTop: theme.spacing(2),
    // Card-like container matching capabilities section
    background: isActive ? theme.palette.background.paper : 'transparent',
    borderRadius: theme.restack?.tokens?.radii?.lg || 20,
    border: isActive ? `1px solid ${alpha(EMERALD_ACCENT, 0.12)}` : 'none',
    '& > *': {
      maxWidth: '100%',
      margin: '0 auto',
      boxShadow: 'none',  // Remove inner shadow since container has border
      animation: 'none',  // Remove float animation on mobile
    }
  },
}));

export const ProcessDesc = styled(Typography)(({ theme }) => ({
  fontSize: '0.9375rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.7,
  marginBottom: theme.spacing(2),
}));

export const ProcessTags = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
}));

export const ProcessTag = styled(Box)(({ theme }) => ({
  padding: `${theme.spacing(0.75)} ${theme.spacing(1.75)}`,
  background: ACCENT_GRADIENT_SOFT,
  borderRadius: 100,
  fontSize: '0.8125rem',
  fontWeight: 500,
  ...gradientTextStyles,
}));

// ============================================================================
// PROCESS VISUAL
// ============================================================================

export const ProcessVisual = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.02) : alpha('#000000', 0.02),
  borderRadius: theme.restack?.tokens?.radii?.lg || 20,
  padding: theme.spacing(6),
  position: 'sticky',
  top: 100,
  minHeight: 480,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  border: `1px solid ${alpha(getAccentColor(theme), 0.08)}`,
  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
}));

export const VisualContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})(({ isActive }) => ({
  display: isActive ? 'block' : 'none',
  animation: isActive ? `${scaleIn} 0.6s cubic-bezier(0.16, 1, 0.3, 1)` : 'none',
  width: '100%',
  maxWidth: 420,
}));

export const VisualMockup = styled(Box)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: theme.restack?.tokens?.radii?.md || 16,
  padding: theme.spacing(4),
  boxShadow: `0 25px 80px ${alpha('#000', 0.18)}, 0 10px 30px ${alpha('#000', 0.1)}`,
  width: '100%',
  border: `1px solid ${alpha(getAccentColor(theme), 0.1)}`,
  animation: `${float} 6s ease-in-out infinite`,
}));

export const MockupHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.75),
  marginBottom: theme.spacing(2),
}));

export const MockupDot = styled(Box)(({ theme }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  '&:nth-of-type(1)': { background: '#FF5F57' },
  '&:nth-of-type(2)': { background: '#FFBD2E' },
  '&:nth-of-type(3)': { background: '#28CA41' },
}));

export const MockupContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

export const MockupLine = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'width',
})(({ theme, width = '100%' }) => ({
  height: 14,
  background: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.08) : alpha('#000000', 0.06),
  borderRadius: 7,
  width,
  animation: `${shimmer} 2s ease-in-out infinite`,
  animationDelay: 'var(--delay, 0s)',
  '&:nth-of-type(1)': { '--delay': '0s' },
  '&:nth-of-type(2)': { '--delay': '0.1s' },
  '&:nth-of-type(3)': { '--delay': '0.2s' },
}));

export const MockupInput = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2.5),
  background: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.04) : alpha('#000000', 0.02),
  borderRadius: theme.restack?.tokens?.radii?.sm || 12,
  fontSize: '0.9375rem',
  color: theme.palette.text.secondary,
  fontStyle: 'italic',
  borderLeft: `4px solid ${EMERALD_ACCENT}`,
  animation: `${fadeInUp} 0.5s ease 0.3s both`,
}));

export const MockupBadges = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2.5),
  flexWrap: 'wrap',
}));

export const MockupBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme, variant }) => ({
  padding: `${theme.spacing(1.25)} ${theme.spacing(2.5)}`,
  background: variant === 'outline'
    ? 'transparent'
    : ACCENT_GRADIENT,
  border: variant === 'outline' ? `1px solid ${EMERALD_ACCENT}` : 'none',
  borderRadius: 100,
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: variant === 'outline' ? EMERALD_ACCENT : '#ffffff',
  animation: `${scaleIn} 0.4s ease both`,
  animationDelay: 'var(--delay, 0s)',
  '&:nth-of-type(1)': { '--delay': '0.2s' },
  '&:nth-of-type(2)': { '--delay': '0.3s' },
  '&:nth-of-type(3)': { '--delay': '0.4s' },
}));

export const MockupResult = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(2.5),
  background: alpha('#B83B1D', 0.12),
  borderRadius: theme.restack?.tokens?.radii?.sm || 12,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  fontSize: '0.9375rem',
  fontWeight: 600,
  color: '#B83B1D',
  animation: `${fadeInUp} 0.5s ease 0.5s both`,
  '& svg': {
    animation: `${pulse} 2s ease-in-out infinite`,
  },
}));

// ============================================================================
// FRAMEWORKS SECTION
// ============================================================================

export const FrameworkTabs = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(6),
  flexWrap: 'wrap',
}));

export const FrameworkTab = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})(({ theme, isActive }) => ({
  padding: `${theme.spacing(1.5)} ${theme.spacing(3)}`,
  background: isActive ? EMERALD_ACCENT : theme.palette.background.paper,
  border: `1px solid ${isActive ? EMERALD_ACCENT : alpha(getAccentColor(theme), 0.15)}`,
  borderRadius: 999,
  fontSize: '0.875rem',
  fontWeight: 500,
  color: isActive ? '#ffffff' : theme.palette.text.secondary,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  textTransform: 'none',
  minWidth: 'auto',
  '&:hover': {
    borderColor: EMERALD_ACCENT,
    background: isActive ? EMERALD_ACCENT : alpha(EMERALD_ACCENT, 0.05),
    color: isActive ? '#ffffff' : EMERALD_ACCENT,
  },
}));

export const FrameworkDisplay = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})(({ theme, isActive }) => ({
  display: isActive ? 'grid' : 'none',
  gridTemplateColumns: '1fr 1.2fr',
  gap: theme.spacing(7.5),
  alignItems: 'center',
  background: theme.palette.background.paper,
  border: `1px solid ${alpha(getAccentColor(theme), 0.12)}`,
  borderRadius: theme.restack?.tokens?.radii?.lg || 20,
  padding: theme.spacing(6),
  animation: isActive ? `${fadeIn} 0.5s ease` : 'none',
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const FrameworkInfo = styled(Box)({});

export const FrameworkTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem',
  fontWeight: 600,
  marginBottom: theme.spacing(1.5),
  color: theme.palette.text.primary,
}));

export const FrameworkDescription = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.7,
  marginBottom: theme.spacing(3),
}));

export const FrameworkFeatures = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
}));

export const FrameworkFeature = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

export const FrameworkFeatureIcon = styled(Box)(({ theme }) => ({
  width: 22,
  height: 22,
  borderRadius: '50%',
  background: alpha('#B83B1D', 0.12),
  color: '#B83B1D',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& svg': {
    fontSize: 14,
  },
}));

export const FrameworkVisual = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 300,
}));

// ============================================================================
// FRAMEWORK FUNNEL - "Frameworks baked into AI thinking"
// ============================================================================

export const FrameworkFunnel = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(4),
  padding: theme.spacing(6),
  background: theme.palette.background.paper,
  border: `1px solid ${alpha(getAccentColor(theme), 0.12)}`,
  borderRadius: theme.restack?.tokens?.radii?.lg || 20,
}));

export const FrameworkGraphicsGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  justifyContent: 'center',
  alignItems: 'flex-end',
  gap: theme.spacing(2.5),
  width: '100%',
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(1.5),
  },
}));

export const FrameworkGraphicCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2.5),
  width: 150,
  height: 150,
  background: theme.palette.background.paper,
  border: `1px solid ${alpha(getAccentColor(theme), 0.15)}`,
  borderRadius: theme.restack?.tokens?.radii?.md || 16,
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  boxShadow: `0 2px 8px ${alpha('#000', 0.04)}`,
  '&:hover': {
    borderColor: alpha(EMERALD_ACCENT, 0.4),
    background: alpha(EMERALD_ACCENT, 0.03),
    transform: 'translateY(-4px)',
    boxShadow: `0 8px 24px ${alpha(EMERALD_ACCENT, 0.12)}`,
  },
}));

export const FrameworkGraphicLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.6875rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textAlign: 'center',
  lineHeight: 1.2,
}));

export const FrameworkMoreCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2.5),
  width: 150,
  height: 150,
  background: alpha(EMERALD_ACCENT, 0.06),
  border: `2px dashed ${alpha(EMERALD_ACCENT, 0.25)}`,
  borderRadius: theme.restack?.tokens?.radii?.md || 16,
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: alpha(EMERALD_ACCENT, 0.5),
    background: alpha(EMERALD_ACCENT, 0.1),
  },
  '& .plus': {
    fontSize: '2rem',
    fontWeight: 300,
    ...gradientTextStyles,
    lineHeight: 1,
  },
  '& .text': {
    fontSize: '0.75rem',
    fontWeight: 500,
    ...gradientTextStyles,
    textAlign: 'center',
  },
}));

export const FunnelFlow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: alpha(EMERALD_ACCENT, 0.4),
  fontSize: '1.5rem',
  '& span': {
    animation: `${pulse} 1.5s ease-in-out infinite`,
    '&:nth-of-type(1)': { animationDelay: '0s' },
    '&:nth-of-type(2)': { animationDelay: '0.2s' },
    '&:nth-of-type(3)': { animationDelay: '0.4s' },
  },
}));

export const FrameworkInputs = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: theme.spacing(1.5),
  maxWidth: 600,
}));

export const FrameworkPill = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'delay',
})(({ theme, delay = 0 }) => ({
  padding: `${theme.spacing(1.25)} ${theme.spacing(2.5)}`,
  background: ACCENT_GRADIENT_SOFT,
  border: `1px solid ${alpha(EMERALD_ACCENT, 0.2)}`,
  borderRadius: 100,
  fontSize: '0.8125rem',
  fontWeight: 600,
  ...gradientTextStyles,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  transition: 'all 0.3s ease',
  cursor: 'default',
  '& span': {
    fontSize: '1rem',
  },
  '&:hover': {
    background: alpha(EMERALD_ACCENT, 0.15),
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 12px ${alpha(EMERALD_ACCENT, 0.15)}`,
  },
}));

export const AgentProcessor = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  padding: theme.spacing(3),
  background: `linear-gradient(135deg, ${alpha(EMERALD_ACCENT, 0.05)} 0%, ${alpha(EMERALD_ACCENT, 0.12)} 100%)`,
  border: `2px solid ${EMERALD_ACCENT}`,
  borderRadius: theme.restack?.tokens?.radii?.md || 16,
  position: 'relative',
  '&::before': {
    content: '"AI"',
    position: 'absolute',
    top: -12,
    left: 20,
    background: ACCENT_GRADIENT,
    color: '#fff',
    padding: '2px 10px',
    borderRadius: 4,
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
  },
}));

export const ProcessorIcon = styled(Box)(({ theme }) => ({
  width: 56,
  height: 56,
  borderRadius: '50%',
  background: ACCENT_GRADIENT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: `0 8px 24px ${alpha(EMERALD_ACCENT, 0.2)}`,
  animation: `${pulse} 3s ease-in-out infinite`,
  '& svg': {
    fontSize: 28,
    color: '#ffffff',
  },
}));

export const ProcessorText = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

export const ProcessorTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

export const ProcessorDesc = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  color: theme.palette.text.secondary,
}));

export const OutputArrow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: '#B83B1D',
  fontSize: '1.5rem',
  '& span': {
    animation: `${pulse} 1.5s ease-in-out infinite`,
    '&:nth-of-type(1)': { animationDelay: '0s' },
    '&:nth-of-type(2)': { animationDelay: '0.2s' },
  },
}));

export const OutputPreview = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2.5),
  background: alpha('#B83B1D', 0.08),
  border: `1px solid ${alpha('#B83B1D', 0.2)}`,
  borderRadius: theme.restack?.tokens?.radii?.md || 16,
}));

export const OutputSlides = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
}));

export const OutputSlide = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'delay',
})(({ theme, delay = 0 }) => ({
  width: 48,
  height: 36,
  background: theme.palette.background.paper,
  border: `1px solid ${alpha('#B83B1D', 0.3)}`,
  borderRadius: 6,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.625rem',
  fontWeight: 700,
  color: '#B83B1D',
  animation: `${scaleIn} 0.4s ease both`,
  animationDelay: `${delay}s`,
}));

export const OutputLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: '#B83B1D',
}));

// ============================================================================
// BETTER CHART VISUALIZATION
// ============================================================================

export const LineChartContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: 120,
  marginTop: 'auto',
  paddingTop: theme.spacing(2),
}));

export const ChartGrid = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 30,
  right: 0,
  bottom: 20,
  borderLeft: `1px solid ${alpha(theme.palette.text.secondary, 0.1)}`,
  borderBottom: `1px solid ${alpha(theme.palette.text.secondary, 0.1)}`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '33%',
    left: 0,
    right: 0,
    borderTop: `1px dashed ${alpha(theme.palette.text.secondary, 0.08)}`,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '66%',
    left: 0,
    right: 0,
    borderTop: `1px dashed ${alpha(theme.palette.text.secondary, 0.08)}`,
  },
}));

export const ChartLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 30,
  right: 0,
  bottom: 20,
  '& svg': {
    width: '100%',
    height: '100%',
  },
  '& path:first-of-type': {
    strokeDasharray: 400,
    strokeDashoffset: 400,
    animation: `${drawLine} 2s ease-out forwards`,
  },
  '& path:last-of-type': {
    opacity: 0,
    animation: `${fillArea} 1s ease-out 1.5s forwards`,
  },
  '@media (prefers-reduced-motion: reduce)': {
    '& path:first-of-type': {
      animation: 'none',
      strokeDashoffset: 0,
    },
    '& path:last-of-type': {
      animation: 'none',
      opacity: 1,
    },
  },
}));

export const ChartYAxis = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 20,
  width: 25,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  paddingRight: 4,
  fontSize: '0.5625rem',
  color: alpha(theme.palette.text.secondary, 0.6),
  fontWeight: 500,
}));

export const ChartXAxis = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 30,
  right: 0,
  height: 16,
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: '0.5625rem',
  color: alpha(theme.palette.text.secondary, 0.6),
  fontWeight: 500,
}));

export const ChartDot = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: 8,
  height: 8,
  borderRadius: '50%',
  background: EMERALD_ACCENT,
  border: `2px solid ${theme.palette.background.paper}`,
  boxShadow: `0 2px 8px ${alpha(EMERALD_ACCENT, 0.25)}`,
  transform: 'translate(-50%, -50%)',
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'translate(-50%, -50%) scale(1.5)',
  },
}));

// ============================================================================
// FRAMEWORK DIAGRAMS
// ============================================================================

// Porter's Five Forces
export const PorterDiagram = styled(Box)({
  position: 'relative',
  width: 320,
  height: 320,
});

export const PorterCenter = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 100,
  height: 100,
  background: ACCENT_GRADIENT,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: '#ffffff',
  boxShadow: `0 8px 32px ${alpha(EMERALD_ACCENT, 0.25)}`,
  zIndex: 2,
  animation: `${pulse} 3s ease-in-out infinite`,
}));

export const PorterForce = styled(Box, {
  shouldForwardProp: (prop) => !['position'].includes(prop),
})(({ theme, position }) => {
  const positions = {
    top: { top: 0, left: '50%', transform: 'translateX(-50%)' },
    bottom: { bottom: 0, left: '50%', transform: 'translateX(-50%)' },
    left: { left: 0, top: '50%', transform: 'translateY(-50%)' },
    right: { right: 0, top: '50%', transform: 'translateY(-50%)' },
  };

  return {
    position: 'absolute',
    ...positions[position],
    padding: `${theme.spacing(1.75)} ${theme.spacing(2.5)}`,
    background: theme.palette.background.paper,
    border: `1px solid ${alpha(getAccentColor(theme), 0.15)}`,
    borderRadius: theme.restack?.tokens?.radii?.md || 16,
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    boxShadow: `0 4px 12px ${alpha('#000', 0.08)}`,
    transition: 'all 0.4s ease',
    '&:hover': {
      borderColor: EMERALD_ACCENT,
      color: EMERALD_ACCENT,
      transform: position === 'top' || position === 'bottom'
        ? 'translateX(-50%) scale(1.05)'
        : 'translateY(-50%) scale(1.05)',
      boxShadow: `0 8px 24px ${alpha(EMERALD_ACCENT, 0.2)}`,
    },
  };
});

export const PorterLine = styled(Box, {
  shouldForwardProp: (prop) => !['direction', 'position'].includes(prop),
})(({ theme, direction, position }) => {
  const isHorizontal = direction === 'horizontal';

  return {
    position: 'absolute',
    background: `linear-gradient(${isHorizontal ? '90deg' : '180deg'}, transparent 0%, ${alpha(EMERALD_ACCENT, 0.3)} 50%, transparent 100%)`,
    zIndex: 1,
    ...(isHorizontal ? {
      height: 2,
      width: 70,
      top: '50%',
      transform: 'translateY(-50%)',
      ...(position === 'left' ? { left: 50 } : { right: 50 }),
    } : {
      width: 2,
      height: 70,
      left: '50%',
      transform: 'translateX(-50%)',
      ...(position === 'top' ? { top: 50 } : { bottom: 50 }),
    }),
  };
});

// SWOT Grid
export const SwotGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: theme.spacing(2),
  width: 320,
}));

export const SwotCell = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme, variant }) => {
  const variants = {
    s: { bg: 'linear-gradient(135deg, #FEF2F0 0%, #FCDCD6 100%)', color: '#B83B1D' },
    w: { bg: 'linear-gradient(135deg, #FEF2F2 0%, #FECACA 100%)', color: '#DC2626' },
    o: { bg: 'linear-gradient(135deg, #FFFBEB 0%, #FDE68A 100%)', color: '#D97706' },
    t: { bg: `linear-gradient(135deg, ${alpha(EMERALD_ACCENT, 0.1)} 0%, ${alpha(EMERALD_ACCENT, 0.2)} 100%)`, color: EMERALD_ACCENT },
  };

  const v = variants[variant] || variants.s;

  return {
    padding: theme.spacing(3.5),
    borderRadius: theme.restack?.tokens?.radii?.lg || 20,
    textAlign: 'center',
    transition: 'all 0.4s ease',
    background: v.bg,
    cursor: 'default',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: `0 12px 32px ${alpha(v.color, 0.2)}`,
    },
  };
});

export const SwotLetter = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ variant }) => {
  const colors = { s: '#B83B1D', w: '#DC2626', o: '#D97706', t: EMERALD_ACCENT };
  return {
    fontSize: '2rem',
    fontWeight: 700,
    marginBottom: 6,
    display: 'block',
    color: colors[variant] || colors.s,
  };
});

export const SwotLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ variant }) => {
  const colors = { s: '#B83B1D', w: '#DC2626', o: '#D97706', t: EMERALD_ACCENT };
  return {
    fontSize: '0.75rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: colors[variant] || colors.s,
  };
});

// BCG Matrix
export const BcgGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 3,
  width: 300,
  background: alpha(getAccentColor(theme), 0.2),
  borderRadius: theme.restack?.tokens?.radii?.lg || 20,
  overflow: 'hidden',
}));

export const BcgCell = styled(Box)(({ theme }) => ({
  background: theme.palette.background.paper,
  padding: theme.spacing(4),
  textAlign: 'center',
  transition: 'all 0.4s ease',
  cursor: 'default',
  '&:hover': {
    background: alpha(EMERALD_ACCENT, 0.05),
  },
}));

export const BcgIcon = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ variant }) => {
  // Unified brand palette: rust + tonal warm/cool neutrals. The BCG
  // matrix needs four distinguishable shades; we get them by varying
  // tone (rust → light rust → warm gray → cool gray) instead of hue.
  const colors = { star: '#B83B1D', question: '#8B7E7A', cash: '#D4694E', dog: '#6B7280' };
  return {
    fontSize: '2rem',
    marginBottom: 8,
    display: 'block',
    color: colors[variant] || colors.star,
  };
});

export const BcgLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
}));

// TAM/SAM/SOM
export const TamDiagram = styled(Box)({
  position: 'relative',
  width: 280,
  height: 280,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const TamCircle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'size',
})(({ theme, size }) => {
  const sizes = {
    outer: {
      width: 280,
      height: 280,
      bg: alpha(EMERALD_ACCENT, 0.05),
      border: `2px dashed ${alpha(EMERALD_ACCENT, 0.2)}`,
    },
    mid: {
      width: 180,
      height: 180,
      bg: alpha(EMERALD_ACCENT, 0.1),
      border: `2px dashed ${alpha(EMERALD_ACCENT, 0.3)}`,
    },
    inner: {
      width: 80,
      height: 80,
      bg: ACCENT_GRADIENT,
      border: 'none',
    },
  };

  const s = sizes[size] || sizes.outer;

  return {
    position: 'absolute',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.4s ease',
    background: s.bg,
    border: s.border,
    width: s.width,
    height: s.height,
    ...(size === 'inner' && {
      fontSize: '0.875rem',
      fontWeight: 700,
      color: '#ffffff',
      boxShadow: `0 8px 32px ${alpha(EMERALD_ACCENT, 0.25)}`,
      animation: `${pulse} 3s ease-in-out infinite`,
    }),
  };
});

export const TamLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'position',
})(({ theme, position }) => ({
  position: 'absolute',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: EMERALD_ACCENT,
  background: theme.palette.background.paper,
  padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
  borderRadius: 100,
  border: `1px solid ${alpha(EMERALD_ACCENT, 0.3)}`,
  top: position === 'outer' ? 8 : 55,
  left: '50%',
  transform: 'translateX(-50%)',
}));

// McKinsey 7S - Enhanced Visual
export const SevensGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  position: 'relative',
  padding: theme.spacing(3),
}));

export const SevensRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
}));

export const SevensItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isCenter',
})(({ theme, isCenter }) => ({
  padding: `${theme.spacing(1.75)} ${theme.spacing(2.75)}`,
  background: isCenter
    ? ACCENT_GRADIENT
    : theme.palette.background.paper,
  border: isCenter ? 'none' : `1px solid ${alpha(getAccentColor(theme), 0.15)}`,
  borderRadius: 100,
  fontSize: '0.8125rem',
  fontWeight: isCenter ? 600 : 500,
  color: isCenter ? '#ffffff' : theme.palette.text.secondary,
  transition: 'all 0.3s ease',
  cursor: 'default',
  boxShadow: isCenter ? `0 8px 24px ${alpha(EMERALD_ACCENT, 0.2)}` : 'none',
  '&:hover': {
    borderColor: isCenter ? 'transparent' : EMERALD_ACCENT,
    color: isCenter ? '#ffffff' : EMERALD_ACCENT,
    transform: 'scale(1.05)',
  },
}));

// Value Chain - Enhanced Visual
export const ValueChainDiagram = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'stretch',
  gap: 4,
  height: 140,
  position: 'relative',
}));

export const ValueChainStep = styled(Box)(({ theme }) => ({
  flex: 1,
  background: theme.palette.background.paper,
  border: `1px solid ${alpha(getAccentColor(theme), 0.15)}`,
  borderRadius: theme.restack?.tokens?.radii?.sm || 12,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  position: 'relative',
  transition: 'all 0.4s ease',
  minWidth: 80,
  cursor: 'default',
  '&:hover': {
    borderColor: EMERALD_ACCENT,
    background: alpha(EMERALD_ACCENT, 0.05),
    transform: 'translateY(-4px)',
    boxShadow: `0 8px 24px ${alpha(EMERALD_ACCENT, 0.15)}`,
  },
  '&:not(:last-child)::after': {
    content: '"→"',
    position: 'absolute',
    right: -14,
    fontSize: '1.25rem',
    color: EMERALD_ACCENT,
    zIndex: 1,
    fontWeight: 700,
  },
}));

export const ValueChainIcon = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  background: alpha(EMERALD_ACCENT, 0.1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    color: EMERALD_ACCENT,
  },
}));

export const ValueChainLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.6875rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textAlign: 'center',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}));

// ============================================================================
// CAPABILITIES BENTO GRID - Asymmetric Layout
// ============================================================================

export const CapabilitiesBento = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: theme.spacing(3),
  width: '100%',
  maxWidth: 1100,
  margin: '0 auto',
  boxSizing: 'border-box',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    overflow: 'hidden',
    maxWidth: '100%',
  },
}));

export const BentoCard = styled(BaseCard, {
  shouldForwardProp: (prop) => !['colSpan', 'rowSpan'].includes(prop),
})(({ theme, colSpan = 2, rowSpan = 1 }) => ({
  gridColumn: `span ${colSpan}`,
  gridRow: `span ${rowSpan}`,
  minHeight: rowSpan > 1 ? 420 : 200,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  // Layered shadows for premium depth
  boxShadow: theme.palette.mode === 'dark'
    ? `0 1px 1px rgba(0,0,0,0.08),
       0 2px 2px rgba(0,0,0,0.06),
       0 4px 4px rgba(0,0,0,0.04),
       0 8px 8px rgba(0,0,0,0.02)`
    : `0 1px 1px rgba(0,0,0,0.02),
       0 2px 2px rgba(0,0,0,0.02),
       0 4px 4px rgba(0,0,0,0.015),
       0 8px 8px rgba(0,0,0,0.01)`,
  transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.5s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 2px 2px rgba(0,0,0,0.08),
         0 4px 4px rgba(0,0,0,0.08),
         0 8px 8px rgba(0,0,0,0.06),
         0 16px 16px rgba(0,0,0,0.04),
         0 32px 32px rgba(0,0,0,0.02),
         0 0 0 1px ${alpha(EMERALD_ACCENT, 0.15)}`
      : `0 2px 2px rgba(0,0,0,0.02),
         0 4px 4px rgba(0,0,0,0.02),
         0 8px 8px rgba(0,0,0,0.02),
         0 16px 16px rgba(0,0,0,0.015),
         0 32px 48px rgba(0,0,0,0.03),
         0 0 0 1px ${alpha(EMERALD_ACCENT, 0.1)}`,
    borderColor: alpha(EMERALD_ACCENT, 0.25),
  },
  [theme.breakpoints.down('lg')]: {
    gridColumn: colSpan > 2 ? 'span 2' : 'span 2',
  },
  [theme.breakpoints.down('md')]: {
    gridColumn: 'span 1',
    gridRow: 'span 1',
    minHeight: 200,
  },
  [theme.breakpoints.down('sm')]: {
    overflow: 'hidden',  // Prevent content from bleeding out on mobile
    padding: theme.spacing(2.5),  // Reduced padding on mobile
    minHeight: 180,
    '& img, & svg': {
      maxWidth: '100%',
      height: 'auto',
    },
    // Disable hover transform on touch devices
    '&:hover': {
      transform: 'none',
    },
    // Ensure touch-friendly tap targets
    '& button, & a': {
      minHeight: 44,
      minWidth: 44,
    },
  },
}));

export const BentoIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme, variant = 'orange' }) => {
  // Unified brand palette — every variant renders as a rust icon on the
  // same soft salmon (rust-tinted) surface. Variant names retained as
  // semantic labels only.
  const gradients = {
    cyan: { bg: ACCENT_GRADIENT_SOFT, bgHover: 'rgba(184, 59, 29, 0.18)', icon: ORANGE_ACCENT },
    green: { bg: ACCENT_GRADIENT_SOFT, bgHover: 'rgba(184, 59, 29, 0.18)', icon: ORANGE_ACCENT },
    purple: { bg: ACCENT_GRADIENT_SOFT, bgHover: 'rgba(184, 59, 29, 0.18)', icon: ORANGE_ACCENT },
    orange: { bg: ACCENT_GRADIENT_SOFT, bgHover: 'rgba(184, 59, 29, 0.18)', icon: ORANGE_ACCENT },
  };
  const g = gradients[variant] || gradients.orange;

  return {
    width: 48,
    height: 48,
    borderRadius: theme.restack?.tokens?.radii?.md || 16,
    background: g.bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    transition: 'transform 0.3s ease, background 0.3s ease',
    '& svg': {
      fontSize: 24,
      color: g.icon,
    },
    '.MuiBox-root:hover &': {
      transform: 'scale(1.05)',
      background: g.bgHover,
    },
  };
});

// Wraps BentoTitle + optional "Rolling out" badge so they share a row
// while preserving the title's bottom margin.
export const BentoTitleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
  marginBottom: theme.spacing(1),
  '& > .MuiTypography-root': {
    marginBottom: 0,
  },
}));

// Small "Rolling out next" pill — same bell-glyph language as the
// upcoming connector chips in the Integrations section. Subtle rust
// tint + dashed border so it reads as "in progress" without screaming.
export const BentoUpcomingBadge = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.4),
  padding: `${theme.spacing(0.3)} ${theme.spacing(0.85)}`,
  fontSize: '0.65rem',
  fontWeight: 400,
  letterSpacing: 'normal',
  textTransform: 'none',
  color: ORANGE_ACCENT,
  background: alpha(ORANGE_ACCENT, 0.08),
  border: `1px dashed ${alpha(ORANGE_ACCENT, 0.35)}`,
  borderRadius: 999,
  flexShrink: 0,
  '& svg': {
    fontSize: 11,
    color: ORANGE_ACCENT,
  },
}));

export const BentoTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  marginBottom: theme.spacing(1),
  color: theme.palette.text.primary,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
  },
}));

export const BentoDesc = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 400,
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8125rem',
    lineHeight: 1.5,
  },
}));

export const BentoTags = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
  flexWrap: 'wrap',
}));

export const BentoTag = styled(Box)(({ theme }) => ({
  padding: `${theme.spacing(0.75)} ${theme.spacing(1.5)}`,
  background: ACCENT_GRADIENT_SOFT,
  borderRadius: 12,
  fontSize: '0.75rem',
  fontWeight: 400,
  ...gradientTextStyles,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  '&::before': {
    content: '""',
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#B83B1D',
  },
}));

export const BentoChart = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-end',
  gap: 6,
  height: 80,
  marginTop: 'auto',
  paddingTop: theme.spacing(2),
}));

export const BentoQuote = styled(Box)(({ theme }) => ({
  marginTop: 'auto',
  paddingTop: theme.spacing(2),
  padding: theme.spacing(2),
  background: theme.palette.mode === 'dark'
    ? alpha('#ffffff', 0.03)
    : alpha('#000000', 0.02),
  borderRadius: theme.restack?.tokens?.radii?.sm || 12,
  border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.08) : alpha('#000000', 0.06)}`,
  fontSize: '0.875rem',
  color: theme.palette.text.primary,
  fontFamily: 'monospace',
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '"|"',
    display: 'inline-block',
    marginLeft: 2,
    animation: `${quoteCursor} 1s step-end infinite`,
    color: ORANGE_ACCENT,
  },
  '@media (prefers-reduced-motion: reduce)': {
    '&::after': {
      animation: 'none',
    },
  },
}));

export const BentoExport = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  marginTop: 'auto',
  paddingTop: theme.spacing(2),
}));

export const ExportBtn = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'index',
})(({ theme, index = 0 }) => ({
  padding: `${theme.spacing(1.25)} ${theme.spacing(2.5)}`,
  background: 'transparent',
  border: `1px solid ${alpha(EMERALD_ACCENT, 0.3)}`,
  borderRadius: theme.restack?.tokens?.radii?.sm || 12,
  fontSize: '0.875rem',
  fontWeight: 400,
  ...gradientTextStyles,
  transition: 'all 0.3s ease',
  textTransform: 'none',
  minWidth: 'auto',
  animation: `${buttonPop} 0.5s ease-out forwards`,
  animationDelay: `${0.2 + index * 0.15}s`,
  opacity: 0,
  '&:hover': {
    borderColor: EMERALD_ACCENT,
    background: alpha(EMERALD_ACCENT, 0.1),
    transform: 'translateY(-2px)',
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    opacity: 1,
  },
}));

export const BentoUpgrade = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  marginTop: 'auto',
  paddingTop: theme.spacing(2),
}));

export const UpgradeItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

export const UpgradeBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isNew',
})(({ theme, isNew }) => ({
  width: 80,
  height: 60,
  background: isNew
    ? `linear-gradient(135deg, ${alpha(EMERALD_ACCENT, 0.15)} 0%, ${alpha(EMERALD_ACCENT, 0.25)} 100%)`
    : (theme.palette.mode === 'dark' ? alpha('#ffffff', 0.04) : alpha('#000000', 0.02)),
  border: `1px solid ${isNew ? EMERALD_ACCENT : alpha(getAccentColor(theme), 0.15)}`,
  borderRadius: theme.restack?.tokens?.radii?.sm || 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.25rem',
  transition: 'all 0.4s ease',
  '& svg': {
    color: EMERALD_ACCENT,
    fontSize: 28,
  },
  ...(isNew && {
    animation: `${pulse} 2s ease-in-out infinite`,
  }),
}));

export const UpgradeLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
}));

export const UpgradeArrow = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  ...gradientTextStyles,
  fontWeight: 700,
}));

// RFP Multi-client Visual
export const RfpVisual = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(4),
  marginTop: 'auto',
  paddingTop: theme.spacing(3),
  width: '100%',
}));

export const RfpSource = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

export const RfpSourceBox = styled(Box)(({ theme }) => ({
  width: 90,
  height: 70,
  background: alpha(EMERALD_ACCENT, 0.1),
  border: `2px solid ${EMERALD_ACCENT}`,
  borderRadius: theme.restack?.tokens?.radii?.md || 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  '& svg': {
    color: EMERALD_ACCENT,
    fontSize: 32,
  },
  '&::after': {
    content: '"RFP"',
    position: 'absolute',
    bottom: -8,
    fontSize: '0.75rem',
    fontWeight: 700,
    color: EMERALD_ACCENT,
    background: theme.palette.background.paper,
    padding: '0 6px',
  },
}));

export const RfpArrows = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  color: EMERALD_ACCENT,
  fontSize: '1.5rem',
  fontWeight: 700,
  '& span': {
    animation: `${arrowFlow} 1.5s ease-in-out infinite`,
    '&:nth-of-type(2)': { animationDelay: '0.3s' },
    '&:nth-of-type(3)': { animationDelay: '0.6s' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    '& span': {
      animation: 'none',
    },
  },
}));

export const RfpTargets = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

export const RfpTarget = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'index',
})(({ theme, index = 0 }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: `${theme.spacing(1.5)} ${theme.spacing(2.5)}`,
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.08) : alpha('#000000', 0.08)}`,
  borderRadius: theme.restack?.tokens?.radii?.sm || 12,
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  transition: 'all 0.3s ease',
  animation: `${slideInRight} 0.5s ease-out forwards`,
  animationDelay: `${0.3 + index * 0.15}s`,
  opacity: 0,
  '&:hover': {
    borderColor: EMERALD_ACCENT,
    color: EMERALD_ACCENT,
    transform: 'translateX(4px)',
  },
  '& svg': {
    fontSize: 18,
    color: EMERALD_ACCENT,
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    opacity: 1,
  },
}));

// Bento Image Container
export const BentoImage = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 120,
  borderRadius: theme.restack?.tokens?.radii?.sm || 12,
  overflow: 'hidden',
  marginTop: 'auto',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.4s ease',
  },
  '&:hover img': {
    transform: 'scale(1.05)',
  },
}));

// ============================================================================
// CAPABILITY VISUALIZATIONS — Vendor-style proof visuals (one per card)
// ============================================================================

// Shared frame wrapping each capability visualization
export const CapVisualFrame = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2.5),
  padding: theme.spacing(2),
  borderRadius: theme.restack?.tokens?.radii?.md || 16,
  border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.08) : alpha('#000000', 0.06)}`,
  background: theme.palette.mode === 'dark'
    ? alpha('#ffffff', 0.02)
    : alpha('#000000', 0.015),
  position: 'relative',
  overflow: 'hidden',
}));

// ---------- CITATIONS: Claim → Source trace ----------
export const CitationTrace = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: theme.spacing(1.25),
  position: 'relative',
}));

export const CitationQuote = styled(Box)(({ theme }) => ({
  padding: `${theme.spacing(1.25)} ${theme.spacing(1.5)}`,
  borderRadius: theme.restack?.tokens?.radii?.sm || 12,
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.08) : alpha('#000000', 0.06)}`,
  fontSize: '0.82rem',
  lineHeight: 1.5,
  color: theme.palette.text.primary,
  fontStyle: 'italic',
  position: 'relative',
  '&::before': {
    content: '"\\201C"',
    position: 'absolute',
    top: -6,
    left: 8,
    fontSize: '1.6rem',
    color: alpha(ORANGE_ACCENT, 0.35),
    fontFamily: 'Georgia, serif',
    lineHeight: 1,
  },
}));

export const CitationChip = styled('sup')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 18,
  height: 18,
  padding: '0 5px',
  marginLeft: 3,
  borderRadius: 999,
  background: alpha(ORANGE_ACCENT, 0.12),
  color: ORANGE_ACCENT,
  fontSize: '0.65rem',
  fontWeight: 700,
  fontStyle: 'normal',
  verticalAlign: 'super',
  lineHeight: 1,
  border: `1px solid ${alpha(ORANGE_ACCENT, 0.25)}`,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: alpha(ORANGE_ACCENT, 0.2),
    transform: 'translateY(-1px)',
  },
}));

export const CitationConnector = styled(Box)(({ theme }) => ({
  alignSelf: 'center',
  width: 1,
  height: 14,
  background: `linear-gradient(180deg, ${alpha(ORANGE_ACCENT, 0.4)}, ${alpha(ORANGE_ACCENT, 0)})`,
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -3,
    left: -3,
    width: 7,
    height: 7,
    borderRight: `1.5px solid ${alpha(ORANGE_ACCENT, 0.5)}`,
    borderBottom: `1.5px solid ${alpha(ORANGE_ACCENT, 0.5)}`,
    transform: 'rotate(45deg)',
  },
}));

export const CitationSource = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.25),
  borderRadius: theme.restack?.tokens?.radii?.sm || 12,
  background: theme.palette.background.paper,
  border: `1px solid ${alpha(ORANGE_ACCENT, 0.2)}`,
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.25),
}));

export const CitationSourceIcon = styled(Box)(({ theme }) => ({
  width: 32,
  height: 40,
  flexShrink: 0,
  borderRadius: 4,
  background: alpha(ORANGE_ACCENT, 0.08),
  border: `1px solid ${alpha(ORANGE_ACCENT, 0.2)}`,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, right: 0,
    width: 8, height: 8,
    background: alpha(ORANGE_ACCENT, 0.18),
    clipPath: 'polygon(0 0, 100% 100%, 100% 0)',
  },
  '&::after': {
    content: '"PDF"',
    position: 'absolute',
    bottom: 2, left: 0, right: 0,
    textAlign: 'center',
    fontSize: '0.55rem',
    fontWeight: 700,
    color: ORANGE_ACCENT,
    letterSpacing: '0.04em',
  },
}));

export const CitationSourceBody = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
}));

export const CitationSourceTitle = styled(Box)(({ theme }) => ({
  fontSize: '0.8rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: 2,
}));

export const CitationSourceMeta = styled(Box)(({ theme }) => ({
  fontSize: '0.7rem',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.75),
}));

export const CitationSourceHighlight = styled(Box)(({ theme }) => ({
  height: 6,
  borderRadius: 3,
  background: `linear-gradient(90deg, ${alpha(ORANGE_ACCENT, 0.35)} 0%, ${alpha(ORANGE_ACCENT, 0.15)} 100%)`,
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0,
    height: '100%',
    width: '55%',
    background: alpha(ORANGE_ACCENT, 0.55),
    borderRadius: 3,
  },
}));

// ---------- GOVERNANCE: Before/After brand compliance ----------
export const GovCompare = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  gap: theme.spacing(1),
  alignItems: 'stretch',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    '& .gov-arrow': { display: 'none' },
  },
}));

export const GovPanel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme, variant = 'bad' }) => ({
  padding: theme.spacing(1.25),
  borderRadius: theme.restack?.tokens?.radii?.sm || 12,
  background: theme.palette.background.paper,
  border: variant === 'good'
    ? `1px solid ${alpha(ORANGE_ACCENT, 0.3)}`
    : `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.08) : alpha('#000000', 0.06)}`,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75),
  position: 'relative',
  boxShadow: variant === 'good'
    ? `0 0 0 3px ${alpha(ORANGE_ACCENT, 0.06)}`
    : 'none',
}));

export const GovPanelLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme, variant = 'bad' }) => ({
  fontSize: '0.6rem',
  fontWeight: 400,
  letterSpacing: 'normal',
  textTransform: 'none',
  color: variant === 'good' ? ORANGE_ACCENT : theme.palette.text.secondary,
  marginBottom: theme.spacing(0.25),
}));

export const GovHeadline = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme, variant = 'bad' }) => ({
  fontSize: '0.75rem',
  fontWeight: 400,
  fontFamily: variant === 'good' ? theme.typography.fontFamily : 'Arial, sans-serif',
  color: variant === 'good' ? theme.palette.text.primary : theme.palette.text.secondary,
  lineHeight: 1.2,
}));

export const GovSwatchRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.625),
  // No hex labels — the swatch trio is iconographic shorthand for "palette"
  marginTop: theme.spacing(0.5),
}));

export const GovSwatch = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'color',
})(({ color = '#B83B1D' }) => ({
  width: 18,
  height: 18,
  borderRadius: 4,
  background: color,
  flexShrink: 0,
  boxShadow: `inset 0 0 0 1px ${alpha('#1e1612', 0.06)}`,
}));

export const GovArrow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: alpha(ORANGE_ACCENT, 0.5),
  fontSize: '1.1rem',
  padding: `0 ${theme.spacing(0.25)}`,
}));

export const GovChecklist = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.75),
  marginTop: theme.spacing(1.25),
  justifyContent: 'center',
}));

export const GovCheck = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontSize: '0.65rem',
  fontWeight: 400,
  color: theme.palette.text.secondary,
  '&::before': {
    content: '""',
    width: 12,
    height: 12,
    borderRadius: '50%',
    background: alpha(ORANGE_ACCENT, 0.12),
    border: `1px solid ${alpha(ORANGE_ACCENT, 0.3)}`,
    position: 'relative',
    display: 'inline-block',
    backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' fill='none'><path d='M3 6.5L5 8.5L9 4' stroke='%23B83B1D' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>")`,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  },
}));

// ---------- KNOWLEDGE LOCKER: Three isolated project vaults ----------
export const VaultStack = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75),
  position: 'relative',
}));

export const VaultCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'index',
})(({ theme, index = 0 }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  padding: `${theme.spacing(1)} ${theme.spacing(1.25)}`,
  borderRadius: theme.restack?.tokens?.radii?.sm || 12,
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.08) : alpha('#000000', 0.06)}`,
  position: 'relative',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: alpha(ORANGE_ACCENT, 0.3),
    transform: 'translateX(3px)',
  },
}));

export const VaultLock = styled(Box)(({ theme }) => ({
  width: 28,
  height: 28,
  borderRadius: 12,
  background: alpha(ORANGE_ACCENT, 0.1),
  border: `1px solid ${alpha(ORANGE_ACCENT, 0.22)}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& svg': {
    fontSize: 14,
    color: ORANGE_ACCENT,
  },
}));

export const VaultBody = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
}));

export const VaultName = styled(Box)(({ theme }) => ({
  fontSize: '0.78rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

export const VaultMeta = styled(Box)(({ theme }) => ({
  fontSize: '0.67rem',
  color: theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  '&::before': {
    content: '""',
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: '#16a34a',
    display: 'inline-block',
    flexShrink: 0,
  },
}));

export const VaultIsolationLine = styled(Box)(({ theme }) => ({
  height: 0,
  borderTop: `1px dashed ${alpha(theme.palette.text.primary, 0.12)}`,
  margin: `${theme.spacing(0.25)} ${theme.spacing(2.5)}`,
  position: 'relative',
  '&::after': {
    content: '"isolated"',
    position: 'absolute',
    top: -6,
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: '0.55rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    background: theme.palette.background.paper,
    padding: '0 6px',
  },
}));

export const VaultBadges = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(1.25),
}));

export const VaultBadge = styled(Box)(({ theme }) => ({
  fontSize: '0.6rem',
  fontWeight: 600,
  letterSpacing: '0.04em',
  padding: '2px 8px',
  borderRadius: 999,
  background: alpha(ORANGE_ACCENT, 0.08),
  color: ORANGE_ACCENT,
  border: `1px solid ${alpha(ORANGE_ACCENT, 0.2)}`,
}));

// ---------- CONNECTORS: Vendor constellation grid (3 groups) ----------
export const ConnectorWall = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.25),
}));

export const ConnectorGroupBlock = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75),
}));

export const ConnectorGroupLabel = styled(Box)(({ theme }) => ({
  fontSize: '0.6rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  '&::after': {
    content: '""',
    flex: 1,
    height: 1,
    background: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.08) : alpha('#000000', 0.06),
  },
}));

export const ConnectorChipsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.75),
}));

export const ConnectorChipItem = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 10px',
  borderRadius: 999,
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.1) : alpha('#000000', 0.08)}`,
  fontSize: '0.72rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: alpha(ORANGE_ACCENT, 0.3),
    background: alpha(ORANGE_ACCENT, 0.02),
    transform: 'translateY(-1px)',
  },
  '& svg': {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
}));

export const ConnectorFootnote = styled(Box)(({ theme }) => ({
  fontSize: '0.7rem',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(1),
  textAlign: 'center',
  fontStyle: 'italic',
}));

// ---------- CONNECTOR ORBIT (Model ML-style hub + arc of vendor chips) ------
// A central "Restack" hub with vendor chips arranged on a wide arc around it.
// Each chip carries an `--angle` CSS variable and is positioned by transform
// chain: translate to centre → rotate → translate outward by radius →
// counter-rotate so the chip text stays upright. Replaces the old 3-group
// stacked list in the connectors capability card; reads as one composition
// instead of three lists.

export const ConnectorOrbit = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  // Tall enough to fit the orbit chips around + the hub centred. Chips on
  // the upper arc reach to ~radius above the centre; lower-corner chips
  // reach below by sin(135°)*radius ≈ 0.71*radius.
  height: 260,
  margin: `${theme.spacing(1.5)} auto ${theme.spacing(1)}`,
  // Larger radius so 7 chips with varied widths breathe.
  '--orbit-radius': '118px',
  [theme.breakpoints.down('sm')]: {
    height: 230,
    '--orbit-radius': '98px',
  },
  // Decorative dashed orbit ring
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 'calc(var(--orbit-radius) * 2 + 32px)',
    height: 'calc(var(--orbit-radius) * 2 + 32px)',
    transform: 'translate(-50%, -50%)',
    borderRadius: '50%',
    border: `1px dashed ${theme.palette.mode === 'dark'
      ? alpha('#ffffff', 0.08)
      : alpha('#1e1612', 0.1)}`,
    pointerEvents: 'none',
  },
}));

export const ConnectorOrbitHub = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 56,
  height: 56,
  borderRadius: '50%',
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(135deg, #1e1612, #2a1e18)`
    : `linear-gradient(135deg, #1e1612, #3a2a22)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#ffffff',
  boxShadow: `
    0 8px 24px ${alpha('#1e1612', 0.18)},
    inset 0 1px 0 ${alpha('#ffffff', 0.08)}
  `,
  '& svg': {
    fontSize: 26,
    color: alpha('#ffffff', 0.92),
  },
  // Subtle pulse to suggest "alive / connected"
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: -6,
    borderRadius: '50%',
    border: `1px solid ${alpha(ORANGE_ACCENT, 0.35)}`,
    animation: 'orbitHubPulse 2.8s ease-out infinite',
  },
  '@keyframes orbitHubPulse': {
    '0%':   { transform: 'scale(1)',   opacity: 0.6 },
    '100%': { transform: 'scale(1.4)', opacity: 0   },
  },
}));

export const ConnectorOrbitHubLabel = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 'calc(50% + 38px)',
  left: '50%',
  transform: 'translateX(-50%)',
  fontSize: '0.6rem',
  fontWeight: 700,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
}));

export const ConnectorOrbitChip = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'angle',
})(({ theme, angle = 0 }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  // Transform chain: centre the chip, rotate to angle, translate outward
  // along the rotated Y axis, then counter-rotate so text stays upright.
  transform: `
    translate(-50%, -50%)
    rotate(${angle}deg)
    translateY(calc(var(--orbit-radius) * -1))
    rotate(${-angle}deg)
  `,
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.625),
  padding: `${theme.spacing(0.625)} ${theme.spacing(1.125)}`,
  borderRadius: 999,
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.mode === 'dark'
    ? alpha('#ffffff', 0.1)
    : alpha('#1e1612', 0.08)}`,
  fontSize: '0.7rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  whiteSpace: 'nowrap',
  boxShadow: theme.palette.mode === 'dark'
    ? `0 2px 6px ${alpha('#000', 0.25)}`
    : `0 2px 8px ${alpha('#1e1612', 0.06)}`,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
  '& svg': {
    fontSize: 14,
    color: ORANGE_ACCENT,
  },
  // Soft hover lift — pulls the chip outward slightly
  '&:hover': {
    borderColor: alpha(ORANGE_ACCENT, 0.35),
    boxShadow: `0 6px 18px ${alpha('#1e1612', 0.12)}`,
    transform: `
      translate(-50%, -50%)
      rotate(${angle}deg)
      translateY(calc(var(--orbit-radius) * -1 - 4px))
      rotate(${-angle}deg)
    `,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.62rem',
    padding: `${theme.spacing(0.5)} ${theme.spacing(0.875)}`,
    '& svg': { fontSize: 12 },
  },
}));

// ============================================================================
// RESULTS SECTION
// ============================================================================

export const ResultsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gridTemplateRows: 'auto auto',
  gap: theme.spacing(3),
  alignItems: 'start',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),
  },
}));

// Testimonial Carousel
export const TestimonialCarousel = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: theme.restack?.tokens?.radii?.lg || 20,
  minWidth: 0, // Prevent grid blowout
  height: '100%',
}));

export const TestimonialTrack = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'activeIndex',
})(({ theme, activeIndex = 0 }) => ({
  display: 'flex',
  transition: 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
  transform: `translateX(-${activeIndex * 100}%)`,
}));

export const TestimonialSlide = styled(Box)(({ theme }) => ({
  minWidth: '100%',
  padding: theme.spacing(1),
  height: '100%',
}));

export const TestimonialCard = styled(BaseCard)(({ theme }) => ({
  padding: theme.spacing(4.5),
  height: '100%',
  minHeight: 300,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  transition: 'transform 0.4s ease, box-shadow 0.4s ease, border-color 0.4s ease',
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(180deg, ${alpha('#ffffff', 0.02)} 0%, ${alpha('#ffffff', 0.01)} 100%)`
    : `linear-gradient(180deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#fafaf9', 0.6)} 100%)`,
  '&:hover': {
    transform: 'translateY(-3px)',
    borderColor: alpha(getAccentColor(theme), 0.25),
    boxShadow: theme.palette.mode === 'dark'
      ? `0 16px 44px ${alpha('#000', 0.35)}`
      : `0 16px 44px ${alpha('#1e1612', 0.08)}`,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
    minHeight: 260,
    '&:hover': {
      transform: 'none',
    },
  },
}));

export const TestimonialQuote = styled(Typography)(({ theme }) => ({
  fontSize: '3.25rem',
  fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif',
  fontStyle: 'normal',
  fontWeight: 500,
  color: alpha(getAccentColor(theme), 0.55),
  lineHeight: 0.8,
  marginBottom: theme.spacing(1),
  userSelect: 'none',
}));

export const TestimonialText = styled(Typography)(({ theme }) => ({
  fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif',
  fontStyle: 'normal',
  fontWeight: 400,
  // Reduced again from 1rem → 0.875rem (14px) so testimonial copy reads
  // as scannable body text. Italic serif at smaller size still signals
  // "quote" without dominating the card or competing with adjacent stats.
  fontSize: '0.875rem',
  lineHeight: 1.6,
  letterSpacing: '-0.002em',
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(3),
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8125rem',
    lineHeight: 1.55,
    marginBottom: theme.spacing(2),
  },
}));

export const TestimonialAuthor = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

export const AuthorAvatar = styled(Box)(({ theme }) => ({
  width: 56,
  height: 56,
  borderRadius: '50%',
  overflow: 'hidden',
  background: ACCENT_GRADIENT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
  fontSize: '1.125rem',
  color: '#ffffff',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}));

export const AuthorName = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: '0.9375rem',
  letterSpacing: '-0.005em',
  color: theme.palette.text.primary,
}));

export const AuthorRole = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  color: theme.palette.text.secondary,
  letterSpacing: '0.005em',
}));

export const CarouselDots = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(1),
  marginTop: 'auto',
  paddingTop: theme.spacing(4),
}));

export const CarouselDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})(({ theme, isActive }) => ({
  width: isActive ? 24 : 8,
  height: 8,
  borderRadius: 4,
  background: isActive ? EMERALD_ACCENT : alpha(getAccentColor(theme), 0.3),
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: isActive ? EMERALD_ACCENT : alpha(getAccentColor(theme), 0.5),
  },
}));

export const StatsStack = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  minWidth: 0, // Prevent overflow
}));

export const StatCard = styled(BaseCard, {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme, variant }) => ({
  padding: theme.spacing(3.5),
  transition: 'transform 0.4s ease, box-shadow 0.4s ease, border-color 0.4s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    borderColor: alpha(EMERALD_ACCENT, 0.3),
    boxShadow: theme.palette.mode === 'dark'
      ? `0 12px 36px ${alpha('#000', 0.4)}`
      : `0 12px 36px ${alpha('#000', 0.12)}`,
  },
  ...(variant === 'iteration' && {
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha('#B83B1D', 0.08)} 100%)`,
    borderColor: alpha('#B83B1D', 0.2),
  }),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2.5),
    '&:hover': {
      transform: 'none',
    },
  },
}));

export const StatValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'inherit',
  fontSize: '3rem',
  fontWeight: 700,
  fontStyle: 'normal',
  letterSpacing: '-0.03em',
  background: `linear-gradient(135deg, ${ORANGE_ACCENT} 0%, ${EMERALD_ACCENT} 100%)`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  // Small inline padding keeps the gradient mask off the glyph edges.
  display: 'inline-block',
  paddingInline: '0.04em',
  paddingBlock: '0.05em',
  lineHeight: 1.15,
  marginBottom: theme.spacing(1.25),
  [theme.breakpoints.down('sm')]: {
    fontSize: '2.25rem',
  },
}));

export const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.9375rem',
  fontWeight: 400,
  color: theme.palette.text.primary,
}));

export const StatSublabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  color: theme.palette.text.secondary,
}));

export const IterationFlow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
}));

export const FlowStep = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isFinal',
})(({ theme, isFinal }) => ({
  padding: `${theme.spacing(1.25)} ${theme.spacing(2.5)}`,
  background: isFinal ? '#B83B1D' : theme.palette.background.paper,
  border: isFinal ? 'none' : `1px solid ${alpha(getAccentColor(theme), 0.15)}`,
  borderRadius: 100,
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: isFinal ? '#ffffff' : theme.palette.text.secondary,
}));

export const FlowArrow = styled(Typography)(({ theme }) => ({
  color: '#B83B1D',
  fontSize: '1.125rem',
  fontWeight: 700,
}));

// ============================================================================
// SECURITY SECTION
// ============================================================================

export const SecurityGrid = styled(Box)(({ theme }) => ({
  // 5 cards in a single row on desktop — gives the section a clean,
  // procurement-grade scan instead of a sprawling 2x4 wall. Falls back
  // to 3-up on tablet and stacked on mobile.
  display: 'grid',
  gridTemplateColumns: 'repeat(5, 1fr)',
  gap: theme.spacing(2),
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(1.5),
  },
}));

export const SecurityCard = styled(BaseCard)(({ theme }) => ({
  // Tighter padding so 5 cards comfortably share a single row.
  padding: theme.spacing(2.5),
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  background: theme.palette.background.paper,
  transition: 'transform 0.4s ease, box-shadow 0.4s ease, border-color 0.4s ease',
  '&:hover': {
    transform: 'translateY(-3px)',
    borderColor: alpha(EMERALD_ACCENT, 0.4),
    boxShadow: theme.palette.mode === 'dark'
      ? `0 10px 28px ${alpha('#000', 0.4)}`
      : `0 10px 28px ${alpha('#1e1612', 0.08)}`,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2.25),
    gap: theme.spacing(1),
    '&:hover': {
      transform: 'none',
    },
  },
}));

export const SecurityIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme, variant = 'cyan' }) => {
  // Unified brand palette — every variant renders as a rust icon on the
  // same soft salmon (rust-tinted) surface. Variant names retained as
  // semantic labels only.
  const gradients = {
    cyan: { bg: ACCENT_GRADIENT_SOFT, icon: ORANGE_ACCENT, shadow: 'rgba(184, 59, 29, 0.2)' },
    green: { bg: ACCENT_GRADIENT_SOFT, icon: ORANGE_ACCENT, shadow: 'rgba(184, 59, 29, 0.2)' },
    purple: { bg: ACCENT_GRADIENT_SOFT, icon: ORANGE_ACCENT, shadow: 'rgba(184, 59, 29, 0.2)' },
    orange: { bg: ACCENT_GRADIENT_SOFT, icon: ORANGE_ACCENT, shadow: 'rgba(184, 59, 29, 0.25)' },
  };
  const g = gradients[variant] || gradients.cyan;

  return {
    // Reduced from 72×72 → 52×52 with smaller inner glyph. The previous
    // size dominated the security cards visually — these are supporting
    // illustrations, not the focal point. The card text is what matters.
    width: 52,
    height: 52,
    borderRadius: '50%',
    background: g.bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(1),
    transition: 'all 0.4s ease',
    '& svg': {
      fontSize: 24,
      color: g.icon,
    },
    '& img': {
      width: 28,
      height: 28,
      objectFit: 'contain',
    },
    '.MuiBox-root:hover &': {
      transform: 'scale(1.05)',
    },
  };
});

export const SecurityTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
  },
}));

export const SecurityDesc = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 400,
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8125rem',
    lineHeight: 1.5,
  },
}));

export const SecurityBadge = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
  background: alpha('#B83B1D', 0.1),
  borderRadius: 100,
  fontSize: '0.75rem',
  fontWeight: 400,
  color: '#B83B1D',
  marginTop: 'auto',
  '& svg': {
    fontSize: 14,
    animation: `${checkmarkAppear} 0.5s ease-out forwards`,
    animationDelay: '0.3s',
    opacity: 0,
  },
  '@media (prefers-reduced-motion: reduce)': {
    '& svg': {
      animation: 'none',
      opacity: 1,
    },
  },
}));

// ============================================================================
// PRICING SECTION
// ============================================================================

export const PricingGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  alignItems: 'stretch',
  gap: theme.spacing(2.5),
  maxWidth: 1040,
  margin: '0 auto',
  '& > *': {
    display: 'flex',
    flexDirection: 'column',
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    maxWidth: 480,
    gap: theme.spacing(2.5),
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
    gap: theme.spacing(2),
    padding: '0 8px',
  },
}));

export const PricingCard = styled(BaseCard, {
  shouldForwardProp: (prop) => prop !== 'featured',
})(({ theme, featured }) => ({
  padding: theme.spacing(3.25),
  textAlign: 'center',
  position: 'relative',
  overflow: 'visible',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease, border-color 0.3s ease',
  // Featured card styling — tightened: NO outer halo, NO 2px chunky border,
  // just a 1px accent border + a small "Most popular" badge above the card.
  // Linear / Vercel pattern — the tier earns attention through copy and the
  // badge, not through visual screaming.
  ...(featured && {
    border: `1px solid ${alpha(EMERALD_ACCENT, 0.55)}`,
    boxShadow: `0 4px 14px ${alpha('#1e1612', 0.05)}`,
    zIndex: 2,
    '&::before': {
      content: '"MOST POPULAR"',
      position: 'absolute',
      top: -10,
      left: '50%',
      transform: 'translateX(-50%)',
      background: '#ffffff',
      color: EMERALD_ACCENT,
      padding: '3px 12px',
      borderRadius: 100,
      fontSize: '0.62rem',
      fontWeight: 700,
      letterSpacing: '0.1em',
      whiteSpace: 'nowrap',
      border: `1px solid ${alpha(EMERALD_ACCENT, 0.55)}`,
      boxShadow: 'none',
    },
  }),
  '&:hover': {
    transform: 'translateY(-4px)',
    borderColor: alpha(EMERALD_ACCENT, featured ? 0.7 : 0.3),
    boxShadow: `0 10px 30px ${alpha('#1e1612', 0.08)}`,
  },
  [theme.breakpoints.down('md')]: {
    ...(featured && {
      order: -1, // Move featured card to top on mobile
    }),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
    '&:hover': {
      transform: 'none',
    },
    ...(featured && {
      marginTop: theme.spacing(2), // Account for the "Most popular" badge
    }),
  },
}));

export const PricingBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 16,
  right: -32,
  background: ACCENT_GRADIENT,
  color: '#ffffff',
  padding: `${theme.spacing(0.5)} ${theme.spacing(4)}`,
  fontSize: '0.75rem',
  fontWeight: 400,
  transform: 'rotate(45deg)',
  textTransform: 'none',
}));

export const PricingTier = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 400,
  ...gradientTextStyles,
  textTransform: 'none',
  letterSpacing: 'normal',
  marginBottom: theme.spacing(1.25),
}));

export const PricingPrice = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
  lineHeight: 1.15,
  letterSpacing: '-0.02em',
  marginBottom: theme.spacing(0.75),
  '& span': {
    display: 'block',
    fontSize: '0.8125rem',
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.75rem',
    '& span': {
      fontSize: '0.75rem',
    },
  },
}));

export const PricingNote = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  fontWeight: 400,
  lineHeight: 1.5,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(2.5),
  // Reserve space for ~2 lines so cards with 1-line subtitles (Individual)
  // align their feature checklists at the same Y as cards with 2-line
  // subtitles (Team / Practice). Without this, the checkmarks on
  // Individual start higher than the others and the row looks ragged.
  minHeight: '2.5rem',
}));

// The checklist is the card's second movement. A hairline above it separates
// the centred header block (tier, price, subtitle, seat slider) from the
// left-aligned list, so a long card reads as two deliberate parts rather than
// one continuous column of mixed alignment.
export const PricingFeatures = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.1),
  paddingTop: theme.spacing(2.5),
  marginBottom: theme.spacing(3),
  borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
  flex: 1,
}));

export const PricingFeature = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'index',
})(({ theme, index = 0 }) => ({
  display: 'flex',
  // flex-start so the checkmark stays pinned to the top of the FIRST line
  // when text wraps to a second line — `center` would float the icon between
  // the two lines, which looks broken.
  alignItems: 'flex-start',
  // Cards inherit textAlign:'center' from PricingCard. Override here so the
  // checklist reads as a left-aligned bullet list — wrapped text continues
  // under itself instead of centering the second line.
  textAlign: 'left',
  gap: theme.spacing(1.25),
  fontSize: '0.825rem',
  fontWeight: 400,
  lineHeight: 1.45,
  color: theme.palette.text.secondary,
  animation: `${featureReveal} 0.35s ease-out forwards`,
  animationDelay: `${0.05 + index * 0.04}s`,
  opacity: 0,
  '& svg': {
    fontSize: 18,
    color: '#B83B1D',
    flexShrink: 0,
    // Nudge the icon down ~2px so its optical center aligns with the
    // first line of text (under flex-start the icon top would otherwise
    // sit a hair above the text cap-height).
    marginTop: '2px',
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    opacity: 1,
  },
}));

export const PricingButton = styled(Button, {
  shouldForwardProp: (prop) => !['variant'].includes(prop),
})(({ theme, variant }) => ({
  marginTop: 'auto',
  padding: `${theme.spacing(1.5)} ${theme.spacing(3)}`,
  borderRadius: 999,
  fontWeight: 500,
  fontSize: '0.9375rem',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  minHeight: 48, // Touch-friendly target
  ...(variant === 'primary' ? {
    background: ACCENT_GRADIENT,
    color: '#ffffff',
    border: 'none',
    boxShadow: `0 8px 24px ${alpha(EMERALD_ACCENT, 0.2)}`,
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 12px 32px ${alpha(EMERALD_ACCENT, 0.25)}`,
    },
  } : variant === 'outline' ? {
    background: 'transparent',
    ...gradientTextStyles,
    border: `1px solid ${EMERALD_ACCENT}`,
    '&:hover': {
      background: ACCENT_GRADIENT_SOFT,
    },
  } : {
    background: 'rgba(255, 255, 255, 0.1)',
    color: '#ffffff',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.2)',
    },
  }),
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    '&:hover': {
      transform: 'none',
    },
  },
}));

// ============================================================================
// CTA SECTION - Enhanced
// ============================================================================

export const CtaSection = styled(Section)({
  padding: '120px 0',
  position: 'relative',
  overflow: 'hidden',
});

export const CtaWrapper = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? '#18181b'
    : '#1a1a1a',
  borderRadius: theme.restack?.tokens?.radii?.lg || 20,
  padding: theme.spacing(10),
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  // Subtle gradient overlay only
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 30%, rgba(184, 59, 29, 0.07) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(212, 105, 78, 0.05) 0%, transparent 50%)
    `,
    pointerEvents: 'none',
    animation: `${float} 15s ease-in-out infinite`,
  },
  [theme.breakpoints.down('md')]: {
    padding: `${theme.spacing(6)} ${theme.spacing(3)}`,
  },
}));

export const CtaContent = styled(Box)({
  position: 'relative',
  zIndex: 1,
});

export const CtaTitle = styled(Typography)({
  fontSize: 'clamp(28px, 4vw, 44px)',
  fontWeight: 600,
  marginBottom: 16,
  letterSpacing: '-0.02em',
  color: '#ffffff',
});

export const CtaSubtitle = styled(Typography)({
  fontSize: '1.125rem',
  color: 'rgba(255, 255, 255, 0.7)',
  marginBottom: 40,
  maxWidth: 480,
  marginLeft: 'auto',
  marginRight: 'auto',
});

export const CtaButton = styled(Button)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
  background: ACCENT_GRADIENT,
  color: '#ffffff',
  textDecoration: 'none',
  borderRadius: 999,
  fontWeight: 600,
  fontSize: '1rem',
  transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
  boxShadow: `0 8px 32px ${alpha(EMERALD_ACCENT, 0.25)}`,
  border: 'none',
  cursor: 'pointer',
  textTransform: 'none',
  position: 'relative',
  overflow: 'hidden',
  // Shimmer effect
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: `linear-gradient(90deg, transparent, ${alpha('#fff', 0.2)}, transparent)`,
    animation: `${shimmer} 3s infinite`,
  },
  '&:hover': {
    transform: 'translateY(-3px) scale(1.02)',
    boxShadow: `0 16px 48px ${alpha(EMERALD_ACCENT, 0.5)}`,
    background: ACCENT_GRADIENT_HOVER,
  },
  '& .arrow': {
    transition: 'transform 0.3s ease',
  },
  '&:hover .arrow': {
    transform: 'translateX(4px)',
  },
}));

export const CtaNote = styled(Typography)({
  marginTop: 20,
  fontSize: '0.875rem',
  color: 'rgba(255, 255, 255, 0.5)',
});

// ============================================================================
// ANIMATION UTILITIES
// ============================================================================

// Scroll reveal. Deliberately restrained: a short rise and fade, nothing else.
// The previous version travelled 40px, scaled from 0.98, and blurred 6px over
// 0.7s. At normal scroll speed that meant a reader met most sections while the
// type was still soft and drifting, which is what made the page feel unsettled.
// A 14px rise over 0.45s lands before the eye arrives, and dropping the blur
// and the scale keeps text crisp (both also forced expensive repaints).
export const AnimateOnScroll = styled(Box, {
  shouldForwardProp: (prop) => !['isVisible', 'delay'].includes(prop),
})(({ isVisible, delay = 0 }) => ({
  opacity: isVisible ? 1 : 0,
  transform: isVisible ? 'translateY(0)' : 'translateY(14px)',
  transition: `
    opacity 0.45s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s,
    transform 0.45s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s
  `,
  '@media (prefers-reduced-motion: reduce)': {
    opacity: 1,
    transform: 'none',
    transition: 'none',
  },
}));

export const CenteredHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(8),
  '& > *': {
    margin: '0 auto',
  },
}));

// ============================================================================
// MARKETING IMAGE COMPONENTS
// ============================================================================

export const MarketingImage = styled(Box)(({ theme }) => ({
  borderRadius: theme.restack?.tokens?.radii?.md || 16,
  overflow: 'hidden',
  boxShadow: `0 20px 60px ${alpha('#000', 0.15)}`,
  border: `1px solid ${alpha(getAccentColor(theme), 0.1)}`,
  '& img': {
    width: '100%',
    height: 'auto',
    display: 'block',
  },
}));

export const GifContainer = styled(Box)(({ theme }) => ({
  borderRadius: theme.restack?.tokens?.radii?.md || 16,
  overflow: 'hidden',
  boxShadow: `0 16px 48px ${alpha('#000', 0.12)}`,
  background: theme.palette.background.paper,
  border: `1px solid ${alpha(getAccentColor(theme), 0.1)}`,
  '& img': {
    width: '100%',
    height: 'auto',
    display: 'block',
  },
}));

// ============================================================================
// LOGO CAROUSEL - Infinite Marquee
// ============================================================================

const marqueeScroll = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

export const LogoCarouselSection = styled(Box)(({ theme }) => ({
  padding: `${theme.spacing(6)} 0`,
  background: theme.palette.mode === 'dark'
    ? alpha('#ffffff', 0.02)
    : alpha('#000000', 0.02),
  borderTop: `1px solid ${alpha(getAccentColor(theme), 0.08)}`,
  borderBottom: `1px solid ${alpha(getAccentColor(theme), 0.08)}`,
  width: '100vw',
  marginLeft: 'calc(-50vw + 50%)',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    padding: `${theme.spacing(4)} 0`,
  },
}));

export const LogoCarouselHeader = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  fontWeight: 400,
  color: theme.palette.text.secondary,
  textAlign: 'center',
  marginBottom: theme.spacing(3),
  textTransform: 'none',
  letterSpacing: 'normal',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    marginBottom: theme.spacing(2),
  },
}));

export const LogoCarouselWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
}));

export const LogoCarouselTrackWrapper = styled(Box)({
  display: 'flex',
  overflow: 'hidden',
  width: '100%',
  // Gradient fade masks on edges
  maskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
  WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
});

export const LogoCarouselTrack = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  // Tightened from spacing(10) — combined with the new fixed slot
  // sizing on LogoCarouselItem this keeps the strip dense and unified
  // rather than letting each logo claim its own oversized footprint.
  gap: theme.spacing(6),
  // Infinite marquee animation
  animation: `${marqueeScroll} 60s linear infinite`,
  width: 'max-content',
  '&:hover': {
    animationPlayState: 'paused',
  },
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(4),
    animation: `${marqueeScroll} 45s linear infinite`,
  },
}));

export const LogoCarouselItem = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  // Compact slot footprint — matches the hero strip's static desktop
  // size (108×30). Reduced from 140×36 so the workflow-fold logo strip
  // reads as a refined trust rail rather than a logo parade.
  width: 108,
  height: 30,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: theme.palette.mode === 'dark' ? 0.6 : 0.5,
  filter: theme.palette.mode === 'dark'
    ? 'brightness(0) invert(1)'
    : 'grayscale(100%)',
  transition: 'all 0.3s ease',
  userSelect: 'none',
  '&:hover': {
    opacity: 0.9,
    filter: theme.palette.mode === 'dark'
      ? 'brightness(0) invert(1)'
      : 'grayscale(0%)',
    transform: 'scale(1.06)',
  },
  // Force every img into the slot regardless of the inline height passed
  // from logo metadata — overrides keep the visual rhythm consistent.
  '& img': {
    display: 'block',
    objectFit: 'contain',
    maxWidth: '100%',
    maxHeight: '100%',
    width: 'auto',
    height: 'auto !important',
  },
  [theme.breakpoints.down('md')]: {
    width: 96,
    height: 26,
  },
  [theme.breakpoints.down('sm')]: {
    width: 84,
    height: 24,
  },
}));

// ============================================================================
// METRICS BANNER - Social Proof Stats
// ============================================================================

export const MetricsBanner = styled(Box)(({ theme }) => ({
  // Refined stat band: a hairline surface card with vertical dividers
  // between each metric. Matches the artifact-card 14px radius and the
  // accent-hairline treatment used across the security/pricing sections.
  display: 'flex',
  alignItems: 'stretch',
  justifyContent: 'center',
  flexWrap: 'nowrap',
  gap: 0,
  maxWidth: 920,
  margin: `${theme.spacing(4)} auto 0`,
  padding: `${theme.spacing(3.5)} ${theme.spacing(2)}`,
  borderRadius: 16,
  border: `1px solid ${alpha(getAccentColor(theme), 0.12)}`,
  background: theme.palette.mode === 'dark'
    ? alpha('#ffffff', 0.03)
    : alpha('#ffffff', 0.55),
  [theme.breakpoints.down('md')]: {
    padding: `${theme.spacing(3)} ${theme.spacing(1)}`,
  },
  [theme.breakpoints.down('sm')]: {
    flexWrap: 'wrap',
    rowGap: theme.spacing(3),
    padding: `${theme.spacing(3)} ${theme.spacing(1.5)}`,
  },
}));

export const MetricItem = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: theme.spacing(0.75),
  padding: `0 ${theme.spacing(2)}`,
  minWidth: 0,
  // Vertical hairline divider between adjacent metrics.
  '& + &': {
    borderLeft: `1px solid ${alpha(getAccentColor(theme), 0.12)}`,
  },
  [theme.breakpoints.down('sm')]: {
    flex: '0 0 100%',
    padding: `0 ${theme.spacing(1)}`,
    '& + &': {
      borderLeft: 'none',
      borderTop: `1px solid ${alpha(getAccentColor(theme), 0.1)}`,
      paddingTop: theme.spacing(3),
    },
  },
}));

export const MetricValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'inherit',
  fontSize: '2.5rem',
  fontWeight: 700,
  fontStyle: 'normal',
  letterSpacing: '-0.03em',
  textAlign: 'center',
  ...gradientTextStyles,
  // Small inline padding keeps the gradient mask off the glyph edges.
  paddingInline: '0.04em',
  paddingBlock: '0.05em',
  lineHeight: 1.15,
  [theme.breakpoints.down('md')]: {
    fontSize: '2.125rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.75rem',
  },
}));

export const MetricLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
  },
}));

// ============================================================================
// HOW IT WORKS ANIMATION COMPONENTS
// ============================================================================

// Total animation cycle: 16 seconds (5 stages) - 20% faster
const ANIMATION_DURATION = 16;

// Additional keyframes for enhanced animations
const linkSlideIn = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-10px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

const checkmarkPop = keyframes`
  0% {
    opacity: 0;
    transform: scale(0);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const editHighlight = keyframes`
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
`;

export const HowItWorksAnimationContainer = styled(Box)(({ theme }) => ({
  maxWidth: 1100,
  width: '100%',
  height: 480,
  margin: '0 auto',
  padding: theme.spacing(5),
  borderRadius: theme.restack?.tokens?.radii?.lg || 20,
  background: theme.palette.mode === 'dark'
    ? alpha('#ffffff', 0.02)
    : alpha('#000000', 0.015),
  border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.06) : alpha('#000000', 0.06)}`,
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Animated gradient border
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: -1,
    borderRadius: 'inherit',
    padding: 1,
    background: `conic-gradient(from var(--gradient-angle, 0deg),
      ${alpha(EMERALD_ACCENT, 0.4)},
      transparent 25%,
      transparent 75%,
      ${alpha(EMERALD_ACCENT, 0.4)})`,
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    animation: `${gradientBorderRotate} 6s linear infinite`,
    opacity: 0.7,
    pointerEvents: 'none',
  },
  // Progress bar at bottom
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    height: 3,
    background: `linear-gradient(90deg, ${ORANGE_ACCENT}, ${EMERALD_ACCENT})`,
    borderRadius: '0 0 24px 24px',
    animation: `${stageProgress} 16s ease-in-out infinite`,
  },
  '@media (prefers-reduced-motion: reduce)': {
    '&::before, &::after': {
      animation: 'none',
    },
    '& *': {
      animation: 'none !important',
    },
  },
  [theme.breakpoints.down('md')]: {
    height: 420,
    padding: theme.spacing(4),
  },
  [theme.breakpoints.down('sm')]: {
    height: 360,
    padding: theme.spacing(3),
  },
}));

// Stage visibility controller - each stage fades in/out at its scheduled time
export const AnimationStage = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'stage',
})(({ theme, stage }) => {
  // Stage timing in the 20-second cycle (percentages) - 5 stages
  const stageTiming = {
    typing: { start: 0, end: 15 },       // 0s - 3s
    research: { start: 15, end: 30 },    // 3s - 6s
    slides: { start: 30, end: 50 },      // 6s - 10s
    iterate: { start: 50, end: 70 },     // 10s - 14s
    export: { start: 70, end: 90 },      // 14s - 18s
  };

  const timing = stageTiming[stage] || stageTiming.typing;

  // Create keyframe for this specific stage with improved transitions (scale + blur)
  const stageVisibility = keyframes`
    0%, ${timing.start}% {
      opacity: 0;
      transform: scale(0.92) translateY(16px);
      filter: blur(8px);
      visibility: hidden;
    }
    ${timing.start + 3}% {
      opacity: 1;
      transform: scale(1) translateY(0);
      filter: blur(0);
      visibility: visible;
    }
    ${timing.end - 3}% {
      opacity: 1;
      transform: scale(1) translateY(0);
      filter: blur(0);
      visibility: visible;
    }
    ${timing.end}%, 100% {
      opacity: 0;
      transform: scale(1.04) translateY(-10px);
      filter: blur(6px);
      visibility: hidden;
    }
  `;

  return {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    opacity: 0,
    visibility: 'hidden',
    animation: `${stageVisibility} ${ANIMATION_DURATION}s ease-in-out infinite`,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  };
});

// Stage 1: Typing Input — borrows the hero's pptx-sidebar surface
// language: hairline border, no elevated shadow, smaller radius. The
// input reads as "chat composer" rather than "elevated UI card".
export const TypingInputBox = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 540,
  padding: `${theme.spacing(1.5)} ${theme.spacing(1.75)}`,
  background: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.02) : '#fafafa',
  borderRadius: 10,
  border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.06) : '#e8e8e8'}`,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  boxShadow: 'none',
}));

export const TypedText = styled(Box)(({ theme }) => ({
  fontFamily: '"Inter", sans-serif',
  fontSize: '0.82rem',
  lineHeight: 1.5,
  color: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.88) : '#444',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  borderRight: 'none',
  animation: `${typeText} 3.5s steps(60, end) forwards`,
  '&::before': {
    content: '"Build a market sizing deck for Nordic fintech entry (use attached data room)"',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    '&::before': {
      content: '"Build a market sizing deck..."',
    },
  },
}));

// Brand-orange caret matches the hero's pptx-sidebar accent. Lighter
// width (1.5px) to keep the composer feeling native, not blocky.
export const TypingCursor = styled(Box)(({ theme }) => ({
  width: 1.5,
  height: '1.05em',
  background: '#B83B1D',
  borderRadius: 1,
  animation: `${cursorBlink} 0.8s step-end infinite`,
  flexShrink: 0,
}));

// Stage 2: Research Phase — restraint pass. Big 1.5rem ResearchLabel
// + 10px ACCENT_GRADIENT pulsing dots was loud; collapsed into a single
// compact eyebrow header (matching IterateHeader's pattern) and the
// dots shrunk to small brand-orange beats.
export const ResearchPhaseContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  width: '100%',
  maxWidth: 540,
}));

export const ResearchHeader = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  marginBottom: theme.spacing(0.25),
}));

export const ResearchDotsContainer = styled(Box)({
  display: 'inline-flex',
  gap: 4,
  alignItems: 'center',
});

export const PulsingDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'delay',
})(({ delay = 0 }) => ({
  width: 5,
  height: 5,
  borderRadius: '50%',
  background: '#B83B1D',
  animation: `${researchPulse} 0.8s ease-in-out infinite`,
  animationDelay: `${delay}s`,
}));

export const ResearchLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.7rem',
  fontWeight: 400,
  letterSpacing: 'normal',
  textTransform: 'none',
  color: theme.palette.text.secondary,
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.65rem',
  },
}));

export const ResearchLinksContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75),
  width: '100%',
  maxWidth: 460,
}));

// Hairline cards, no shadow — same surface treatment as the hero's
// step-trail rows. Lets the trail read as a quiet checklist rather than
// a stack of elevated UI cards.
export const ResearchLinkCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'index',
})(({ theme, index = 0 }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  padding: `${theme.spacing(0.85)} ${theme.spacing(1.25)}`,
  background: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.02) : '#fafafa',
  borderRadius: 8,
  border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.06) : '#e8e8e8'}`,
  boxShadow: 'none',
  animation: `${linkSlideIn} 0.4s ease forwards`,
  animationDelay: `${index * 0.15}s`,
  opacity: 0,
  [theme.breakpoints.down('sm')]: {
    padding: `${theme.spacing(0.7)} ${theme.spacing(1)}`,
  },
}));

export const LinkFavicon = styled(Box)(({ theme }) => ({
  width: 18,
  height: 18,
  borderRadius: 4,
  background: theme.palette.mode === 'dark'
    ? alpha('#ffffff', 0.05)
    : alpha('#000000', 0.04),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  fontSize: 10,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  '& svg': {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
}));

export const LinkContent = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
});

export const LinkTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 400,
  color: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.92) : '#333',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: 1.35,
}));

export const LinkUrl = styled(Typography)(({ theme }) => ({
  fontSize: '0.65rem',
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: 1.35,
}));

// Brand-orange checkmark instead of #B83B1D green — matches the hero
// step trail's `--check` affordance and keeps the section's accent
// palette consistent.
export const LinkCheckmark = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'index',
})(({ index = 0 }) => ({
  width: 14,
  height: 14,
  borderRadius: '50%',
  background: '#B83B1D',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  animation: `${checkmarkPop} 0.3s ease forwards`,
  animationDelay: `${0.3 + index * 0.15}s`,
  opacity: 0,
  '& svg': {
    fontSize: 10,
    color: '#ffffff',
  },
}));

// Stage 2: Analytical Synthesis (slide thumbnails grid). Restraint pass:
// 1.5rem header → compact eyebrow with brand-orange dot. Slide thumbs
// drop their elevated 0 4px 16px shadow + slideGlow flicker for a flat
// hairline-bordered look that reads as "preview frame", not "UI card".
export const SlidesContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  width: '100%',
}));

export const SlidesHeader = styled(Typography)(({ theme }) => ({
  fontSize: '0.7rem',
  fontWeight: 600,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  textAlign: 'center',
  marginBottom: theme.spacing(1),
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  '&::before': {
    content: '""',
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#B83B1D',
    boxShadow: `0 0 0 3px ${alpha('#B83B1D', 0.15)}`,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.65rem',
  },
}));

export const SlidesGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: theme.spacing(1.5),
  width: '100%',
  maxWidth: 820,
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
    maxWidth: 600,
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    maxWidth: 380,
    gap: theme.spacing(1),
  },
}));

export const SlideCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'index',
})(({ theme, index = 0 }) => ({
  aspectRatio: '16/10',
  borderRadius: 8,
  background: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.02) : '#ffffff',
  border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.06) : '#e8e8e8'}`,
  padding: theme.spacing(1.25),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  animation: `${slidePopIn} 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards`,
  animationDelay: `${index * 0.1}s`,
  opacity: 0,
  boxShadow: 'none',
  [theme.breakpoints.down('md')]: {
    '&:nth-of-type(4)': {
      display: 'none',
    },
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.75),
    '&:nth-of-type(3), &:nth-of-type(4)': {
      display: 'none',
    },
  },
}));

export const SlideCardHeader = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 600,
  color: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.92) : '#333',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.08) : '#ececec'}`,
  paddingBottom: theme.spacing(0.6),
  marginBottom: theme.spacing(0.6),
  [theme.breakpoints.down('sm')]: {
    fontSize: 10,
  },
}));

export const SlideCardBody = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  flex: 1,
  justifyContent: 'center',
});

export const SlideCardLine = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'width',
})(({ theme, width = '100%' }) => ({
  height: 4,
  width,
  borderRadius: 2,
  background: theme.palette.mode === 'dark'
    ? alpha('#ffffff', 0.1)
    : alpha('#000000', 0.08),
  [theme.breakpoints.down('sm')]: {
    height: 3,
  },
}));

export const SlideCardChart = styled(Box)(({ theme }) => ({
  width: '100%',
  flex: 1,
  minHeight: 32,
  borderRadius: 6,
  background: theme.palette.mode === 'dark'
    ? alpha('#ffffff', 0.03)
    : alpha('#000000', 0.02),
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-around',
  padding: '6px 8px 6px',
  [theme.breakpoints.down('sm')]: {
    minHeight: 24,
    padding: '4px 6px 4px',
  },
}));

export const ChartBar = styled(Box, {
  shouldForwardProp: (prop) => !['height', 'index'].includes(prop),
})(({ theme, height = '50%', index = 0 }) => ({
  width: '18%',
  height: height,
  background: 'transparent',
  border: `1.5px solid ${EMERALD_ACCENT}`,
  borderRadius: 3,
  animation: `${slidePopIn} 0.4s ease-out forwards`,
  animationDelay: `${index * 0.1}s`,
  opacity: 0,
}));

export const SlideCardBullets = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
});

const bulletWidths = ['85%', '70%', '90%', '60%'];
export const BulletPoint = styled(Box, {
  shouldForwardProp: (prop) => !['width', 'index'].includes(prop),
})(({ theme, index = 0 }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  animation: `${slidePopIn} 0.3s ease-out forwards`,
  animationDelay: `${index * 0.08}s`,
  opacity: 0,
  '&::before': {
    content: '""',
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: 'transparent',
    border: `1.5px solid ${EMERALD_ACCENT}`,
    flexShrink: 0,
  },
  '&::after': {
    content: '""',
    height: 4,
    width: bulletWidths[index % bulletWidths.length],
    borderRadius: 2,
    background: theme.palette.mode === 'dark'
      ? alpha('#ffffff', 0.1)
      : alpha('#000000', 0.08),
  },
}));

// Stage 4: Iteration - Chat window
export const IterateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1.75),
  width: '100%',
  maxWidth: 540,
}));

// Restrained step header — borrows the hero's compact eyebrow aesthetic
// (uppercase, tight letter-spacing, muted) instead of a loud 1.5rem title.
// Matches the visual restraint of `.pptx-sidebar__step-label`.
export const IterateHeader = styled(Typography)(({ theme }) => ({
  fontSize: '0.7rem',
  fontWeight: 600,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  textAlign: 'center',
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  '&::before': {
    content: '""',
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#B83B1D',
    boxShadow: `0 0 0 3px ${alpha('#B83B1D', 0.15)}`,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.65rem',
  },
}));

export const IterateSlidePreview = styled(Box)(({ theme }) => ({
  width: 240,
  aspectRatio: '16/10',
  borderRadius: theme.restack?.tokens?.radii?.sm || 12,
  background: theme.palette.background.paper,
  border: `2px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.15) : alpha('#000000', 0.12)}`,
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  position: 'relative',
  boxShadow: theme.palette.mode === 'dark'
    ? `0 4px 20px ${alpha('#000', 0.3)}`
    : `0 4px 20px ${alpha('#000', 0.1)}`,
  flexShrink: 0,
  [theme.breakpoints.down('md')]: {
    width: 200,
  },
  [theme.breakpoints.down('sm')]: {
    width: 180,
    padding: theme.spacing(1.5),
  },
}));

export const IterateHighlight = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '30%',
  left: theme.spacing(1),
  right: theme.spacing(1),
  height: 12,
  borderRadius: 4,
  background: `linear-gradient(90deg, ${alpha(EMERALD_ACCENT, 0.2)}, ${alpha(ORANGE_ACCENT, 0.25)}, ${alpha(EMERALD_ACCENT, 0.2)})`,
  backgroundSize: '200% 100%',
  animation: `${editHighlight} 2s ease-in-out infinite`,
}));

// Borrowed from the hero's pptx-sidebar — flat card, hairline border,
// no heavy box shadow. Lighter background (off-white in light mode) so
// it reads as "chat surface" not "elevated UI card".
export const IterateChatBubble = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 480,
  padding: theme.spacing(1.5),
  background: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.02) : '#fafafa',
  borderRadius: 10,
  border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.06) : '#e8e8e8'}`,
  boxShadow: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

export const IterateChatMessage = styled(Box, {
  shouldForwardProp: (prop) => !['isUser', 'index'].includes(prop),
})(({ theme, isUser, index = 0 }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: isUser ? 'flex-end' : 'flex-start',
  gap: theme.spacing(0.35),
  animation: `${slidePopIn} 0.4s ease-out forwards`,
  animationDelay: `${index * 0.3}s`,
  opacity: 0,
}));

// Tiny uppercase sender label, hero pattern. Sits above the bubble so we
// can drop the bulky 32px gradient avatars while still distinguishing
// speakers. Brand orange dot for the AI side anchors it to Restack.
export const ChatAvatar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})(({ theme, isUser }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  fontSize: '0.6rem',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  paddingLeft: theme.spacing(0.5),
  paddingRight: theme.spacing(0.5),
  '&::before': isUser ? undefined : {
    content: '""',
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: '#B83B1D',
    flexShrink: 0,
  },
  '& svg': { display: 'none' },
}));

// Hero-aligned bubble: compact (0.78rem), tight padding, neutral gray for
// the user (mirrors `.pptx-sidebar__msg--user`'s #f0f0f0 + #333) and a
// faint brand-orange tint for the AI (`rgba(184,59,29,0.06)`). No
// gradients, no shadow — keeps the section feeling restrained.
export const ChatText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})(({ theme, isUser }) => ({
  fontSize: '0.78rem',
  lineHeight: 1.5,
  maxWidth: '88%',
  padding: `${theme.spacing(0.85)} ${theme.spacing(1.25)}`,
  borderRadius: 10,
  borderTopRightRadius: isUser ? 3 : 10,
  borderTopLeftRadius: isUser ? 10 : 3,
  background: isUser
    ? (theme.palette.mode === 'dark' ? alpha('#ffffff', 0.08) : '#f0f0f0')
    : (theme.palette.mode === 'dark' ? alpha('#B83B1D', 0.14) : 'rgba(184, 59, 29, 0.06)'),
  color: isUser
    ? theme.palette.text.primary
    : (theme.palette.mode === 'dark' ? alpha('#ffffff', 0.92) : '#444'),
  boxShadow: 'none',
  border: 'none',
}));

// Tiny inline status pill — used after the AI's iterate response to give
// visual proof of the change. Mirrors the hero's `--check` checkmark
// affordance but lives inline in the chat surface, not the step trail.
export const IterateChangePill = styled(Box)(({ theme }) => ({
  alignSelf: 'flex-start',
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginLeft: theme.spacing(0.5),
  marginTop: theme.spacing(0.25),
  padding: `${theme.spacing(0.3)} ${theme.spacing(0.85)}`,
  fontSize: '0.65rem',
  fontWeight: 500,
  letterSpacing: '0.01em',
  color: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.85) : '#444',
  background: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.04) : '#ffffff',
  border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.1) : '#e8e8e8'}`,
  borderRadius: 999,
  animation: `${slidePopIn} 0.4s ease-out 1s forwards`,
  opacity: 0,
  '& .check-glyph': {
    width: 12,
    height: 12,
    borderRadius: '50%',
    background: '#B83B1D',
    color: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 9,
    flexShrink: 0,
  },
}));

// Three-dot typing indicator that appears before the AI bubble — borrowed
// from `.pptx-sidebar__thinking` in the hero. Makes the chat feel real
// rather than a static panel of text.
export const ChatTypingDots = styled(Box)(({ theme }) => ({
  alignSelf: 'flex-start',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
  borderRadius: 10,
  borderTopLeftRadius: 3,
  background: theme.palette.mode === 'dark' ? alpha('#B83B1D', 0.14) : 'rgba(184, 59, 29, 0.06)',
  marginLeft: theme.spacing(0.5),
  marginTop: theme.spacing(0.25),
  animation: `${slidePopIn} 0.3s ease-out 0.4s forwards, fadeOutDots 0.3s ease-in 0.95s forwards`,
  opacity: 0,
  '& > span': {
    width: 4,
    height: 4,
    borderRadius: '50%',
    background: '#B83B1D',
    animation: 'heroDotBounce 0.6s ease-in-out infinite',
  },
  '& > span:nth-of-type(2)': { animationDelay: '0.15s' },
  '& > span:nth-of-type(3)': { animationDelay: '0.3s' },
  '@keyframes fadeOutDots': {
    to: { opacity: 0, transform: 'translateY(-4px)' },
  },
}));

export const IterateArrow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: EMERALD_ACCENT,
  fontSize: 24,
  animation: `${pulse} 1.5s ease-in-out infinite`,
  [theme.breakpoints.down('md')]: {
    transform: 'rotate(90deg)',
  },
}));

// Stage 5: Export - Slides to formats animation
export const ExportContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(3),
  width: '100%',
  maxWidth: 700,
}));

export const ExportHeader = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.125rem',
  },
}));

export const ExportVisual = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(2),
  },
}));

// Stacked slides visual
export const ExportSlidesStack = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: 140,
  height: 100,
  animation: `${slidePopIn} 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards`,
  opacity: 0,
  [theme.breakpoints.down('sm')]: {
    width: 110,
    height: 80,
  },
}));

export const ExportSlideCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'index',
})(({ theme, index = 0 }) => ({
  position: 'absolute',
  width: 120,
  height: 75,
  borderRadius: 8,
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.12) : alpha('#000000', 0.1)}`,
  boxShadow: theme.palette.mode === 'dark'
    ? `0 3px 12px ${alpha('#000', 0.3)}`
    : `0 3px 12px ${alpha('#000', 0.12)}`,
  display: 'flex',
  flexDirection: 'column',
  padding: 10,
  gap: 5,
  top: index * 8,
  left: index * 8,
  zIndex: 3 - index,
  // Mini slide content
  '&::before': {
    content: '""',
    width: '55%',
    height: 6,
    borderRadius: 3,
    background: index === 0 ? `linear-gradient(90deg, ${ORANGE_ACCENT}, ${EMERALD_ACCENT})`
      : theme.palette.mode === 'dark' ? alpha('#ffffff', 0.15) : alpha('#000000', 0.1),
  },
  '&::after': {
    content: '""',
    width: '75%',
    height: 4,
    borderRadius: 2,
    background: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.08) : alpha('#000000', 0.06),
  },
  [theme.breakpoints.down('sm')]: {
    width: 90,
    height: 56,
    padding: 6,
    top: index * 6,
    left: index * 6,
  },
}));

export const ExportFormatsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1),
  },
}));

export const ExportFormatIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'index',
})(({ theme, index = 0 }) => ({
  width: 64,
  height: 64,
  borderRadius: 12,
  background: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.05) : alpha('#000000', 0.03),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  animation: `${slidePopIn} 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards`,
  animationDelay: `${0.2 + index * 0.15}s`,
  opacity: 0,
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '& img': {
    width: 44,
    height: 44,
    objectFit: 'contain',
  },
  [theme.breakpoints.down('sm')]: {
    width: 48,
    height: 48,
    '& img': {
      width: 32,
      height: 32,
    },
  },
}));

export const ExportArrow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: EMERALD_ACCENT,
  animation: `${pulse} 1.5s ease-in-out infinite`,
  '& svg': {
    fontSize: 32,
  },
  [theme.breakpoints.down('sm')]: {
    '& svg': {
      fontSize: 24,
    },
  },
}));

// Keep these for backwards compatibility but they're no longer used
export const ExportFileIcon = styled(Box)({ display: 'none' });
export const ExportFileLabel = styled(Typography)({ display: 'none' });
export const ExportDestination = styled(Box)({ display: 'none' });
export const ExportDestinationLabel = styled(Typography)({ display: 'none' });
export const ExportDestinationDesc = styled(Typography)({ display: 'none' });

// Legacy exports for backwards compatibility (can be removed if not used elsewhere)
export const ResearchSourcesIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(0.5),
  '& > div': {
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: alpha(EMERALD_ACCENT, 0.35),
    animation: `${fadeIn} 0.3s ease forwards`,
  },
}));

export const StructuringContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: theme.spacing(2),
  width: '100%',
  maxWidth: 520,
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    maxWidth: 320,
    gap: theme.spacing(1.5),
  },
}));

export const WireframeSlide = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'index',
})(({ theme, index = 0 }) => ({
  aspectRatio: '16/10',
  borderRadius: theme.restack?.tokens?.radii?.sm || 10,
  border: `2px dashed ${alpha(EMERALD_ACCENT, 0.35)}`,
  background: theme.palette.mode === 'dark'
    ? alpha('#ffffff', 0.02)
    : alpha('#000000', 0.02),
  padding: theme.spacing(1.5),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75),
  animation: `${drawWireframe} 0.5s ease forwards`,
  animationDelay: `${index * 0.2}s`,
  opacity: 0,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
    '&:nth-of-type(3)': {
      display: 'none',
    },
  },
}));

export const WireframeLine = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'width' && prop !== 'delay',
})(({ theme, width = '100%', delay = 0 }) => ({
  height: 6,
  width,
  borderRadius: 3,
  background: alpha(EMERALD_ACCENT, 0.2),
  transformOrigin: 'left',
  animation: `${wireframeLineGrow} 0.4s ease forwards`,
  animationDelay: `${delay}s`,
  transform: 'scaleX(0)',
  [theme.breakpoints.down('sm')]: {
    height: 5,
  },
}));

export const FinalSlidesContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: theme.spacing(1.5),
  width: '100%',
  maxWidth: 600,
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
    maxWidth: 450,
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    maxWidth: 280,
    gap: theme.spacing(1),
  },
}));

// ============================================================================
// BLOG / INSIGHTS SECTION
// ============================================================================

export const BlogGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: theme.spacing(4),
  maxWidth: 760,
  marginLeft: 'auto',
  marginRight: 'auto',
  borderTop: `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.08) : alpha('#1e1612', 0.08)}`,
}));

export const BlogCard = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '100px 1fr auto',
  alignItems: 'baseline',
  columnGap: theme.spacing(3),
  rowGap: theme.spacing(0.5),
  padding: `${theme.spacing(3)} ${theme.spacing(1)}`,
  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? alpha('#ffffff', 0.08) : alpha('#1e1612', 0.08)}`,
  transition: 'background 0.2s ease',
  cursor: 'pointer',
  textDecoration: 'none',
  color: 'inherit',
  '&:hover': {
    background: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.02) : alpha('#1e1612', 0.015),
  },
  '&:hover .blog-read-more': {
    transform: 'translateX(3px)',
  },
  '&:hover .blog-title': {
    color: theme.palette.text.primary,
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    rowGap: theme.spacing(0.75),
    padding: `${theme.spacing(2.5)} ${theme.spacing(0.5)}`,
  },
}));

export const BlogCategory = styled(Box)(({ theme }) => ({
  fontSize: '0.7rem',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  color: theme.palette.text.secondary,
  opacity: 0.7,
  alignSelf: 'baseline',
  whiteSpace: 'nowrap',
}));

export const BlogTitle = styled(Typography)(({ theme }) => ({
  // Sans-serif intentionally — serif is reserved for landing-page section
  // titles (where the editorial character earns its keep). Blog rows read
  // better as scannable sans copy.
  fontWeight: 600,
  fontSize: '1.0625rem',
  lineHeight: 1.35,
  color: theme.palette.text.primary,
  letterSpacing: '-0.01em',
  transition: 'color 0.2s ease',
}));

export const BlogExcerpt = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  lineHeight: 1.55,
  color: theme.palette.text.secondary,
  gridColumn: '2 / 3',
  display: '-webkit-box',
  WebkitLineClamp: 1,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    gridColumn: '1 / -1',
  },
}));

export const BlogMeta = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  alignSelf: 'baseline',
  whiteSpace: 'nowrap',
}));

export const BlogReadMore = styled('span')({
  ...gradientTextStyles,
  fontWeight: 500,
  fontSize: '0.75rem',
  display: 'inline-block',
  transition: 'transform 0.2s ease',
});

// ============================================================================
// EXECUTIVE PULLQUOTE BAND (P3) — sits between Hero and Workflow
// ============================================================================

export const PullquoteBand = styled(Box)(({ theme }) => ({
  maxWidth: 880,
  margin: '0 auto',
  padding: `${theme.spacing(3)} ${theme.spacing(3)}`,
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

export const PullquoteMark = styled('span')(({ theme }) => ({
  display: 'block',
  fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif',
  fontStyle: 'normal',
  fontSize: '3.5rem',
  lineHeight: 0.6,
  color: alpha(getAccentColor(theme), 0.35),
  marginBottom: theme.spacing(1.25),
  userSelect: 'none',
}));

export const PullquoteText = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  fontStyle: 'normal',
  fontSize: '1.5rem',
  lineHeight: 1.4,
  letterSpacing: '-0.01em',
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.15rem',
    lineHeight: 1.45,
  },
}));

export const PullquoteAttribution = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  fontWeight: 400,
  letterSpacing: 'normal',
  textTransform: 'none',
  color: theme.palette.text.secondary,
}));

// ============================================================================
// USE CASE LIBRARY (P0) — between Core Capabilities and Impact
// ============================================================================

export const UseCaseCategories = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
}));

export const UseCaseCategory = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
}));

export const UseCaseCategoryLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: alpha(getAccentColor(theme), 0.85),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '&::after': {
    content: '""',
    flex: 1,
    height: 1,
    background: `linear-gradient(90deg, ${alpha(getAccentColor(theme), 0.2)} 0%, ${alpha(getAccentColor(theme), 0)} 100%)`,
  },
}));

export const UseCaseGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(1),
  },
}));

export const UseCaseCard = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: `${theme.spacing(1.75)} ${theme.spacing(2)}`,
  background: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.025)'
    : 'rgba(255, 255, 255, 0.7)',
  border: `1px solid ${theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.06)'
    : 'rgba(0, 0, 0, 0.05)'}`,
  borderRadius: 16,
  cursor: 'default',
  overflow: 'hidden',
  transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
  // Decorative ghost "slide frame" in the bottom-right — hints that each
  // use case maps to an actual slide artefact without adding any text.
  '&::after': {
    content: '""',
    position: 'absolute',
    right: -14,
    bottom: -14,
    width: 56,
    height: 36,
    borderRadius: 4,
    border: `1.5px solid ${theme.palette.mode === 'dark'
      ? alpha('#ffffff', 0.04)
      : alpha('#1e1612', 0.05)}`,
    background: theme.palette.mode === 'dark'
      ? alpha('#ffffff', 0.015)
      : alpha('#1e1612', 0.012),
    transform: 'rotate(-6deg)',
    pointerEvents: 'none',
    transition: 'transform 0.3s ease, border-color 0.3s ease',
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    borderColor: alpha(getAccentColor(theme), 0.3),
    background: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : '#ffffff',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 6px 20px ${alpha('#000', 0.3)}`
      : `0 6px 20px ${alpha('#1e1612', 0.08)}`,
  },
  '&:hover::after': {
    transform: 'rotate(-3deg) translate(-4px, -4px)',
    borderColor: alpha(getAccentColor(theme), 0.18),
  },
  [theme.breakpoints.down('sm')]: {
    padding: `${theme.spacing(1.5)} ${theme.spacing(1.75)}`,
    '&:hover': { transform: 'none' },
  },
}));

export const UseCaseIcon = styled(Box)(({ theme }) => ({
  width: 38,
  height: 38,
  flexShrink: 0,
  borderRadius: 12,
  // Stronger fill — Model ML's icons are solid coloured tiles, not soft
  // gradients. This reads better in a dense grid.
  background: `linear-gradient(135deg, ${alpha(getAccentColor(theme), 0.14)} 0%, ${alpha(getAccentColor(theme), 0.06)} 100%)`,
  border: `1px solid ${alpha(getAccentColor(theme), 0.18)}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  zIndex: 1,
  '& svg': {
    fontSize: 20,
    color: getAccentColor(theme),
  },
}));

export const UseCaseBody = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  flex: 1,
  position: 'relative',
  zIndex: 1, // sit above the decorative slide-frame ::after
});

export const UseCaseName = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  lineHeight: 1.25,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

export const UseCaseTime = styled(Typography)(({ theme }) => ({
  fontSize: '0.6875rem',
  color: theme.palette.text.secondary,
  letterSpacing: '0.02em',
  marginTop: 1,
}));

export const UseCaseFootnote = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(4),
  textAlign: 'center',
  fontSize: '0.8125rem',
  color: theme.palette.text.secondary,
  fontStyle: 'italic',
  '& strong': {
    color: theme.palette.text.primary,
    fontStyle: 'normal',
    fontWeight: 600,
  },
}));

// ============================================================================
// USE CASES MARQUEE — horizontal "film roll" of use-case cards (replaces
// the previous 26-card stacked grid, which read as too-many-buttons).
// Pattern borrowed from the testimonial marquee: full-bleed wrapper with
// edge-fade mask, a flex track at width:max-content animated by the
// shared `marqueeScroll` keyframe (0% → -50%), children duplicated 1:1
// for seamless looping. Slower 90s loop than testimonials' 60s because
// there are ~26 cards and the eye needs more time per card. Hover-pause
// for accessibility, prefers-reduced-motion fallback to native scroll.
// ============================================================================

export const UseCasesMarqueeWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  WebkitMaskImage:
    'linear-gradient(90deg, transparent 0%, #000 6%, #000 94%, transparent 100%)',
  maskImage:
    'linear-gradient(90deg, transparent 0%, #000 6%, #000 94%, transparent 100%)',
  '&:hover .use-cases-marquee-track': {
    animationPlayState: 'paused',
  },
  [theme.breakpoints.down('sm')]: {
    WebkitMaskImage:
      'linear-gradient(90deg, transparent 0%, #000 3%, #000 97%, transparent 100%)',
    maskImage:
      'linear-gradient(90deg, transparent 0%, #000 3%, #000 97%, transparent 100%)',
  },
}));

export const UseCasesMarqueeTrack = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  width: 'max-content',
  animation: `${marqueeScroll} 90s linear infinite`,
  paddingInline: theme.spacing(2),
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    scrollSnapType: 'x mandatory',
  },
  '& > *': {
    flex: '0 0 auto',
    width: 250,
    scrollSnapAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      width: 220,
    },
  },
}));

// Marquee variant of UseCaseCard — same hairline + icon shape as the
// grid card but loses the decorative slide-frame ::after (would overlap
// with adjacent cards in a tight horizontal flow) and shrinks the
// minimum width so cards don't dominate the row.
export const UseCaseMarqueeCard = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  padding: `${theme.spacing(1.5)} ${theme.spacing(1.75)}`,
  background: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.025)'
    : 'rgba(255, 255, 255, 0.7)',
  border: `1px solid ${theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.06)'
    : 'rgba(0, 0, 0, 0.05)'}`,
  borderRadius: 16,
  cursor: 'default',
  transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
  '&:hover': {
    borderColor: alpha(getAccentColor(theme), 0.3),
    background: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : '#ffffff',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 6px 20px ${alpha('#000', 0.3)}`
      : `0 6px 20px ${alpha('#1e1612', 0.08)}`,
  },
}));

// Tiny uppercase category eyebrow above the use-case name. Replaces the
// per-category section labels in the old grid layout — each card now
// carries its own category context so the marquee doesn't lose meaning.
export const UseCaseMarqueeCategory = styled(Typography)(({ theme }) => ({
  fontSize: '0.55rem',
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: getAccentColor(theme),
  lineHeight: 1.2,
  marginBottom: 2,
}));

// ============================================================================
// CASE STUDY CARDS (P1) — replaces single cycling testimonial in Impact
// ============================================================================

export const CaseStudyGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: theme.spacing(2.5),
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),
  },
}));

export const CaseStudyCard = styled(BaseCard)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  // Fixed height keeps the marquee row visually steady regardless of which
  // testimonial happens to be the longest.
  height: 320,
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(180deg, ${alpha('#ffffff', 0.02)} 0%, ${alpha('#ffffff', 0.01)} 100%)`
    : `linear-gradient(180deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#fafaf9', 0.6)} 100%)`,
  transition: 'transform 0.4s ease, box-shadow 0.4s ease, border-color 0.4s ease',
  '&:hover': {
    transform: 'translateY(-3px)',
    borderColor: alpha(getAccentColor(theme), 0.25),
    boxShadow: theme.palette.mode === 'dark'
      ? `0 14px 38px ${alpha('#000', 0.35)}`
      : `0 14px 38px ${alpha('#1e1612', 0.08)}`,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2.5),
    height: 340,
    '&:hover': { transform: 'none' },
  },
}));

export const CaseStudyHead = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
}));

export const CaseStudyTag = styled(Box)(({ theme }) => ({
  padding: `${theme.spacing(0.5)} ${theme.spacing(1.25)}`,
  background: ACCENT_GRADIENT_SOFT,
  borderRadius: 12,
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  ...gradientTextStyles,
}));

export const CaseStudyMetric = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#B83B1D',
  '& svg': {
    fontSize: 14,
  },
}));

export const CaseStudyText = styled(Typography)(({ theme }) => ({
  // Sans-serif body — testimonials read better as plain copy than as serif
  // pull-quotes. Editorial serif is reserved for section H2 emphasis.
  // Reduced from 0.9375rem (15px) → 0.8125rem (13px) so quote copy reads
  // as supporting evidence rather than dominating the card visually.
  fontWeight: 400,
  fontSize: '0.8125rem',
  lineHeight: 1.6,
  color: theme.palette.text.primary,
  flex: 1,
  // Cap to ~7 lines so cards in the marquee row stay visually balanced
  display: '-webkit-box',
  WebkitLineClamp: 7,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.78125rem',
    WebkitLineClamp: 8,
  },
}));

export const CaseStudyAuthor = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  paddingTop: theme.spacing(1.5),
  marginTop: theme.spacing(0.5),
  borderTop: `1px solid ${theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.06)'
    : 'rgba(0, 0, 0, 0.06)'}`,
}));

export const CaseStudyAvatar = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  background: ACCENT_GRADIENT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
  fontSize: '0.875rem',
  color: '#ffffff',
  flexShrink: 0,
}));

export const CaseStudyAuthorName = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  lineHeight: 1.25,
}));

export const CaseStudyAuthorRole = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.25,
}));

// ============================================================================
// TESTIMONIAL MARQUEE — slow horizontal "film roll" of case-study cards
// ============================================================================
// Why a marquee instead of a grid: the testimonials are the social proof
// surface. A continuous scroll communicates "we have lots of these" without
// requiring the reader to interact, and lets edge cards peek-and-fade like
// the Model ML reference. We render the card list twice and animate the
// track by exactly -50% so the loop is seamless. Pauses on hover for
// accessibility / readability. Reuses the existing `marqueeScroll` keyframe
// (defined for the logo carousel above) — same 0% → -50% loop pattern.

export const TestimonialMarqueeWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  // Renders as a direct Section child (outside Container), so it naturally
  // spans the section's full width — no negative-margin bleed needed.
  width: '100%',
  overflow: 'hidden',
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  // Fade edges so cards "enter" / "leave" the frame softly. Symmetric
  // 6%/94% fade region — enough to soften without eating an entire card.
  WebkitMaskImage:
    'linear-gradient(90deg, transparent 0%, #000 6%, #000 94%, transparent 100%)',
  maskImage:
    'linear-gradient(90deg, transparent 0%, #000 6%, #000 94%, transparent 100%)',
  // Pause animation when user hovers the row
  '&:hover .marquee-track': {
    animationPlayState: 'paused',
  },
  [theme.breakpoints.down('sm')]: {
    WebkitMaskImage:
      'linear-gradient(90deg, transparent 0%, #000 3%, #000 97%, transparent 100%)',
    maskImage:
      'linear-gradient(90deg, transparent 0%, #000 3%, #000 97%, transparent 100%)',
  },
}));

export const TestimonialMarqueeTrack = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2.5),
  width: 'max-content',
  // 60s linear loop reads as "ambient motion" — slow enough to read while
  // it passes, fast enough that the scene visibly changes during a glance.
  // -50% works because the children are duplicated 1:1 — when the first
  // half scrolls off, the second half (identical) is exactly where the
  // first half started, producing a seamless loop.
  animation: `${marqueeScroll} 60s linear infinite`,
  paddingInline: theme.spacing(2),
  // Respect users who prefer no motion — fall back to native scroll
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    scrollSnapType: 'x mandatory',
  },
  '& > *': {
    flex: '0 0 auto',
    width: 380,
    scrollSnapAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      width: 300,
    },
  },
}));

export const ImpactStatsRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: theme.spacing(2.5),
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(1.5),
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

// ============================================================================
// INTEGRATIONS STRIP (P2) — between Impact and Security
// ============================================================================

export const IntegrationsSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
  paddingTop: theme.spacing(2),
}));

export const IntegrationsGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

export const IntegrationsGroupLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.7rem',
  fontWeight: 600,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  textAlign: 'center',
}));

export const IntegrationsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1.25),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1),
  },
}));

export const IntegrationChip = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'upcoming',
})(({ theme, upcoming }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.875),
  padding: `${theme.spacing(1)} ${theme.spacing(1.75)}`,
  background: upcoming
    ? (theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.015)'
        : 'rgba(255, 255, 255, 0.55)')
    : (theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.03)'
        : '#ffffff'),
  border: upcoming
    ? `1px dashed ${theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.12)'
        : 'rgba(0, 0, 0, 0.13)'}`
    : `1px solid ${theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.07)'
        : 'rgba(0, 0, 0, 0.07)'}`,
  borderRadius: 12,
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: upcoming ? theme.palette.text.secondary : theme.palette.text.primary,
  opacity: upcoming ? 0.78 : 1,
  transition: 'all 0.25s ease',
  '&:hover': {
    borderColor: alpha(getAccentColor(theme), 0.3),
    transform: 'translateY(-1px)',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 4px 14px ${alpha('#000', 0.3)}`
      : `0 4px 14px ${alpha('#1e1612', 0.06)}`,
  },
  '& > svg': {
    fontSize: 16,
    color: theme.palette.text.secondary,
  },
  '& .integration-chip__upcoming-bell': {
    fontSize: 11,
    marginLeft: theme.spacing(0.25),
    color: getAccentColor(theme),
    opacity: 0.85,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    padding: `${theme.spacing(0.75)} ${theme.spacing(1.25)}`,
    '&:hover': { transform: 'none' },
  },
}));

export const IntegrationsFootnote = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  fontSize: '0.7rem',
  color: alpha(theme.palette.text.primary, 0.42),
  textAlign: 'center',
  fontStyle: 'italic',
  letterSpacing: '-0.005em',
}));

// ============================================================================
// LANDING HERO - CSS String for injection
// ============================================================================

export const landingHeroStyles = `
/* Landing Header */
.landing-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 2rem;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: transparent;
  pointer-events: none;
}

.landing-header__logo,
.landing-header__cta {
  pointer-events: auto;
}

.landing-header__logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: inherit;
  text-decoration: none;
}

.landing-header__cta {
  display: inline-flex;
  align-items: center;
  padding: 0.6rem 1.25rem;
  background: linear-gradient(135deg, #B83B1D 0%, #D4694E 100%);
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.landing-header__cta:hover {
 
  box-shadow: 0 4px 12px rgba(184, 59, 29, 0.3);
}

/* Landing Hero - Centered Layout with Floating Cards */
.landing-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: auto;
  min-height: 100vh;
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  margin-top: 0;
  /* Clearance for the fixed announcement banner (40px) + header, plus
     breathing room above the headline. */
  padding-top: clamp(138px, 13.5vh, 174px);
  padding-bottom: clamp(10px, 1.8vh, 20px); /* Keep marquee off the hard bottom edge */
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background: inherit;
}

/* Gradient mesh behind hero content — centered */
.landing-hero::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
 
  width: 100%;
  height: 100%;
  background:
    radial-gradient(ellipse 80% 50% at 50% 40%, rgba(184, 59, 29, 0.05) 0%, transparent 50%),
    radial-gradient(ellipse 60% 40% at 40% 80%, rgba(184, 59, 29, 0.03) 0%, transparent 50%),
    radial-gradient(ellipse 40% 30% at 60% 20%, rgba(184, 59, 29, 0.02) 0%, transparent 50%);
  pointer-events: none;
  z-index: -2;
}

/* Subtle grid overlay behind hero — editorial feel */
.landing-hero::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(to right, rgba(30, 22, 18, 0.045) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(30, 22, 18, 0.045) 1px, transparent 1px);
  background-size: 56px 56px;
  -webkit-mask-image: radial-gradient(ellipse 70% 55% at 50% 40%, #000 0%, transparent 75%);
  mask-image: radial-gradient(ellipse 70% 55% at 50% 40%, #000 0%, transparent 75%);
  pointer-events: none;
  z-index: -1;
  /* Super-subtle life: the grid gently breathes (fades) and drifts a
     single cell over a long cycle, so patches of it quietly fade in and
     out rather than sitting perfectly static. */
  animation: heroGridBreathe 9s ease-in-out infinite, heroGridDrift 40s linear infinite;
}

@keyframes heroGridBreathe {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

@keyframes heroGridDrift {
  from { background-position: 0 0, 0 0; }
  to { background-position: 56px 56px, 56px 56px; }
}

@media (prefers-reduced-motion: reduce) {
  .landing-hero::after {
    animation: none;
  }
}

.landing-hero__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  justify-content: center;
  /* Explicit width + wide cap so the content box is not shrink-wrapped
     to the mockup (~988px). The headline needs the extra room to stay
     on a single line at the larger size. */
  width: 100%;
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
  /* Top padding above the headline. */
  padding: clamp(1rem, 2vh, 1.5rem) 2rem 0;
  margin-top: 0;
  /* Wider gap between the title and subhead so the upper hero breathes. */
  gap: clamp(1.1rem, 2.4vh, 1.6rem);
  position: relative;
  z-index: 2;
}

/* Hero content stagger animation */
@keyframes heroContentStagger {
  from {
    opacity: 0;
   
  }
  to {
    opacity: 1;
   
  }
}

.landing-hero__headline-wrapper {
  animation: heroContentStagger 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: 0.1s;
  opacity: 0;
}

.landing-hero__input-wrapper {
  animation: heroContentStagger 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: 0.25s;
  opacity: 0;
}

.landing-hero__trusted-inline {
  animation: heroContentStagger 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: 0.4s;
  opacity: 0;
}

/* Badge */
.landing-hero__badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 100px;
  border: 1px solid rgba(184, 59, 29, 0.18);
  background: rgba(184, 59, 29, 0.06);
  width: fit-content;
}

.landing-hero__badge-text {
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(184, 59, 29, 0.85);
}

/* Headline — wider max-width so the single-line desktop layout has
   room for the full "Your AI Consulting Partner. Inside PowerPoint."
   sentence. On smaller viewports the .landing-hero__headline-break span
   forces a wrap (see media query below). The gap controls the air
   between title and subtitle — bumped from 0.95rem so the subtitle
   reads as a deliberate beat below the headline, not a tight caption. */
.landing-hero__headline-wrapper {
  max-width: 1180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.landing-hero__headline {
  /* Headline uses the site sans (Inter), bold. Plain and punchy; the
     only color emphasis is the rust accent gradient on the first two
     words. Upper size cap kept modest so the single line still fits
     standard desktop widths. */
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(2.15rem, 3.6vw, 2.8rem);
  font-weight: 700;
  font-style: normal;
  line-height: 1.18;
  letter-spacing: -0.03em;
  color: inherit;
  text-align: center;
}

.landing-hero__headline-accent {
  background: linear-gradient(135deg, #B83B1D 0%, #D4694E 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  /* inline-block + horizontal padding fixes the italic clipping issue:
     background-clip: text masks the gradient to the typography metrics
     box, but italic capitals (especially A, T, V) overshoot the left
     edge. Without padding, the leading edge of the first letter is
     visibly cut off. inline-block ensures the gradient renders against
     the full glyph extent rather than a wrapping line box. */
  display: inline-block;
  padding-inline: 0.08em;
}

/* Restored serif accent (Playfair Display italic) — but now it only wraps
   "AI Consulting" rather than the full "Your AI Consulting Partner."
   phrase, so the editorial italic lands precisely on the two words that
   carry the proposition. The rest of the headline stays in black sans
   for institutional restraint. */
.landing-hero__headline-serif {
  font-family: 'Playfair Display', Georgia, 'Times New Roman', serif;
  font-style: italic;
  font-weight: 600;
  letter-spacing: -0.015em;
  /* Same italic-clip compensation as .headline-accent. When this class
     is combined with .headline-accent on the same span, the padding
     stacks harmlessly (0.16em total) — which actually helps even more
     for steep italics. */
  display: inline-block;
  padding-inline: 0.06em;
}

/* Break controller: inline whitespace on desktop (single-line
   layout), block on tablet/mobile so the headline wraps to two lines
   for readability on narrower screens. */
.landing-hero__headline-break {
  display: inline;
}
@media (max-width: 768px) {
  .landing-hero__headline-break {
    display: block;
    height: 0;
    overflow: hidden;
  }
}

/* Subtitle — desktop sits on a single line. Wider max-width and
   white-space: nowrap together guarantee it never wraps. The mobile
   override at @media (max-width: 600px) below restores normal wrapping
   with a tighter 32ch cap. */
.landing-hero__subtitle {
  font-size: 0.975rem;
  font-weight: 400;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.62);
  max-width: 760px;
  white-space: nowrap;
  text-align: center;
}


/* CTA Wrapper */
.landing-hero__cta-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 0.35rem;
  animation: heroContentStagger 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: 0.25s;
  opacity: 0;
  position: relative;
  z-index: 10;
}

/* Trusted By - Inline below input */
.landing-hero__trusted-inline {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
  margin-top: 3rem;
  max-width: 520px;
}

.landing-hero__trusted-label {
  font-size: 0.8rem;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.45);
  text-transform: none;
  letter-spacing: normal;
}

.landing-hero__trusted-logos {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  align-items: center;
  justify-items: start;
  gap: 1.5rem 2rem;
  width: 100%;
}

.landing-hero__trusted-logo {
  opacity: 0.45;
  filter: grayscale(100%);
  transition: opacity 0.3s ease, filter 0.3s ease;
  max-width: 100%;
  height: auto !important;
  max-height: 36px;
}

.landing-hero__trusted-logo:hover {
  opacity: 0.8;
  filter: grayscale(0%);
}


/* Trusted By Section - Below Hero */
.landing-trusted-by {
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  padding: 2rem 4rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  background: inherit;
}

.landing-trusted-by__label {
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.5;
}

.landing-trusted-by__marquee {
  width: 100%;
  max-width: 900px;
  overflow: hidden;
  mask-image: linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%);
  -webkit-mask-image: linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%);
}

.landing-trusted-by__track {
  display: flex;
  gap: 3rem;
  animation: trustedByScroll 25s linear infinite;
  width: max-content;
}

.landing-trusted-by__logo {
  flex-shrink: 0;
  opacity: 0.5;
  filter: grayscale(100%);
  transition: opacity 0.3s ease, filter 0.3s ease;
}

.landing-trusted-by__logo:hover {
  opacity: 0.85;
  filter: grayscale(0%);
}

@keyframes trustedByScroll {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}

/* Hero Logo Marquee */
/* Hero logo strip — original visual treatment restored. The only
   surviving change from the recent restyle is the track-wrapper's
   max-width: 1200px (so logos cycle within the page content width
   instead of edge-to-edge across 100vw). All section chrome (bg tint,
   borders, padding bump, animation speedup) reverted. */
/* Logo strip pushed down 50px so the animation above occupies more
   of the hero viewport. */
.landing-hero__logo-marquee {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: calc(0.65rem + 10px);
  padding: 0.35rem 0 0;
  margin-top: auto;
 
  width: 100%;
  position: relative;
  z-index: 2;
}

.landing-hero__logo-marquee-label {
  font-size: 0.8125rem;
  font-weight: 400;
  letter-spacing: normal;
  text-transform: none;
  color: rgba(0, 0, 0, 0.45);
}

/* Track wrapper — constrained to max page-content width (1200px).
   This is the ONLY piece kept from the recent restyle: the user
   wanted logos to stop running edge-to-edge across 100vw, while
   keeping the rest of the strip looking like the original. */
.landing-hero__logo-marquee-track-wrapper {
  width: 100%;
  max-width: 1200px;
  overflow: hidden;
  mask-image: linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%);
  -webkit-mask-image: linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%);
}

.landing-hero__logo-marquee-track {
  display: flex;
  align-items: center;
  gap: 5rem;
  animation: heroLogoScroll 150s linear infinite;
  width: max-content;
}

.landing-hero__logo-marquee-track:hover {
  animation-play-state: paused;
}

/* Desktop (≥1025px): the hero already has a 15s animation loop running
   in the slide mockup — adding a second slow-moving marquee underneath
   competes for attention. Lock the logo strip to a static, evenly-spaced
   row of the 7 client logos. The duplicate set (added in JSX for seamless
   marquee looping) is hidden; the track-wrapper's edge-fade mask is
   removed since fade is only meaningful when content is scrolling.
   Mobile/tablet keep the marquee — they have less competing motion. */
@media (min-width: 1025px) {
  .landing-hero__logo-marquee-track-wrapper {
    -webkit-mask-image: none;
            mask-image: none;
  }
  .landing-hero__logo-marquee-track {
    animation: none !important;
    width: 100%;
    max-width: 1100px;
    margin: 0 auto;
    justify-content: space-between;
    gap: 0;
  }
  .landing-hero__logo-marquee-track > .landing-hero__logo-marquee-slot:nth-child(n + 8) {
    display: none;
  }
  /* Smaller slot footprint for the static desktop row — the marquee
     baseline (132×36) feels heavy when the logos are stationary. A
     compact slot reads as a refined trust strip rather than a logo
     parade. */
  .landing-hero__logo-marquee-track > .landing-hero__logo-marquee-slot {
    width: 108px;
    height: 30px;
  }
  /* Fade the two extreme logos so the row tapers visually toward the
     edges. The middle 5 sit at the standard muted opacity (0.5);
     leftmost + rightmost drop to 0.28 so the eye reads the row as
     "softening into the page" rather than "hard stopping at columns 1
     and 7". Subtle but creates a more deliberate composition. */
  .landing-hero__logo-marquee-track > .landing-hero__logo-marquee-slot:nth-child(1) .landing-hero__logo-marquee-img,
  .landing-hero__logo-marquee-track > .landing-hero__logo-marquee-slot:nth-child(7) .landing-hero__logo-marquee-img {
    opacity: 0.28;
  }
}

/* Each logo lives in a fixed-size SLOT with the image set to object-fit
   contain. This forces every brand mark — wide ones (Oliver Wyman),
   narrow ones (LEK), tall ones (Deloitte) — to render inside the same
   visual footprint, fixing the "logos in different sizes" problem. */
.landing-hero__logo-marquee-slot {
  flex-shrink: 0;
  width: 132px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.landing-hero__logo-marquee-img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  opacity: 0.5;
  filter: grayscale(100%);
  transition: all 0.3s ease;
}

.landing-hero__logo-marquee-img:hover {
  opacity: 0.9;
  filter: grayscale(0%);
  transform: scale(1.06);
}

@keyframes heroLogoScroll {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}

/* PowerPoint Mockup — Centered below hero text */
/* Wrapper uncapped so the enlarged 1560px mockup isn't clipped. */
.landing-hero__pptx-mockup-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  /* Gap between the hero title and the animation. */
  margin-top: clamp(26px, 3.2vh, 40px);
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1600px;
  animation: heroContentStagger 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: 0.4s;
  opacity: 0;
}

/* Soft glow behind mockup */
.landing-hero__pptx-mockup-wrapper::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 85%;
  height: 65%;
  background: radial-gradient(ellipse, rgba(184, 59, 29, 0.08) 0%, transparent 65%);
  filter: blur(50px);
  pointer-events: none;
  z-index: 0;
}

/* PowerPoint Mockup Frame. Original 760px proportions, uniformly scaled
   up so the whole frame grows together without distorting the aspect
   ratio. Base 1.30x on mid-desktop (where the framing cards still need
   room beside it); bumped to 1.38x at >=1400px (~15% larger) where the
   viewport has the space. Visible: ~988px (mid) to ~1049px (wide). */
.landing-hero__pptx-mockup {
  width: 100%;
  max-width: 760px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow:
    0 24px 48px rgba(0, 0, 0, 0.12),
    0 8px 24px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(0, 0, 0, 0.06);
  background: #ffffff;
  position: relative;
  z-index: 1;
  transform: scale(1.30);
  transform-origin: top center;
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.3s ease;
  /* Compensates for the extra height the scale adds below the layout box
     (origin is top center): 500px * 0.38 ≈ 190px. */
  margin-bottom: 180px;
}

.landing-hero__pptx-mockup:hover {
  box-shadow:
    0 28px 56px rgba(0, 0, 0, 0.14),
    0 12px 32px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(0, 0, 0, 0.06);
}

/* Hero mockup animation starts immediately at T=0 (no pre-roll delay).
   The previous 2s delay + animation-fill-mode: backwards combination
   was removed per design direction — the loop now begins the moment
   the page renders, so the first beat (user message slide-up + Step 1
   activation) is the user's first impression rather than a static
   blank frame. The float-card 0% keyframes are still pinned to the
   idle-visible state (set in the heroCardLifecycleX keyframes below);
   that change is independently good because it eliminates a flicker
   at the iteration loop boundary that existed in the original frames,
   so it stays even though the original motivation (showing the cards
   during the pre-roll) no longer applies. */

/* Title Bar */
.landing-hero__pptx-titlebar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0.75rem;
  /* PowerPoint orange — completes the app-colour system (Excel green / Word
     blue / PowerPoint orange) across the three hero windows. */
  background: linear-gradient(180deg, #C9461F 0%, #B83B1D 100%);
  position: relative;
}

.landing-hero__pptx-titlebar-left {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.landing-hero__pptx-titlebar-dots {
  display: flex;
  gap: 6px;
}

.landing-hero__pptx-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

/* Monochrome translucent window controls — read cleanly on the orange bar. */
.landing-hero__pptx-dot--red { background: rgba(255, 255, 255, 0.55); }
.landing-hero__pptx-dot--yellow { background: rgba(255, 255, 255, 0.4); }
.landing-hero__pptx-dot--green { background: rgba(255, 255, 255, 0.55); }

.landing-hero__pptx-titlebar-quick {
  display: flex;
  gap: 3px;
}

.landing-hero__pptx-quick-btn {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.22);
}

.landing-hero__pptx-titlebar-center {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.landing-hero__pptx-titlebar-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.landing-hero__pptx-titlebar-text {
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 500;
  letter-spacing: 0.01em;
}

.landing-hero__pptx-titlebar-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.landing-hero__pptx-search-box {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  padding: 0.15rem 0.5rem;
  font-size: 0.5rem;
  color: rgba(255, 255, 255, 0.85);
}

.landing-hero__pptx-search-icon {
  font-size: 0.45rem;
}

.landing-hero__pptx-search-text {
  font-size: 0.5rem;
}

.landing-hero__pptx-share-btn {
  font-size: 0.5rem;
  font-weight: 600;
  color: #B83B1D;
  background: #fff;
  border-radius: 3px;
  padding: 0.15rem 0.5rem;
  letter-spacing: 0.01em;
}

/* Ribbon */
.landing-hero__pptx-ribbon {
  display: flex;
  gap: 0;
  padding: 0 0.35rem;
  background: #f3f3f3;
  border-bottom: none;
  overflow: hidden;
}

.landing-hero__pptx-ribbon-tab {
  font-size: 0.55rem;
  font-weight: 500;
  color: #666;
  padding: 0.3rem 0.45rem;
  cursor: default;
  border-bottom: 2px solid transparent;
  white-space: nowrap;
  flex-shrink: 0;
}

.landing-hero__pptx-ribbon-tab--home {
  font-weight: 600;
  color: #444;
}

.landing-hero__pptx-ribbon-tab--restack {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  color: #B83B1D;
}

.landing-hero__pptx-ribbon-tab--active {
  color: #B83B1D;
  border-bottom-color: #B83B1D;
  font-weight: 600;
}

/* Ribbon Toolbar */
.landing-hero__pptx-toolbar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0.75rem;
  background: #f8f8f8;
  border-bottom: 1px solid #e0e0e0;
}

.landing-hero__pptx-toolbar-group {
  display: flex;
  align-items: center;
  gap: 3px;
}

.landing-hero__pptx-toolbar-btn {
  width: 18px;
  height: 18px;
  border-radius: 3px;
  background: #e8e8e8;
}

.landing-hero__pptx-toolbar-btn--wide {
  width: 32px;
}

.landing-hero__pptx-toolbar-sep {
  width: 1px;
  height: 18px;
  background: #d8d8d8;
}

/* Body */
.landing-hero__pptx-body {
  display: grid;
  grid-template-columns: 52px 1fr 220px;
  /* 310px body height — 50% less grey margins around the slide.
     The uniform scale(1.15) on the mockup grows this proportionally. */
  height: 310px;
  overflow: hidden;
}

/* Slide Thumbnails */
.landing-hero__pptx-thumbs {
  background: #f0f0f0;
  border-right: 1px solid #e0e0e0;
  padding: 0.5rem 4px;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  overflow: hidden;
}

.landing-hero__pptx-thumb {
  display: flex;
  align-items: flex-start;
  gap: 2px;
}

.landing-hero__pptx-thumb-num {
  font-size: 0.4rem;
  color: #999;
  width: 10px;
  text-align: right;
  flex-shrink: 0;
  margin-top: 1px;
  line-height: 1;
}

.landing-hero__pptx-thumb-slide {
  flex: 1;
  aspect-ratio: 16 / 9;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 1px;
}

.landing-hero__pptx-thumb--active .landing-hero__pptx-thumb-slide {
  border-color: #B83B1D;
  border-width: 1.5px;
  animation: heroThumbPulse 15s ease infinite;
}

/* Slide Area — padding tightened from 0.55rem 0.85rem so the white
   slide grows to fill more of its gray PPT canvas. Because the slide
   below has aspect-ratio: 16/9 + width: 100%, reducing horizontal
   padding makes it wider, which auto-increases its height too. The
   net effect is a noticeably bigger slide (more readable content)
   with much less gray bleed top/bottom. */
.landing-hero__pptx-slide-area {
  padding: 0.25rem 0.4rem;
  background: #e8e8e8;
  display: flex;
  align-items: center;
  justify-content: center;
}

.landing-hero__pptx-slide {
  width: 100%;
  background: #ffffff;
  border-radius: 4px;
  /* Roomy internal padding so the slide reads like a real consulting
     deck with proper breathing room between elements. */
  padding: 0.7rem 1rem 0.55rem;
  display: flex;
  flex-direction: column;
  /* space-between pushes sources/methodology to the bottom, filling
     the 16:9 frame evenly instead of leaving dead white space. */
  justify-content: space-between;
  gap: 0.15rem;
  position: relative;
  /* Lock 16:9 aspect ratio so the slide never grows when content appears */
  aspect-ratio: 16 / 9;
  min-height: 0;
  overflow: hidden;
  animation: heroSlideGlow 15s ease infinite;
}

/* Branded top accent strip — a thin gradient bar at the top of the slide,
   exactly like a real consulting deck template. */
.landing-hero__pptx-slide::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #B83B1D 0%, #D4694E 50%, #D4A574 100%);
  border-radius: 4px 4px 0 0;
}

/* ---- Hero mockup animation keyframes (18s infinite loop) ---- */
/*
 * TIMELINE (absolute-positioned steps — no max-height, no layout shift):
 *   0–5%   User message fades in
 *   7–13%  Thinking dots
 *  14–23%  Step 1  Scanning sources        (fade-in 14%, hold, fade-out 22–23%)
 *  24–34%  Step 2  Extracting metrics       (fade-in 24%, hold, fade-out 33–34%)
 *  35–44%  Step 3  Validating data          (fade-in 35%, hold, fade-out 43–44%)
 *  45–50%  Step 4  Quality check            (fade-in 45%, hold, fade-out 49–50%)
 *  51–67%  Step 5  Building slide + slide   (fade-in 51%, hold, fade-out 66–67%)
 *  67–82%  Edit interaction — cursor, selection box, drag (user can edit directly)
 *  82–90%  Fade out
 *  90–100% Dead / reset gap
 *
 * Each step: opacity 0 → 1 (appear) → 1 (hold) → 0 (disappear).
 * 1% gap between steps ensures strictly sequential display.
 */

/* User prompt: slides UP from below + fades in over the first ~5% of
   the loop, mimicking the "send" gesture of a real chat composer. Stays
   visible through the full loop (chart drag, citations, sources arrival,
   everything) — in a real PowerPoint chat the prompt remains in the
   transcript permanently; fading it during the chart build was incorrect
   behaviour. The translateY 20px → 0 enter is what makes it feel sent
   (and not just appear out of nowhere). */
@keyframes heroUserMsg {
  0%   { opacity: 0; transform: translateY(20px); }
  3%   { opacity: 0; transform: translateY(20px); }
  7%   { opacity: 1; transform: translateY(0); }
  100% { opacity: 1; transform: translateY(0); }
}

@keyframes heroThinkingDots {
  /* Show briefly during the "AI thinking" beat (7–13%), then fade AND
     collapse vertical space so step 1's checkmark line slots directly
     below the user prompt — no empty grey band sitting between them. */
  0%, 6%  { opacity: 0; max-height: 0; padding-top: 0; padding-bottom: 0; }
  7%      { opacity: 1; max-height: 24px; padding-top: 0.18rem; padding-bottom: 0.18rem; }
  13%     { opacity: 1; max-height: 24px; padding-top: 0.18rem; padding-bottom: 0.18rem; }
  14%     { opacity: 0; max-height: 0; padding-top: 0; padding-bottom: 0; }
  100%    { opacity: 0; max-height: 0; padding-top: 0; padding-bottom: 0; }
}

/* ---- Step keyframes — sequential trail model -------------------------
   Each step has 4 paired animations:
     heroStepCardN    outer card opacity + lift (lifecycle)
     heroStepActiveN  spinner + active label (visible only in active win)
     heroStepDoneN    check + done label (visible from active end+1 -> 91)
     heroStepDetailN  detail panel max-height + opacity
   Loop reset: 92–100% all fade out so steps re-emerge cleanly next loop.
*/

/* Step 1 — Scanning sources (active 14–22%, done from 23%) */
@keyframes heroStepCard1 {
  0%, 13%  { opacity: 0; }
  14%      { opacity: 1; }
  99%      { opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}
@keyframes heroStepActive1 {
  0%, 13%  { opacity: 0; }
  14%      { opacity: 1; }
  22%      { opacity: 1; }
  23%      { opacity: 0; }
  100%     { opacity: 0; }
}
@keyframes heroStepDone1 {
  0%, 22%  { opacity: 0; }
  23%      { opacity: 1; }
  99%      { opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}
@keyframes heroStepDetail1 {
  0%, 13%  { opacity: 0; max-height: 0; }
  14%      { opacity: 1; max-height: 80px; }
  22%      { opacity: 1; max-height: 80px; }
  23%      { opacity: 0; max-height: 0; }
  100%     { opacity: 0; max-height: 0; }
}
@keyframes heroSourceItem1 {
  0%, 14.5% { opacity: 0; }
  16%       { opacity: 1; }
  22%       { opacity: 1; }
  23%       { opacity: 0; }
  100%      { opacity: 0; }
}
@keyframes heroSourceItem2 {
  0%, 16.5% { opacity: 0; }
  18%       { opacity: 1; }
  22%       { opacity: 1; }
  23%       { opacity: 0; }
  100%      { opacity: 0; }
}
@keyframes heroSourceItem3 {
  0%, 18%   { opacity: 0; }
  19.5%     { opacity: 1; }
  22%       { opacity: 1; }
  23%       { opacity: 0; }
  100%      { opacity: 0; }
}
@keyframes heroSourceItem4 {
  0%, 19.5% { opacity: 0; }
  21%       { opacity: 1; }
  22%       { opacity: 1; }
  23%       { opacity: 0; }
  100%      { opacity: 0; }
}
@keyframes heroSourceItem5 {
  0%, 20.5% { opacity: 0; }
  22%       { opacity: 1; }
  22.5%     { opacity: 1; }
  23%       { opacity: 0; }
  100%      { opacity: 0; }
}

/* Step 2 — Extracting metrics (active 24–33%, done from 34%) */
@keyframes heroStepCard2 {
  0%, 23%  { opacity: 0; }
  24%      { opacity: 1; }
  99%      { opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}
@keyframes heroStepActive2 {
  0%, 23%  { opacity: 0; }
  24%      { opacity: 1; }
  33%      { opacity: 1; }
  34%      { opacity: 0; }
  100%     { opacity: 0; }
}
@keyframes heroStepDone2 {
  0%, 33%  { opacity: 0; }
  34%      { opacity: 1; }
  99%      { opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}
@keyframes heroStepDetail2 {
  0%, 23%  { opacity: 0; max-height: 0; }
  24%      { opacity: 1; max-height: 80px; }
  33%      { opacity: 1; max-height: 80px; }
  34%      { opacity: 0; max-height: 0; }
  100%     { opacity: 0; max-height: 0; }
}
@keyframes heroAnalysisBar1 {
  0%, 25% { width: 0%; }
  29%     { width: 100%; }
  33%     { width: 100%; }
  34%     { width: 0%; }
  100%    { width: 0%; }
}
@keyframes heroAnalysisBar2 {
  0%, 27% { width: 0%; }
  31%     { width: 100%; }
  33%     { width: 100%; }
  34%     { width: 0%; }
  100%    { width: 0%; }
}
@keyframes heroAnalysisBar3 {
  0%, 29% { width: 0%; }
  33%     { width: 100%; }
  33.5%   { width: 100%; }
  34%     { width: 0%; }
  100%    { width: 0%; }
}

/* Extracted value chips — pop in right as each bar fills */
@keyframes heroAnalysisValue1 {
  0%, 28.5% { opacity: 0; transform: translateY(2px) scale(0.9); }
  30%       { opacity: 1; transform: translateY(0) scale(1); }
  33%       { opacity: 1; }
  34%       { opacity: 0; }
  100%      { opacity: 0; }
}
@keyframes heroAnalysisValue2 {
  0%, 30.5% { opacity: 0; transform: translateY(2px) scale(0.9); }
  32%       { opacity: 1; transform: translateY(0) scale(1); }
  33%       { opacity: 1; }
  34%       { opacity: 0; }
  100%      { opacity: 0; }
}
@keyframes heroAnalysisValue3 {
  0%, 32.5% { opacity: 0; transform: translateY(2px) scale(0.9); }
  33.5%     { opacity: 1; transform: translateY(0) scale(1); }
  34%       { opacity: 0; }
  100%      { opacity: 0; }
}

/* Step 3 — Validated data (merged: absorbs old extract+validate+quality
   into one beat). Active 35-52% (extended from 35-43% to fill the gap
   left by removing slot 4). Done from 53%. The crossref items time the
   ✓ rows so they appear in sequence and stay visible through 52%. */
@keyframes heroStepCard3 {
  0%, 34%  { opacity: 0; }
  35%      { opacity: 1; }
  99%      { opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}
@keyframes heroStepActive3 {
  0%, 34%  { opacity: 0; }
  35%      { opacity: 1; }
  52%      { opacity: 1; }
  53%      { opacity: 0; }
  100%     { opacity: 0; }
}
@keyframes heroStepDone3 {
  0%, 52%  { opacity: 0; }
  53%      { opacity: 1; }
  99%      { opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}
@keyframes heroStepDetail3 {
  0%, 34%  { opacity: 0; max-height: 0; }
  35%      { opacity: 1; max-height: 80px; }
  52%      { opacity: 1; max-height: 80px; }
  53%      { opacity: 0; max-height: 0; }
  100%     { opacity: 0; max-height: 0; }
}
@keyframes heroCrossrefItem1 {
  0%, 35.5% { opacity: 0; }
  37%       { opacity: 1; }
  52%       { opacity: 1; }
  53%       { opacity: 0; }
  100%      { opacity: 0; }
}
@keyframes heroCrossrefItem2 {
  0%, 38%   { opacity: 0; }
  39.5%     { opacity: 1; }
  52%       { opacity: 1; }
  53%       { opacity: 0; }
  100%      { opacity: 0; }
}
@keyframes heroCrossrefItem3 {
  0%, 41%   { opacity: 0; }
  42.5%     { opacity: 1; }
  52%       { opacity: 1; }
  53%       { opacity: 0; }
  100%      { opacity: 0; }
}
@keyframes heroCrossrefItem4 {
  0%, 44%   { opacity: 0; }
  45.5%     { opacity: 1; }
  52%       { opacity: 1; }
  53%       { opacity: 0; }
  100%      { opacity: 0; }
}

/* Step 4 — Quality check (active 67–73%, done from 74%).
   Runs after Building slide finishes, before chart drag at 75%. */
@keyframes heroStepCard4 {
  0%, 66%  { opacity: 0; }
  67%      { opacity: 1; }
  99%      { opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}
@keyframes heroStepActive4 {
  0%, 66%  { opacity: 0; }
  67%      { opacity: 1; }
  73%      { opacity: 1; }
  74%      { opacity: 0; }
  100%     { opacity: 0; }
}
@keyframes heroStepDone4 {
  0%, 73%  { opacity: 0; }
  74%      { opacity: 1; }
  99%      { opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}
@keyframes heroStepDetail4 {
  0%, 66%  { opacity: 0; max-height: 0; }
  67%      { opacity: 1; max-height: 80px; }
  73%      { opacity: 1; max-height: 80px; }
  74%      { opacity: 0; max-height: 0; }
  100%     { opacity: 0; max-height: 0; }
}
@keyframes heroQualityItem1 {
  0%, 67.5% { opacity: 0; }
  68.5%     { opacity: 1; }
  73%       { opacity: 1; }
  74%       { opacity: 0; }
  100%      { opacity: 0; }
}
@keyframes heroQualityItem2 {
  0%, 69%   { opacity: 0; }
  70%       { opacity: 1; }
  73%       { opacity: 1; }
  74%       { opacity: 0; }
  100%      { opacity: 0; }
}
@keyframes heroQualityItem3 {
  0%, 70.5% { opacity: 0; }
  71.5%     { opacity: 1; }
  73%       { opacity: 1; }
  74%       { opacity: 0; }
  100%      { opacity: 0; }
}

/* Step 5 — Building slide (active 53–64%, done from 65%).
   Finishes 2% before Step 4 quality check starts at 67%. */
@keyframes heroStepCard5 {
  0%, 52%  { opacity: 0; }
  53%      { opacity: 1; }
  99%      { opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}
@keyframes heroStepActive5 {
  0%, 52%  { opacity: 0; }
  53%      { opacity: 1; }
  64%      { opacity: 1; }
  65%      { opacity: 0; }
  100%     { opacity: 0; }
}
@keyframes heroStepDone5 {
  0%, 64%  { opacity: 0; }
  65%      { opacity: 1; }
  99%      { opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}
@keyframes heroStepDetail5 {
  0%, 52%  { opacity: 0; max-height: 0; }
  53%      { opacity: 1; max-height: 80px; }
  64%      { opacity: 1; max-height: 80px; }
  65%      { opacity: 0; max-height: 0; }
  100%     { opacity: 0; max-height: 0; }
}

/* Step 6 — Chart move (active 73–87% during cursor drag, done from 88%).
   This step replaces the old standalone "edit hint" so the chart move
   reads as the natural final beat of the same sequential trail. No
   detail panel — header line only. */
@keyframes heroStepCard6 {
  0%, 72%  { opacity: 0; }
  73%      { opacity: 1; }
  99%      { opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}
@keyframes heroStepActive6 {
  0%, 72%  { opacity: 0; }
  73%      { opacity: 1; }
  87%      { opacity: 1; }
  88%      { opacity: 0; }
  100%     { opacity: 0; }
}
@keyframes heroStepDone6 {
  0%, 87%  { opacity: 0; }
  88%      { opacity: 1; }
  99%      { opacity: 1; }
  100%     { opacity: 0; }
}
@keyframes heroBuildItem1 {
  0%, 51.5% { opacity: 0; }
  53%       { opacity: 1; }
  66%       { opacity: 1; }
  67%       { opacity: 0; }
  100%      { opacity: 0; }
}
@keyframes heroBuildItem2 {
  0%, 55%   { opacity: 0; }
  57%       { opacity: 1; }
  66%       { opacity: 1; }
  67%       { opacity: 0; }
  100%      { opacity: 0; }
}
@keyframes heroBuildItem3 {
  0%, 59%   { opacity: 0; }
  61%       { opacity: 1; }
  66%       { opacity: 1; }
  67%       { opacity: 0; }
  100%      { opacity: 0; }
}
@keyframes heroBuildItem4 {
  0%, 51%   { opacity: 0; }
  53%       { opacity: 0.6; }
  64%       { opacity: 0.6; }
  65%       { opacity: 0; }
  100%      { opacity: 0; }
}
/* heroBuildDone keyframe and the "Done ✓" badge it animated were removed
   when Step 5 became part of the sequential trail — Step 5 now ends with
   "Slide built · 4 citations" as a checkmark line, no separate badge. */

/* ---- Slide content — all elements now start during Step 5 (Building
   slide, active 53-66%) so the editor and sidebar are perfectly in sync:

   • 53-66% Step 5 active in chat (Building slide...)
     → 53-55% title + subtitle + divider appear first
     → 55-66% bullets, insight, chart, citations fill in

   • 67-99% Step 5 done in chat (Slide built)
     → 67-70% chart drag finalizes the layout (the polish touch)
*/

@keyframes heroSlideTitle {
  0%, 52%  { clip-path: inset(0 100% 0 0); opacity: 0; }
  53%      { clip-path: inset(0 100% 0 0); opacity: 1; }
  55%      { clip-path: inset(0 0% 0 0); opacity: 1; }
  99%      { clip-path: inset(0 0% 0 0); opacity: 1; }
  99.9%    { opacity: 0; clip-path: inset(0 0% 0 0); }
  100%     { opacity: 0; clip-path: inset(0 100% 0 0); }
}

@keyframes heroSlideSubtitle {
  0%, 54%  { clip-path: inset(0 100% 0 0); opacity: 0; }
  55%      { clip-path: inset(0 100% 0 0); opacity: 1; }
  57%      { clip-path: inset(0 0% 0 0); opacity: 1; }
  99%      { clip-path: inset(0 0% 0 0); opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; clip-path: inset(0 100% 0 0); }
}

@keyframes heroSlideDivider {
  0%, 56%  { transform: scaleX(0); }
  57%      { transform: scaleX(1); }
  99%      { transform: scaleX(1); opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; transform: scaleX(0); }
}

/* Bullets + insight dim to 0.55 during edit phase (78–88%) so chart is the focus */
/* Bullets + insight now start at 57% (after title/subtitle/divider) */
/* Exhibit chrome (the numbered head rule, and the chart's category baseline)
   must not sit on the slide before the agent has drawn anything: the loop
   opens on an EMPTY slide, so any always-visible furniture reads as a
   half-built page. Each frame therefore arrives on the same beat as the
   content it belongs to.

   Exhibit 1's chrome tracks the sizing rows, including their dim during the
   drag beat at 79%. Exhibit 2's chrome tracks the bars, which stay at full
   strength because that exhibit is the object being dragged. */
@keyframes heroSlideExhibit1Frame {
  0%, 56.5% { opacity: 0; }
  57.5%     { opacity: 1; }
  76%       { opacity: 1; }
  79%       { opacity: 0.55; }
  99%       { opacity: 0.55; }
  99.9%     { opacity: 0; }
  100%      { opacity: 0; }
}

@keyframes heroSlideExhibit2Frame {
  0%, 60.5% { opacity: 0; }
  61.5%     { opacity: 1; }
  99%       { opacity: 1; }
  99.9%     { opacity: 0; }
  100%      { opacity: 0; }
}

.pptx-slide__exhibit-frame--1 {
  animation: heroSlideExhibit1Frame 15s cubic-bezier(0.16, 1, 0.3, 1) infinite;
}

.pptx-slide__exhibit-frame--2 {
  animation: heroSlideExhibit2Frame 15s cubic-bezier(0.16, 1, 0.3, 1) infinite;
}

/* Funnel bars wipe out from the label edge on the same beat the sizing rows
   fade in (57-62% of the loop), so Exhibit 1 builds as one gesture. They then
   hold at full width for the rest of the cycle. */
@keyframes heroFunnelGrow {
  0%, 57.5% { transform: scaleX(0); }
  63%       { transform: scaleX(1); }
  99.9%     { transform: scaleX(1); }
  100%      { transform: scaleX(0); }
}

@keyframes heroSlideBullet1 {
  0%, 57%  { opacity: 0; }
  58%      { opacity: 1; }
  76%      { opacity: 1; }
  79%      { opacity: 0.55; }
  99%      { opacity: 0.55; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}

@keyframes heroSlideBullet2 {
  0%, 58.5% { opacity: 0; }
  59.5%     { opacity: 1; }
  76%       { opacity: 1; }
  79%       { opacity: 0.55; }
  99%       { opacity: 0.55; }
  99.9%     { opacity: 0; }
  100%      { opacity: 0; }
}

@keyframes heroSlideBullet3 {
  0%, 60%  { opacity: 0; }
  61%      { opacity: 1; }
  76%      { opacity: 1; }
  79%      { opacity: 0.55; }
  99%      { opacity: 0.55; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}

@keyframes heroSlideInsight {
  0%, 61%  { opacity: 0; }
  62%      { opacity: 1; }
  76%      { opacity: 1; }
  79%      { opacity: 0.55; }
  99%      { opacity: 0.55; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}

/* Chart title, bars, sources — shifted ~2% later to follow bullets */
@keyframes heroSlideChartTitle {
  0%, 62%  { opacity: 0; }
  63%      { opacity: 1; }
  99%      { opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}

@keyframes heroChartBar1 {
  0%, 62%  { transform: scaleY(0); }
  63.5%    { transform: scaleY(1); }
  99%      { transform: scaleY(1); opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; transform: scaleY(0); }
}

@keyframes heroChartBar2 {
  0%, 63%   { transform: scaleY(0); }
  64.5%     { transform: scaleY(1); }
  99%       { transform: scaleY(1); opacity: 1; }
  99.9%     { opacity: 0; }
  100%      { opacity: 0; transform: scaleY(0); }
}

@keyframes heroChartBar3 {
  0%, 64%  { transform: scaleY(0); }
  65.5%    { transform: scaleY(1); }
  99%      { transform: scaleY(1); opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; transform: scaleY(0); }
}

@keyframes heroChartBar4 {
  0%, 65%   { transform: scaleY(0); }
  66.5%     { transform: scaleY(1); }
  99%       { transform: scaleY(1); opacity: 1; }
  99.9%     { opacity: 0; }
  100%      { opacity: 0; transform: scaleY(0); }
}

/* CAGR appears just as the final bar completes — a clean "done" cue */
@keyframes heroChartCagr {
  0%, 66%  { opacity: 0; transform: scale(0.85); }
  67.5%    { opacity: 1; transform: scale(1); }
  99%      { opacity: 1; transform: scale(1); }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}

@keyframes heroSlideSource {
  0%, 64%  { opacity: 0; }
  65%      { opacity: 1; }
  99%      { opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}

@keyframes heroSlideMethodology {
  0%, 65%  { opacity: 0; }
  66%      { opacity: 1; }
  99%      { opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}

@keyframes heroSlidePageNum {
  0%, 65%  { opacity: 0; }
  66%      { opacity: 1; }
  99%      { opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}

@keyframes heroDotBounce {
  0%, 100% { transform: translateY(0); opacity: 0.7; }
  50%      { transform: translateY(-3px); opacity: 1; }
}

/* Thumbnail pulse during build phase */
@keyframes heroThumbPulse {
  0%, 50%  { box-shadow: 0 0 0 1px rgba(184, 59, 29, 0.15); }
  51%      { box-shadow: 0 0 6px 2px rgba(184, 59, 29, 0.25); }
  58%      { box-shadow: 0 0 3px 1px rgba(184, 59, 29, 0.35); }
  66%      { box-shadow: 0 0 6px 2px rgba(184, 59, 29, 0.25); }
  67%      { box-shadow: 0 0 0 1px rgba(184, 59, 29, 0.15); }
  100%     { box-shadow: 0 0 0 1px rgba(184, 59, 29, 0.15); }
}

/* Reading-pass highlights for slides 2, 7, 8 during the "Read deck
   context" beat (chat trail step 2 active 24-33%). Highlights light
   up in stagger (matching bar-fill order in the step 2 detail panel)
   and PERSIST through the entire validation phase (until 52%). This
   gives the user enough time to look from the chat panel to the
   filmstrip and clearly see the connection — earlier the highlights
   only lasted 1-1.5s during the active reading beat, which felt
   delayed because the user perceived them after looking down. They
   fade together at 53% as "Building slide..." takes over. */
@keyframes heroThumbReading2 {
  0%, 23%  { background: #fff; border-color: #ddd; }
  24%      { background: rgba(184, 59, 29, 0.18); border-color: #B83B1D; }
  52%      { background: rgba(184, 59, 29, 0.18); border-color: #B83B1D; }
  53%      { background: #fff; border-color: #ddd; }
  100%     { background: #fff; border-color: #ddd; }
}
@keyframes heroThumbReading7 {
  0%, 25%  { background: #fff; border-color: #ddd; }
  26%      { background: rgba(184, 59, 29, 0.18); border-color: #B83B1D; }
  52%      { background: rgba(184, 59, 29, 0.18); border-color: #B83B1D; }
  53%      { background: #fff; border-color: #ddd; }
  100%     { background: #fff; border-color: #ddd; }
}
@keyframes heroThumbReading8 {
  0%, 27%  { background: #fff; border-color: #ddd; }
  28%      { background: rgba(184, 59, 29, 0.18); border-color: #B83B1D; }
  52%      { background: rgba(184, 59, 29, 0.18); border-color: #B83B1D; }
  53%      { background: #fff; border-color: #ddd; }
  100%     { background: #fff; border-color: #ddd; }
}
.landing-hero__pptx-thumb--reading-2 .landing-hero__pptx-thumb-slide {
  animation: heroThumbReading2 15s ease infinite;
}
.landing-hero__pptx-thumb--reading-7 .landing-hero__pptx-thumb-slide {
  animation: heroThumbReading7 15s ease infinite;
}
.landing-hero__pptx-thumb--reading-8 .landing-hero__pptx-thumb-slide {
  animation: heroThumbReading8 15s ease infinite;
}
/* Slide number on the left also lights up rust during the read window
   to amplify the visual cue. Without this the small "2" / "7" / "8"
   beside the thumbs stays gray and disappears against the sidebar. */
@keyframes heroThumbNumReading2 {
  0%, 23%  { color: #999; font-weight: 400; }
  24%      { color: #B83B1D; font-weight: 600; }
  52%      { color: #B83B1D; font-weight: 600; }
  53%      { color: #999; font-weight: 400; }
  100%     { color: #999; font-weight: 400; }
}
@keyframes heroThumbNumReading7 {
  0%, 25%  { color: #999; font-weight: 400; }
  26%      { color: #B83B1D; font-weight: 600; }
  52%      { color: #B83B1D; font-weight: 600; }
  53%      { color: #999; font-weight: 400; }
  100%     { color: #999; font-weight: 400; }
}
@keyframes heroThumbNumReading8 {
  0%, 27%  { color: #999; font-weight: 400; }
  28%      { color: #B83B1D; font-weight: 600; }
  52%      { color: #B83B1D; font-weight: 600; }
  53%      { color: #999; font-weight: 400; }
  100%     { color: #999; font-weight: 400; }
}
.landing-hero__pptx-thumb--reading-2 .landing-hero__pptx-thumb-num {
  animation: heroThumbNumReading2 15s ease infinite;
}
.landing-hero__pptx-thumb--reading-7 .landing-hero__pptx-thumb-num {
  animation: heroThumbNumReading7 15s ease infinite;
}
.landing-hero__pptx-thumb--reading-8 .landing-hero__pptx-thumb-num {
  animation: heroThumbNumReading8 15s ease infinite;
}

/* Shimmer sweep for progress bars */
@keyframes heroShimmer {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
}

/* Slide canvas glow during build — with a bright "done beat" peak at 67–69% */
/* Slide glow now starts at 53% to match Step 5 activation */
@keyframes heroSlideGlow {
  0%, 52%  { box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1); transform: scale(1); }
  53%      { box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1), 0 0 12px rgba(184, 59, 29, 0.08); transform: scale(1); }
  60%      { box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1), 0 0 20px rgba(184, 59, 29, 0.12); transform: scale(1); }
  66%      { box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1), 0 0 24px rgba(184, 59, 29, 0.18); transform: scale(1); }
  /* "Done" beat — bright pulse + micro-bounce right as CAGR pops */
  68%      { box-shadow: 0 2px 10px rgba(0, 0, 0, 0.14), 0 0 40px rgba(184, 59, 29, 0.32); transform: scale(1.012); }
  70%      { box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12), 0 0 28px rgba(184, 59, 29, 0.22); transform: scale(1); }
  99%      { box-shadow: 0 1px 6px rgba(0, 0, 0, 0.11), 0 0 16px rgba(184, 59, 29, 0.10); transform: scale(1); }
  100%     { box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1); transform: scale(1); }
}

/* ---- Edit interaction keyframes (67–92% hold phase) ---- */
/* Story: chart is a real PPT object — user grabs it and drags it to a better position on the slide */

/* Cursor appears at 76% (after quality check done at 74%),
   reaches chart at 80%, drags through 86%, then holds. */
@keyframes heroEditCursor {
  0%, 75%   { opacity: 0; top: 86%; left: 82%; }
  76%       { opacity: 1; top: 86%; left: 82%; }
  80%       { opacity: 1; top: 74%; left: 48%; }
  81%       { opacity: 1; top: 75%; left: 48%; }
  86%       { opacity: 1; top: 79%; left: 53.5%; }
  99%       { opacity: 1; top: 79%; left: 53.5%; }
  100%      { opacity: 0; top: 86%; left: 82%; }
}

/* Selection box appears when cursor grabs chart at 80% */
@keyframes heroEditSelect {
  0%, 80%  { opacity: 0; }
  81%      { opacity: 1; }
  99%      { opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}

/* Chart drag at 81-86% — after cursor grabs at 80%. */
@keyframes heroChartMove {
  0%, 80%  { transform: translate(0, 0); }
  81%      { transform: translate(1px, 0.5px); }
  86%      { transform: translate(10px, 6px); }
  99%      { transform: translate(10px, 6px); }
  100%     { transform: translate(0, 0); }
}

/* heroEditHint keyframe + .pptx-sidebar__edit-hint rule were removed
   when the chart-move beat became Step 6 of the sequential trail. The
   "Moved chart to balance layout" line lives inside the trail now, so
   no separate sidebar element / animation is needed. */

/* ---- Slide content classes ---- */

/* Action title — supports up to 2 lines (McKinsey-style "answer-first"
   headlines often wrap to two rows). Slightly smaller font so the
   wrapped title doesn't crowd the bullets/chart below. text-wrap:pretty
   produces a cleaner break on the second line. */
.pptx-slide__title {
  font-size: 0.72rem;
  font-weight: 700;
  color: #1a1a2e;
  letter-spacing: -0.012em;
  line-height: 1.2;
  margin-bottom: 0.1rem;
  text-wrap: pretty;
  animation: heroSlideTitle 15s cubic-bezier(0.16, 1, 0.3, 1) infinite;
}

.pptx-slide__subtitle {
  font-size: 0.46rem;
  font-weight: 400;
  color: #777;
  letter-spacing: 0.02em;
  line-height: 1.2;
  animation: heroSlideSubtitle 15s cubic-bezier(0.16, 1, 0.3, 1) infinite;
  margin-top: 0;
  margin-bottom: 0.15rem;
}

.pptx-slide__divider {
  height: 2px;
  background: linear-gradient(90deg, #B83B1D 0%, #D4A574 100%);
  border-radius: 1px;
  transform-origin: left;
  margin-bottom: 0.2rem;
  animation: heroSlideDivider 15s cubic-bezier(0.16, 1, 0.3, 1) infinite;
}

/* ---- Two-exhibit body -----------------------------------------------------
   The slide's evidence sits in two numbered exhibits side by side, the way a
   market-sizing page is actually built: the sizing funnel on the left, the
   projection on the right. Both columns share one head treatment (exhibit
   number + unit-labelled title) and one baseline grid, so the page reads as
   a single composed layout rather than stacked widgets. */
.pptx-slide__exhibits {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;
  align-items: stretch;
  margin-top: 0.32rem;
  margin-bottom: 0.1rem;
  /* Take the slide's spare height so the plot area grows into it rather
     than leaving a band of dead white between the exhibits and the
     insight strip. */
  flex: 1;
  min-height: 0;
}

.pptx-slide__exhibit {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

/* Exhibit head: a hairline-ruled caption row. The number is the accent, the
   title carries the unit, and any right-aligned metric (the CAGR chip) sits
   on the same rule. */
.pptx-slide__exhibit-head {
  display: flex;
  align-items: baseline;
  gap: 0.3rem;
  padding-bottom: 0.16rem;
  margin-bottom: 0.34rem;
  border-bottom: 1px solid rgba(46, 20, 10, 0.13);
}

.pptx-slide__exhibit-num {
  font-size: 0.38rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #B83B1D;
  flex-shrink: 0;
}

.pptx-slide__exhibit-title {
  font-size: 0.44rem;
  font-weight: 600;
  color: #333;
  letter-spacing: -0.005em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Footnote under an exhibit: the assumption a reviewer would ask for. */
.pptx-slide__exhibit-foot {
  margin-top: auto;
  padding-top: 0.3rem;
  font-size: 0.35rem;
  line-height: 1.35;
  color: #9a9a9a;
}

/* ---- Exhibit 1: market-sizing funnel -------------------------------------
   Label, proportional bar, figure. Bar widths are the true ratios of the
   figures, so the funnel narrows honestly instead of decoratively. */
.pptx-slide__funnel {
  display: flex;
  flex-direction: column;
  gap: 0.34rem;
}

.pptx-slide__funnel-row {
  display: grid;
  grid-template-columns: 1.55rem 1fr auto;
  align-items: center;
  gap: 0.3rem;
}

.pptx-slide__funnel-label {
  font-size: 0.42rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #B83B1D;
}

.pptx-slide__funnel-track {
  display: block;
  height: 0.46rem;
  background: rgba(46, 20, 10, 0.055);
  border-radius: 1px;
  overflow: hidden;
}

.pptx-slide__funnel-bar {
  display: block;
  height: 100%;
  border-radius: 1px;
  transform-origin: left;
  animation: heroFunnelGrow 15s cubic-bezier(0.16, 1, 0.3, 1) infinite;
}

/* 4.2 / 1.8 / 0.34 as shares of the 4.2 total addressable. */
.pptx-slide__funnel-bar--tam {
  width: 100%;
  background: linear-gradient(90deg, #B83B1D 0%, #C4513A 100%);
}

.pptx-slide__funnel-bar--sam {
  width: 42.9%;
  background: linear-gradient(90deg, #C9603F 0%, #D4794E 100%);
  animation-delay: 0.15s;
}

.pptx-slide__funnel-bar--som {
  width: 8.1%;
  min-width: 3px;
  background: linear-gradient(90deg, #DD8A63 0%, #E8A77E 100%);
  animation-delay: 0.3s;
}

.pptx-slide__funnel-value {
  font-size: 0.46rem;
  font-weight: 700;
  color: #2E140A;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* Reveal timings are shared with the old bullet rows, so the funnel rows and
   the exhibit footnotes still cascade in on the same beats of the 15s loop. */
.pptx-slide__bullet--1 { animation: heroSlideBullet1 15s cubic-bezier(0.16, 1, 0.3, 1) infinite; }
.pptx-slide__bullet--2 { animation: heroSlideBullet2 15s cubic-bezier(0.16, 1, 0.3, 1) infinite; }
.pptx-slide__bullet--3 { animation: heroSlideBullet3 15s cubic-bezier(0.16, 1, 0.3, 1) infinite; }

/* Key Insight callout */
.pptx-slide__insight {
  font-size: 0.44rem;
  color: #555;
  padding: 0.28rem 0.45rem;
  border-left: 2px solid #B83B1D;
  background: rgba(184, 59, 29, 0.06);
  border-radius: 0 4px 4px 0;
  line-height: 1.35;
  margin-top: 0.36rem;
  margin-bottom: 0.15rem;
  animation: heroSlideInsight 15s ease infinite;
}

.pptx-slide__insight-label {
  font-weight: 700;
  color: #B83B1D;
}

/* Chart wrapper with title and CAGR — the whole thing is a draggable PPT object,
   selection box wraps around it and it translates during the edit phase. */
.pptx-slide__chart-wrapper {
  position: relative;
  animation: heroChartMove 15s cubic-bezier(0.22, 1, 0.36, 1) infinite;
}

.pptx-slide__chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* Bumped from 0.2rem so the bar-value labels (which sit at top: -12px
     above each bar — including the tallest rightmost bar that's directly
     under the CAGR text) clear the header without colliding. */
  margin-bottom: 0.55rem;
}

.pptx-slide__chart-title {
  font-size: 0.42rem;
  font-weight: 700;
  color: #888;
  animation: heroSlideChartTitle 15s ease infinite;
}

.pptx-slide__chart-cagr {
  margin-left: auto;
  font-size: 0.4rem;
  font-weight: 700;
  color: #B83B1D;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  flex-shrink: 0;
  animation: heroChartCagr 15s ease infinite;
}

.pptx-slide__chart {
  display: flex;
  align-items: flex-end;
  /* Half the old gutter: the chart now lives in a half-width column, so
     wide gutters would leave the bars looking like stray sticks. */
  gap: 0.3rem;
  flex: 1;
  min-height: 62px;
  padding-bottom: 14px;
  /* Headroom for the value label above the tallest bar, which would
     otherwise collide with the CAGR figure on the exhibit head rule. */
  margin-top: 13px;
  /* Category baseline, the way a plotted exhibit sits on its axis. */
  box-shadow: inset 0 -14px 0 -13px rgba(46, 20, 10, 0.22);
}

.pptx-slide__chart-bar {
  flex: 1;
  border-radius: 2px 2px 0 0;
  transform-origin: bottom;
  position: relative;
}

.pptx-slide__chart-bar--1 {
  height: 40%;
  background: linear-gradient(180deg, #B83B1D 0%, #D4694E 100%);
  animation: heroChartBar1 15s cubic-bezier(0.16, 1, 0.3, 1) infinite;
}

.pptx-slide__chart-bar--2 {
  height: 58%;
  background: linear-gradient(180deg, #C4513A 0%, #D97B60 100%);
  animation: heroChartBar2 15s cubic-bezier(0.16, 1, 0.3, 1) infinite;
}

.pptx-slide__chart-bar--3 {
  height: 75%;
  background: linear-gradient(180deg, #D4694E 0%, #E0937E 100%);
  animation: heroChartBar3 15s cubic-bezier(0.16, 1, 0.3, 1) infinite;
}

.pptx-slide__chart-bar--4 {
  height: 100%;
  background: linear-gradient(180deg, #E07D5A 0%, #EAAA8E 100%);
  animation: heroChartBar4 15s cubic-bezier(0.16, 1, 0.3, 1) infinite;
}

/* Value + axis labels sit dead-centre over their bar. The translateX(-50%)
   pairing with left: 50% is what actually centres them; without it every
   label rendered ~4px right of its bar and the chart read as misaligned. */
.pptx-slide__bar-value {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.38rem;
  font-weight: 700;
  color: #666;
  white-space: nowrap;
}

.pptx-slide__chart-label {
  position: absolute;
  bottom: -14px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.5rem;
  color: #888;
  white-space: nowrap;
}

/* ─── Inline citation superscripts ─────────────────────────────────────
   Renders the small "1", "2", "3", "4" callouts after each figure.
   Sized at ~55% of body to read as a true superscript (not a number),
   coloured with the brand accent so it's scannable as a citation marker.
   Inherits opacity from its parent line so it fades in/out with the
   bullet that contains it (no separate keyframes needed). */
.pptx-slide__cite {
  font-size: 55%;
  font-weight: 700;
  color: #B83B1D;
  margin-left: 1px;
  vertical-align: super;
  line-height: 0;
  /* Faux-link affordance — small underline that hints "tap me" */
  text-decoration: underline;
  text-decoration-thickness: 0.5px;
  text-underline-offset: 1.5px;
  text-decoration-color: rgba(184, 59, 29, 0.4);
  cursor: help;
}

/* Highlight the in-line figure that carries the citation in the insight
   row — keeps the eye drawn to the cited stat. */
.pptx-slide__insight-figure {
  font-weight: 700;
  color: #1e1612;
}

/* ─── Numbered source footer ───────────────────────────────────────────
   Single horizontal row of citations — keeps the slide at its native
   16:9 proportion (vs. the prior 4-row stack which stretched the body).
   Each item reads as a small superscript-numbered faux-link, separated
   by a thin centred dot. Wraps gracefully on very narrow viewports. */
.pptx-slide__sources {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.18rem 0.55rem;
  margin-top: 0.45rem;
  padding-top: 0.3rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  animation: heroSlideSource 15s ease infinite;
}

.pptx-slide__source-row {
  display: inline-flex;
  align-items: baseline;
  gap: 0.18rem;
  font-size: 0.42rem;
  color: #777;
  line-height: 1.3;
  position: relative;
}

/* Thin dot separator between adjacent sources (skip the first) */
.pptx-slide__source-row + .pptx-slide__source-row::before {
  content: '·';
  color: rgba(0, 0, 0, 0.18);
  margin-right: 0.4rem;
  margin-left: -0.18rem;
  font-weight: 700;
}

.pptx-slide__source-num {
  font-size: 0.36rem;
  font-weight: 700;
  color: #B83B1D;
  flex-shrink: 0;
  /* Superscript-ish positioning matches in-body citation markers */
  vertical-align: super;
  line-height: 1;
}

.pptx-slide__source-link {
  color: #555;
  text-decoration: underline;
  text-decoration-thickness: 0.5px;
  text-underline-offset: 1.5px;
  text-decoration-color: rgba(0, 0, 0, 0.18);
  transition: color 0.15s ease, text-decoration-color 0.15s ease;
  white-space: nowrap;
}

.pptx-slide__source-link:hover {
  color: #B83B1D;
  text-decoration-color: #B83B1D;
}

.pptx-slide__methodology {
  font-size: 0.4rem;
  color: #999;
  margin-top: 0.25rem;
  font-style: italic;
  animation: heroSlideMethodology 15s ease infinite;
}

.pptx-slide__page-num {
  position: absolute;
  bottom: 0.5rem;
  right: 0.75rem;
  font-size: 0.42rem;
  font-weight: 700;
  color: #ccc;
  animation: heroSlidePageNum 15s ease infinite;
}

/* ---- Edit interaction elements ---- */

/* Mouse cursor — positioned absolutely inside slide */
.pptx-slide__edit-cursor {
  position: absolute;
  z-index: 10;
  pointer-events: none;
  opacity: 0;
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.25));
  animation: heroEditCursor 15s cubic-bezier(0.22, 1, 0.36, 1) infinite;
}

/* Selection box — wraps the whole chart-wrapper. Sits INSIDE the wrapper so
   it auto-follows the heroChartMove translate. 4px outset on all sides so the
   user reads "I grabbed the chart object" — not "I'm editing one bar". */
.pptx-slide__edit-select {
  position: absolute;
  inset: -4px;
  border: 1.5px solid #2B7CE9;
  border-radius: 2px;
  opacity: 0;
  pointer-events: none;
  z-index: 5;
  animation: heroEditSelect 15s cubic-bezier(0.22, 1, 0.36, 1) infinite;
}

/* Blue drag handles (8 points) */
.pptx-slide__edit-handle {
  position: absolute;
  width: 5px;
  height: 5px;
  background: #2B7CE9;
  border: 1px solid #fff;
  border-radius: 1px;
}


/* Sidebar */
.landing-hero__pptx-sidebar {
  border-left: 1px solid #e0e0e0;
  background: #fafafa;
  padding: 0.6rem 0.7rem 0.7rem;
  display: flex;
  flex-direction: column;
  /* Tight gap so the first step card reads as a direct continuation of
     the user prompt — no empty grey band between them. The thinking
     dots beat slots into the same gap when active. */
  gap: 0.3rem;
  /* Let the sidebar scroll within the locked body height so the growing
     step trail never gets clipped — scrollbar hidden for clean UI. */
  overflow-y: auto;
  scrollbar-width: none;          /* Firefox */
  -ms-overflow-style: none;       /* IE/Edge */
  /* Decorative mockup: wheel events must scroll the PAGE, not this pane.
     Nothing inside is interactive and no JS scrolls it, so pointer-events
     none lets the user's scroll pass straight through. */
  pointer-events: none;
}
.landing-hero__pptx-sidebar::-webkit-scrollbar {
  display: none;                  /* Chrome/Safari */
}

.landing-hero__pptx-sidebar-header {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.55rem;
  font-weight: 400;
  color: #555;
  padding-bottom: 0.4rem;
  border-bottom: 1px solid #e8e8e8;
  letter-spacing: 0.02em;
}

/* ---- Sidebar chat animation classes ----
   Unified typography: every line in the chat sidebar uses the SAME
   font size (0.55rem) and weight (400). Color is the only signal —
   rust for active step, neutral grey for done labels and detail rows.
   This keeps the chat reading as a calm, consistent feed rather than
   a noisy mix of weights and sizes. */

.pptx-sidebar__msg {
  font-size: 0.55rem;
  line-height: 1.5;
  padding: 0.45rem 0.55rem;
  border-radius: 6px;
  font-weight: 400;
}

/* User prompt: indented from the left so it reads as "sent by you"
   (iMessage/WhatsApp pattern) instead of tethering to the left edge.
   Also carries the slide-from-below entrance via heroUserMsg keyframe
   — initial opacity:0 + translateY(20px) ensures the message starts
   off-screen below before the animation pulls it into place. */
.pptx-sidebar__msg--user {
  align-self: flex-end;
  max-width: 92%;
  margin-left: auto;
  background: #f0f0f0;
  color: #333;
  font-weight: 400;
  border-bottom-right-radius: 2px;
  opacity: 0;
  transform: translateY(20px);
  animation: heroUserMsg 15s ease infinite;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

/* Attachment row inside the user prompt — demonstrates the document
   upload capability. Two chips (PDF + XLS) sit on a single line BELOW
   the prompt text, signalling to landing-page visitors that the tool
   accepts source docs as input alongside the natural-language prompt.
   nowrap + min-width: 0 keeps the row from breaking; chip filenames
   ellipsize if the sidebar narrows below the natural fit. */
.pptx-sidebar__msg-attachments {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.3rem;
  min-width: 0;
  width: 100%;
}

.pptx-sidebar__attachment {
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  padding: 0.18rem 0.32rem 0.18rem 0.22rem;
  font-size: 0.5rem;
  font-weight: 400;
  color: #555;
  line-height: 1;
  /* flex: 0 1 auto + min-width: 0 lets each chip shrink with ellipsis
     if the row gets tight, while the natural width keeps both on a
     single line at the default sidebar width. */
  flex: 0 1 auto;
  min-width: 0;
}

.pptx-sidebar__attachment-badge {
  font-size: 0.4rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  padding: 0.14rem 0.22rem;
  border-radius: 2px;
  color: #ffffff;
  flex-shrink: 0;
  line-height: 1;
}

/* Adobe-style red for PDF, Microsoft Excel green for XLSX — instantly
   recognisable file-type colour coding without legend or label. */
.pptx-sidebar__attachment--pdf .pptx-sidebar__attachment-badge {
  background: #B83B1D;
}

.pptx-sidebar__attachment--xls .pptx-sidebar__attachment-badge {
  background: #1f7a3a;
}

.pptx-sidebar__attachment-name {
  font-size: 0.5rem;
  color: #444;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.pptx-sidebar__msg-text {
  font-size: 0.55rem;
  line-height: 1.5;
  color: #333;
}

.pptx-sidebar__msg--ai {
  background: rgba(184, 59, 29, 0.06);
  color: #444;
}

.pptx-sidebar__thinking {
  display: flex;
  gap: 4px;
  /* Compact vertical so even while visible the dots don't push step 1
     too far from the user prompt. After they fade, the keyframe also
     collapses max-height so the layout closes the gap entirely and
     step 1's checkmark reads as the immediate AI response. */
  padding: 0.18rem 0.5rem;
  overflow: hidden;
  animation: heroThinkingDots 15s ease infinite;
}

.pptx-sidebar__dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #B83B1D;
  animation: heroDotBounce 0.6s ease-in-out infinite;
}

.pptx-sidebar__dot:nth-child(2) {
  animation-delay: 0.15s;
}

.pptx-sidebar__dot:nth-child(3) {
  animation-delay: 0.3s;
}

/* ---- Steps container: flex column, GROWS as steps appear ---------------
   The earlier version used absolute positioning so all steps stacked at
   top-left and swapped via opacity. That hid the user's progress as soon
   as each step finished. The new model lets each step's "card" appear in
   sequence, morph from active (with detail panel expanded) to done
   (single checkmark line), and STAY visible through the rest of the loop.
   So by the end, the sidebar reads as a top-to-bottom progress trail:
       ✓ Scanned 847 sources
       ✓ Extracted key metrics
       ✓ Validated data · 92% confidence
       ✓ Quality check passed
       ✓ Slide built · 4 citations
       Moved chart to balance layout
*/
.pptx-sidebar__steps-container {
  display: flex;
  flex-direction: column;
  gap: 0.22rem;
  /* Lock to a FIXED height so the PowerPoint frame stops "dancing" as
     steps come and go. Calibrated to the busiest moment of the loop
     (5 done lines + 1 active card with detail panel expanded + gaps,
     across all 6 step cards).

        5 done lines × ~21px      = 105
        1 active card (~95px)     =  95
        5 gaps × ~3.5px           =  18
        rounding / line-height    =   7
        ───────────────────────────
        ≈ 225px */
  height: 225px;
  flex-shrink: 0;
  overflow: hidden;
}

/* ---- Each step card ---------------------------------------------------
   Hidden by default; per-step keyframes (heroStepCardN) toggle visibility
   and the active/done state classes inside it. */
.pptx-sidebar__step {
  background: rgba(184, 59, 29, 0.05);
  border-left: 2px solid #B83B1D;
  border-radius: 4px;
  /* Compact padding so all 6 cards fit comfortably in the locked
     steps-container height (240px) — even at the busiest moment of
     the loop (5 done lines + 1 active card with detail expanded). */
  padding: 0.3rem 0.5rem 0.3rem 0.45rem;
  display: flex;
  flex-direction: column;
  gap: 0.22rem;
  opacity: 0;
 
}

/* The header row: icon + label, inline-grid stacks let us swap "active"
   (spinner + present-tense label) and "done" (check + past-tense label)
   without layout shift. */
.pptx-sidebar__step-row {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.55rem;
  font-weight: 400;
  color: #B83B1D;
  letter-spacing: 0.01em;
  min-width: 0;
}

.pptx-sidebar__step-icon-stack {
  display: inline-grid;
  grid-template: 1fr / 1fr;
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}

.pptx-sidebar__step-icon-stack > * {
  grid-area: 1 / 1;
}

.pptx-sidebar__step-icon {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #B83B1D;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  font-size: 0.5rem;
  font-weight: 700;
  opacity: 0;
}

/* Active variant uses a small filled inner dot to read as "working" */
.pptx-sidebar__step-icon--spinner {
  background: rgba(184, 59, 29, 0.18);
}
.pptx-sidebar__step-icon--spinner::before {
  content: '';
  width: 5px;
  height: 5px;
  background: #B83B1D;
  border-radius: 50%;
  animation: heroStepPulse 0.9s ease-in-out infinite;
}

/* Done variant — solid filled circle with white checkmark */
.pptx-sidebar__step-icon--check {
  font-size: 0.55rem;
}

.pptx-sidebar__step-label-stack {
  display: inline-grid;
  grid-template: 1fr / 1fr;
  flex: 1;
  min-width: 0;
}

.pptx-sidebar__step-label-stack > * {
  grid-area: 1 / 1;
}

.pptx-sidebar__step-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0;
}

/* Done label reads in lighter weight + neutral colour to look "settled" */
.pptx-sidebar__step-label--done {
  font-weight: 400;
  color: #555;
}

/* Detail panel — collapsible. Per-step keyframes drive max-height + opacity
   so the panel expands during active and collapses when done, leaving just
   the row line behind. */
.pptx-sidebar__step-detail {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  overflow: hidden;
  opacity: 0;
  max-height: 0;
}

/* Pulse for the active spinner inner dot */
@keyframes heroStepPulse {
  0%, 100% { transform: scale(1); opacity: 0.85; }
  50%      { transform: scale(1.25); opacity: 1; }
}

/* ---- Per-step animations ----------------------------------------------
   Each step has 4 paired animations:
     heroStepCardN    — outer card opacity + lift (lifecycle: appears at
                        active start, stays through 91%, fades 92–100%)
     heroStepActiveN  — spinner icon + active label (visible in active win)
     heroStepDoneN    — check icon + done label (visible from active end+1
                        through 91%, fades 92–100%)
     heroStepDetailN  — detail panel max-height + opacity (open in active,
                        collapsed elsewhere)
*/
.pptx-sidebar__step--1 { animation: heroStepCard1 15s ease infinite; }
.pptx-sidebar__step--2 { animation: heroStepCard2 15s ease infinite; }
.pptx-sidebar__step--3 { animation: heroStepCard3 15s ease infinite; }
.pptx-sidebar__step--4 { animation: heroStepCard4 15s ease infinite; }
.pptx-sidebar__step--5 { animation: heroStepCard5 15s ease infinite; }
.pptx-sidebar__step--6 { animation: heroStepCard6 15s ease infinite; }

.pptx-sidebar__step--1 .pptx-sidebar__step-icon--spinner,
.pptx-sidebar__step--1 .pptx-sidebar__step-label--active { animation: heroStepActive1 15s ease infinite; }
.pptx-sidebar__step--2 .pptx-sidebar__step-icon--spinner,
.pptx-sidebar__step--2 .pptx-sidebar__step-label--active { animation: heroStepActive2 15s ease infinite; }
.pptx-sidebar__step--3 .pptx-sidebar__step-icon--spinner,
.pptx-sidebar__step--3 .pptx-sidebar__step-label--active { animation: heroStepActive3 15s ease infinite; }
.pptx-sidebar__step--4 .pptx-sidebar__step-icon--spinner,
.pptx-sidebar__step--4 .pptx-sidebar__step-label--active { animation: heroStepActive4 15s ease infinite; }
.pptx-sidebar__step--5 .pptx-sidebar__step-icon--spinner,
.pptx-sidebar__step--5 .pptx-sidebar__step-label--active { animation: heroStepActive5 15s ease infinite; }
.pptx-sidebar__step--6 .pptx-sidebar__step-icon--spinner,
.pptx-sidebar__step--6 .pptx-sidebar__step-label--active { animation: heroStepActive6 15s ease infinite; }

.pptx-sidebar__step--1 .pptx-sidebar__step-icon--check,
.pptx-sidebar__step--1 .pptx-sidebar__step-label--done { animation: heroStepDone1 15s ease infinite; }
.pptx-sidebar__step--2 .pptx-sidebar__step-icon--check,
.pptx-sidebar__step--2 .pptx-sidebar__step-label--done { animation: heroStepDone2 15s ease infinite; }
.pptx-sidebar__step--3 .pptx-sidebar__step-icon--check,
.pptx-sidebar__step--3 .pptx-sidebar__step-label--done { animation: heroStepDone3 15s ease infinite; }
.pptx-sidebar__step--4 .pptx-sidebar__step-icon--check,
.pptx-sidebar__step--4 .pptx-sidebar__step-label--done { animation: heroStepDone4 15s ease infinite; }
.pptx-sidebar__step--5 .pptx-sidebar__step-icon--check,
.pptx-sidebar__step--5 .pptx-sidebar__step-label--done { animation: heroStepDone5 15s ease infinite; }
.pptx-sidebar__step--6 .pptx-sidebar__step-icon--check,
.pptx-sidebar__step--6 .pptx-sidebar__step-label--done { animation: heroStepDone6 15s ease infinite; }

.pptx-sidebar__step--1 .pptx-sidebar__step-detail { animation: heroStepDetail1 15s ease infinite; }
.pptx-sidebar__step--2 .pptx-sidebar__step-detail { animation: heroStepDetail2 15s ease infinite; }
.pptx-sidebar__step--3 .pptx-sidebar__step-detail { animation: heroStepDetail3 15s ease infinite; }
.pptx-sidebar__step--4 .pptx-sidebar__step-detail { animation: heroStepDetail4 15s ease infinite; }
.pptx-sidebar__step--5 .pptx-sidebar__step-detail { animation: heroStepDetail5 15s ease infinite; }
/* Step 6 has no detail panel — header row only. */

.pptx-sidebar__source-item {
  font-size: 0.55rem;
  font-weight: 400;
  color: #555;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  opacity: 0;
}

.pptx-sidebar__source-tag {
  font-size: 0.55rem;
  font-weight: 400;
  color: #B83B1D;
  background: rgba(184, 59, 29, 0.08);
  padding: 0.05rem 0.35rem;
  border-radius: 3px;
  flex-shrink: 0;
  letter-spacing: 0.01em;
}

.pptx-sidebar__source-item--1 { animation: heroSourceItem1 15s ease infinite; }
.pptx-sidebar__source-item--2 { animation: heroSourceItem2 15s ease infinite; }
.pptx-sidebar__source-item--3 { animation: heroSourceItem3 15s ease infinite; }
.pptx-sidebar__source-item--4 { animation: heroSourceItem4 15s ease infinite; }
.pptx-sidebar__source-item--5 { animation: heroSourceItem5 15s ease infinite; }

/* Step 2: Extracting Metrics — interior items (animation lives on step--2) */
.pptx-sidebar__analysis-row {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.pptx-sidebar__analysis-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.4rem;
}

.pptx-sidebar__analysis-label {
  font-size: 0.55rem;
  font-weight: 400;
  color: #555;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
}

.pptx-sidebar__analysis-value {
  font-size: 0.55rem;
  font-weight: 400;
  color: #B83B1D;
  font-variant-numeric: tabular-nums;
  opacity: 0;
  flex-shrink: 0;
}

.pptx-sidebar__analysis-value--1 { animation: heroAnalysisValue1 15s ease infinite; }
.pptx-sidebar__analysis-value--2 { animation: heroAnalysisValue2 15s ease infinite; }
.pptx-sidebar__analysis-value--3 { animation: heroAnalysisValue3 15s ease infinite; }

.pptx-sidebar__analysis-bar {
  height: 4px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 2px;
  overflow: hidden;
  position: relative;
}

.pptx-sidebar__analysis-fill {
  height: 100%;
  border-radius: 2px;
  background: linear-gradient(90deg, #B83B1D, #D4A574);
}

/* Shimmer on progress bars */
.pptx-sidebar__analysis-bar::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 40%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  animation: heroShimmer 1.5s ease-in-out infinite;
}

.pptx-sidebar__analysis-bar--1 .pptx-sidebar__analysis-fill {
  animation: heroAnalysisBar1 15s ease infinite;
}
.pptx-sidebar__analysis-bar--2 .pptx-sidebar__analysis-fill {
  animation: heroAnalysisBar2 15s ease infinite;
}
.pptx-sidebar__analysis-bar--3 .pptx-sidebar__analysis-fill {
  animation: heroAnalysisBar3 15s ease infinite;
}

/* Step 3: Validating Data — interior items (animation lives on step--3) */
.pptx-sidebar__crossref-item {
  font-size: 0.55rem;
  font-weight: 400;
  color: #555;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  opacity: 0;
}

.pptx-sidebar__crossref-check {
  color: #B83B1D;
  font-size: 0.55rem;
  font-weight: 400;
}

.pptx-sidebar__crossref-item--1 { animation: heroCrossrefItem1 15s ease infinite; }
.pptx-sidebar__crossref-item--2 { animation: heroCrossrefItem2 15s ease infinite; }
.pptx-sidebar__crossref-item--3 { animation: heroCrossrefItem3 15s ease infinite; }
.pptx-sidebar__crossref-item--4 { animation: heroCrossrefItem4 15s ease infinite; }

/* Step 4: Quality Check — interior items (animation lives on step--4) */
.pptx-sidebar__quality-item {
  font-size: 0.55rem;
  font-weight: 400;
  color: #555;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  opacity: 0;
}

.pptx-sidebar__quality-item--1 { animation: heroQualityItem1 15s ease infinite; }
.pptx-sidebar__quality-item--2 { animation: heroQualityItem2 15s ease infinite; }
.pptx-sidebar__quality-item--3 { animation: heroQualityItem3 15s ease infinite; }

/* Step 5: Building Slide — interior items (animation lives on step--5) */
.pptx-sidebar__build-item {
  font-size: 0.55rem;
  font-weight: 400;
  color: #555;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  opacity: 0;
}

.pptx-sidebar__build-item--1 { animation: heroBuildItem1 15s ease infinite; }
.pptx-sidebar__build-item--2 { animation: heroBuildItem2 15s ease infinite; }
.pptx-sidebar__build-item--3 { animation: heroBuildItem3 15s ease infinite; }
.pptx-sidebar__build-item--4 { animation: heroBuildItem4 15s ease infinite; }

.pptx-sidebar__build-spinner {
  color: #B83B1D;
  font-size: 0.55rem;
  font-weight: 400;
  animation: heroDotBounce 0.8s ease-in-out infinite;
}

/* ─── Persistent progress trail ──────────────────────────────────────────
   Compact one-liners that fade in when each step completes and stay visible
   until the animation loop resets. This gives viewers a running "audit
   trail" of completed phases so nothing feels like it vanished. */

.pptx-sidebar__trail {
  display: flex;
  flex-direction: column;
  gap: 0.18rem;
  margin-top: 0.35rem;
}

.pptx-sidebar__trail-line {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.44rem;
  color: #555;
  padding: 0.15rem 0.35rem;
  border-radius: 3px;
  background: rgba(184, 59, 29, 0.04);
  opacity: 0;
 
}

.pptx-sidebar__trail-check {
  color: #16a34a;
  font-size: 0.5rem;
  font-weight: 700;
  flex-shrink: 0;
  line-height: 1;
}

.pptx-sidebar__trail-text {
  font-weight: 500;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Trail line 1: appears when step 1 (sources) finishes at 23% */
.pptx-sidebar__trail-line--1 {
  animation: heroTrailLine1 15s ease infinite;
}

/* Trail line 2: appears when step 2 (analysis) finishes at 34% */
.pptx-sidebar__trail-line--2 {
  animation: heroTrailLine2 15s ease infinite;
}

/* Trail line 3: appears when step 3 (crossref) finishes at 44% */
.pptx-sidebar__trail-line--3 {
  animation: heroTrailLine3 15s ease infinite;
}

/* Trail line 4: appears when step 4 (quality) finishes at 53% */
.pptx-sidebar__trail-line--4 {
  animation: heroTrailLine4 15s ease infinite;
}

/* Trail line 5: appears when step 5 (building) finishes at 67% */
.pptx-sidebar__trail-line--5 {
  animation: heroTrailLine5 15s ease infinite;
}

@keyframes heroTrailLine1 {
  0%, 22%  { opacity: 0; }
  24%      { opacity: 1; }
  99%      { opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}

@keyframes heroTrailLine2 {
  0%, 33%  { opacity: 0; }
  35%      { opacity: 1; }
  99%      { opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}

@keyframes heroTrailLine3 {
  0%, 43%  { opacity: 0; }
  45%      { opacity: 1; }
  99%      { opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}

@keyframes heroTrailLine4 {
  0%, 52%  { opacity: 0; }
  54%      { opacity: 1; }
  99%      { opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}

@keyframes heroTrailLine5 {
  0%, 66%  { opacity: 0; }
  68%      { opacity: 1; }
  99%      { opacity: 1; }
  99.9%    { opacity: 0; }
  100%     { opacity: 0; }
}

/* Status Bar */
.landing-hero__pptx-statusbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0.75rem;
  background: #f0f0f0;
  font-size: 0.55rem;
  color: #777;
}

.landing-hero__pptx-statusbar-right {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.landing-hero__pptx-statusbar-views {
  display: flex;
  gap: 2px;
}

.landing-hero__pptx-statusbar-view {
  width: 12px;
  height: 9px;
  border-radius: 1.5px;
  background: rgba(0, 0, 0, 0.12);
}

.landing-hero__pptx-statusbar-view--active {
  background: rgba(0, 0, 0, 0.3);
}

.landing-hero__pptx-zoom-track {
  width: 44px;
  height: 2px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 1px;
  position: relative;
}

.landing-hero__pptx-zoom-fill {
  position: absolute;
  left: 0;
  top: 0;
  width: 60%;
  height: 100%;
  background: rgba(0, 0, 0, 0.25);
  border-radius: 1px;
}

.landing-hero__pptx-zoom-label {
  font-size: 0.5rem;
  color: #999;
}

/* ---- Floating Showcase Elements ---- */

/* Decorative SVG */
.landing-hero__deco-svg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: -3;

}

@media (prefers-reduced-motion: reduce) {
  .landing-hero__deco-flow {
    display: none;
  }
}

/* ---- Per-card lifecycle keyframes ----------------------------------------
   Replace heroCardFadeIn for the four linkable artefact cards. Each
   keyframe runs on a 15s infinite loop synchronized with the chat trail
   so the pulse fires at the exact moment its step ticks off. The
   keyframe handles fade-in, steady idle state, the pulse window
   (rust glow + scale boost + opacity bump), then settle back to idle.

   Idle state matches the previous heroCardFadeIn end frame
   (opacity 0.45, scale 0.86) so cards still recede into the periphery
   when not pulsing. */

/* Each card now STAYS highlighted for the FULL active window of its
   corresponding chat-trail step (rather than briefly pulsing at the
   moment the step ticks off). This gives the user enough time to read
   the chat label, look toward the artefact in the periphery, and
   internalise the connection before the next step takes over.

   Active windows in the 15s loop:
     Step 1 "Scanning 847 sources..."  →  ~0-22%   → Sources card
     Step 3 "Validating data..."       →  ~35-52%  → Data Triangulation
     Step 5 "Building slide..."        →  ~54-65%  → Revenue Projection
     Step 4 "Running quality check..." →  ~67-73%  → Quality Check */

/* All four float-card lifecycles now start at 0% in the IDLE-VISIBLE
   state (opacity 0.45, scale 0.86, neutral border) instead of the old
   "hidden, fade-in-from-below" entrance. Two reasons:

   1. With the 2s pre-roll delay + animation-fill-mode: backwards, the
      0% keyframe is what renders during the delay. Idle-visible at 0%
      means cards appear in the periphery from page load, signalling
      "these will be referenced during the demo" before the chat trail
      starts.

   2. Eliminates the old loop flicker. Previously each card transitioned
      from 100% (idle visible) → 0% (hidden) instantly between iterations,
      creating a jolt every 15s. Now 0% matches 100% so the loop is
      seamless. */

@keyframes heroCardLifecycleSources {
  0%        { opacity: 0.45; transform: translateY(0) scale(0.86); border-color: rgba(0,0,0,0.05); box-shadow: 0 1px 2px rgba(0,0,0,0.018), 0 4px 14px rgba(0,0,0,0.022); }
  4%        { opacity: 0.95; transform: translateY(0) scale(0.92); border-color: rgba(184,59,29,0.5); box-shadow: 0 0 0 5px rgba(184,59,29,0.10), 0 1px 2px rgba(0,0,0,0.025), 0 8px 22px rgba(184,59,29,0.18); }
  22%       { opacity: 0.95; transform: translateY(0) scale(0.92); border-color: rgba(184,59,29,0.5); box-shadow: 0 0 0 5px rgba(184,59,29,0.10), 0 1px 2px rgba(0,0,0,0.025), 0 8px 22px rgba(184,59,29,0.18); }
  26%       { opacity: 0.55; transform: translateY(0) scale(0.88); border-color: rgba(184,59,29,0.18); box-shadow: 0 0 0 10px rgba(184,59,29,0), 0 1px 2px rgba(0,0,0,0.02), 0 5px 16px rgba(0,0,0,0.04); }
  30%, 100% { opacity: 0.45; transform: translateY(0) scale(0.86); border-color: rgba(0,0,0,0.05); box-shadow: 0 1px 2px rgba(0,0,0,0.018), 0 4px 14px rgba(0,0,0,0.022); }
}

/* Class --quality wraps the "Data Triangulation" JSX. Highlighted
   throughout Step 3 "Validating data..." active window (~35-52%). */
@keyframes heroCardLifecycleQuality {
  0%        { opacity: 0.45; transform: translateY(0) scale(0.86); border-color: rgba(0,0,0,0.05); box-shadow: 0 1px 2px rgba(0,0,0,0.018), 0 4px 14px rgba(0,0,0,0.022); }
  10%       { opacity: 0.45; transform: translateY(0) scale(0.86); border-color: rgba(0,0,0,0.05); box-shadow: 0 1px 2px rgba(0,0,0,0.018), 0 4px 14px rgba(0,0,0,0.022); }
  34%       { opacity: 0.45; transform: translateY(0) scale(0.86); border-color: rgba(0,0,0,0.05); box-shadow: 0 1px 2px rgba(0,0,0,0.018), 0 4px 14px rgba(0,0,0,0.022); }
  37%       { opacity: 0.95; transform: translateY(0) scale(0.92); border-color: rgba(184,59,29,0.5); box-shadow: 0 0 0 5px rgba(184,59,29,0.10), 0 1px 2px rgba(0,0,0,0.025), 0 8px 22px rgba(184,59,29,0.18); }
  52%       { opacity: 0.95; transform: translateY(0) scale(0.92); border-color: rgba(184,59,29,0.5); box-shadow: 0 0 0 5px rgba(184,59,29,0.10), 0 1px 2px rgba(0,0,0,0.025), 0 8px 22px rgba(184,59,29,0.18); }
  55%       { opacity: 0.55; transform: translateY(0) scale(0.88); border-color: rgba(184,59,29,0.18); box-shadow: 0 0 0 10px rgba(184,59,29,0), 0 1px 2px rgba(0,0,0,0.02), 0 5px 16px rgba(0,0,0,0.04); }
  58%, 100% { opacity: 0.45; transform: translateY(0) scale(0.86); border-color: rgba(0,0,0,0.05); box-shadow: 0 1px 2px rgba(0,0,0,0.018), 0 4px 14px rgba(0,0,0,0.022); }
}

/* Revenue Projection card. Highlighted throughout Step 5 "Building
   slide..." active window (~54-65%). */
@keyframes heroCardLifecycleRevenue {
  0%        { opacity: 0.45; transform: translateY(0) scale(0.86); border-color: rgba(0,0,0,0.05); box-shadow: 0 1px 2px rgba(0,0,0,0.018), 0 4px 14px rgba(0,0,0,0.022); }
  8%        { opacity: 0.45; transform: translateY(0) scale(0.86); border-color: rgba(0,0,0,0.05); box-shadow: 0 1px 2px rgba(0,0,0,0.018), 0 4px 14px rgba(0,0,0,0.022); }
  53%       { opacity: 0.45; transform: translateY(0) scale(0.86); border-color: rgba(0,0,0,0.05); box-shadow: 0 1px 2px rgba(0,0,0,0.018), 0 4px 14px rgba(0,0,0,0.022); }
  56%       { opacity: 0.95; transform: translateY(0) scale(0.92); border-color: rgba(184,59,29,0.5); box-shadow: 0 0 0 5px rgba(184,59,29,0.10), 0 1px 2px rgba(0,0,0,0.025), 0 8px 22px rgba(184,59,29,0.18); }
  65%       { opacity: 0.95; transform: translateY(0) scale(0.92); border-color: rgba(184,59,29,0.5); box-shadow: 0 0 0 5px rgba(184,59,29,0.10), 0 1px 2px rgba(0,0,0,0.025), 0 8px 22px rgba(184,59,29,0.18); }
  68%       { opacity: 0.55; transform: translateY(0) scale(0.88); border-color: rgba(184,59,29,0.18); box-shadow: 0 0 0 10px rgba(184,59,29,0), 0 1px 2px rgba(0,0,0,0.02), 0 5px 16px rgba(0,0,0,0.04); }
  72%, 100% { opacity: 0.45; transform: translateY(0) scale(0.86); border-color: rgba(0,0,0,0.05); box-shadow: 0 1px 2px rgba(0,0,0,0.018), 0 4px 14px rgba(0,0,0,0.022); }
}

/* Class --triangulation wraps the "Quality Check" JSX. Highlighted
   throughout Step 4 "Running quality check..." active window (~67-73%). */
@keyframes heroCardLifecycleTriangulation {
  0%        { opacity: 0.45; transform: translateY(0) scale(0.86); border-color: rgba(0,0,0,0.05); box-shadow: 0 1px 2px rgba(0,0,0,0.018), 0 4px 14px rgba(0,0,0,0.022); }
  12%       { opacity: 0.45; transform: translateY(0) scale(0.86); border-color: rgba(0,0,0,0.05); box-shadow: 0 1px 2px rgba(0,0,0,0.018), 0 4px 14px rgba(0,0,0,0.022); }
  66%       { opacity: 0.45; transform: translateY(0) scale(0.86); border-color: rgba(0,0,0,0.05); box-shadow: 0 1px 2px rgba(0,0,0,0.018), 0 4px 14px rgba(0,0,0,0.022); }
  69%       { opacity: 0.95; transform: translateY(0) scale(0.92); border-color: rgba(184,59,29,0.5); box-shadow: 0 0 0 5px rgba(184,59,29,0.10), 0 1px 2px rgba(0,0,0,0.025), 0 8px 22px rgba(184,59,29,0.18); }
  73%       { opacity: 0.95; transform: translateY(0) scale(0.92); border-color: rgba(184,59,29,0.5); box-shadow: 0 0 0 5px rgba(184,59,29,0.10), 0 1px 2px rgba(0,0,0,0.025), 0 8px 22px rgba(184,59,29,0.18); }
  76%       { opacity: 0.55; transform: translateY(0) scale(0.88); border-color: rgba(184,59,29,0.18); box-shadow: 0 0 0 10px rgba(184,59,29,0), 0 1px 2px rgba(0,0,0,0.02), 0 5px 16px rgba(0,0,0,0.04); }
  80%, 100% { opacity: 0.45; transform: translateY(0) scale(0.86); border-color: rgba(0,0,0,0.05); box-shadow: 0 1px 2px rgba(0,0,0,0.018), 0 4px 14px rgba(0,0,0,0.022); }
}

/* Shared card fade-in. The end state is opacity: 0.45 (lowered from 0.7)
   so the four floating artefact cards recede further into the periphery
   and the central animation (PowerPoint mockup) becomes the undisputed
   focal point. Hover restores opacity: 1 for users who want to read. */
@keyframes heroCardFadeIn {
  from {
    opacity: 0;
    transform: translateY(24px) scale(0.78);
  }
  to {
    opacity: 0.45;
    transform: translateY(0) scale(0.86);
  }
}

/* Cards scroll wrapper — invisible on desktop, horizontal carousel on mobile */
.landing-hero__cards-scroll {
  display: contents; /* no layout impact on desktop */
}

/* Base floating card style — softer shadows than before so the cards
   sit in the periphery of the hero rather than dominating it. Default
   opacity comes from the heroCardFadeIn keyframe (ends at 0.7). The
   opacity transition is added so hover restores full contrast
   smoothly. */
/* Floating cards scaled to 75% (25% smaller) so the central animation
   is the undisputed focal point of the hero. */
.landing-hero__float-card {
  position: absolute;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  padding: 1rem 1.25rem;
  /* Softer shadows + subtle blur on background means the cards feel like
     atmospheric supporting evidence rather than equal-weight UI. */
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.018),
    0 4px 14px rgba(0, 0, 0, 0.022);
  z-index: 1;
  opacity: 0;
  pointer-events: auto;
  cursor: default;
  transform: scale(0.86);
  transition:
    transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
    box-shadow 0.3s ease,
    opacity 0.3s ease,
    border-color 0.3s ease;
}

.landing-hero__float-card:hover {
  opacity: 1 !important;
  transform: scale(1.035);
  box-shadow:
    0 2px 6px rgba(0, 0, 0, 0.06),
    0 12px 40px rgba(0, 0, 0, 0.1);
}

/* Card header */
.float-card__header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.float-card__status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
}

.float-card__header-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1a1a1a;
  letter-spacing: -0.01em;
}

.float-card__header-badge {
  margin-left: auto;
  font-size: 0.65rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.45);
  background: rgba(0, 0, 0, 0.04);
  padding: 0.15rem 0.5rem;
  border-radius: 100px;
}

/* ---- Source Analysis — left edge, bleeding off ---- */
.landing-hero__float-card--sources {
  /* Pulled DOWN from 14% to 30% so it clears the title/subhead/CTA zone
     and frames the upper-left of the (now larger) mockup instead of
     crowding the headline. Nudged up 20px per design. */
  top: calc(42% - 20px);
  /* Pushed further off the left edge so it clears the enlarged mockup and
     reads as peripheral framing rather than crowding it. */
  left: -95px;
  width: 260px;
  font-size: 0.85em;
  animation: heroCardLifecycleSources 15s ease infinite;
}

.float-card__table-header {
  display: flex;
  gap: 0.5rem;
  padding-bottom: 0.4rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  margin-bottom: 0.25rem;
}

.float-card__table-th {
  font-size: 0.6rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.float-card__table-th:first-child {
  flex: 1;
}

.float-card__table-th--right {
  text-align: right;
  width: 60px;
}

.float-card__table-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}

.float-card__table-row:last-child {
  border-bottom: none;
}

.float-card__table-name {
  flex: 1;
  font-size: 0.72rem;
  font-weight: 450;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.float-card__tag {
  font-size: 0.55rem;
  font-weight: 600;
  padding: 0.12rem 0.4rem;
  border-radius: 4px;
  flex-shrink: 0;
}

.float-card__tag--report {
  background: rgba(184, 59, 29, 0.08);
  color: #B83B1D;
}

.float-card__tag--database {
  background: rgba(8, 145, 178, 0.08);
  color: #0891b2;
}

.float-card__tag--filing {
  background: rgba(124, 58, 237, 0.08);
  color: #7c3aed;
}

.float-card__tag--api {
  background: rgba(22, 163, 74, 0.08);
  color: #16a34a;
}

.float-card__table-value {
  font-size: 0.72rem;
  font-weight: 600;
  color: #1a1a1a;
  width: 60px;
  text-align: right;
  flex-shrink: 0;
}

/* ---- Data Triangulation — BOTTOM-right (was upper-right; swapped with
   Revenue Projection so the slide-built output reads first at the top
   and validation lives lower down). The class name is left as
   --quality (no backticks here — they terminate the template literal)
   to avoid renaming a long descendant chain. */
.landing-hero__float-card--quality {
  /* Bottom card (Data Triangulation). Pushed well down per design so it
     frames the mockup's lower-right rather than its middle. */
  bottom: 5%;
  /* This class wraps the "Data Triangulation" JSX. User clarified that
     Data Triangulation should NOT move inward — they meant Data Analysis
     (the Source Analysis card) instead. Reverted to the original neutral
     position. */
  right: -100px;
  width: 250px;
  font-size: 0.85em;
  animation: heroCardLifecycleQuality 15s ease infinite;
}

.float-card__check-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0;
  font-size: 0.75rem;
  color: #444;
}

.float-card__check-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(184, 59, 29, 0.08);
  color: #B83B1D;
  font-size: 0.6rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.float-card__divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.06);
  margin: 0.6rem 0;
}

.float-card__confidence-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.float-card__confidence-label {
  font-size: 0.7rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.45);
  flex-shrink: 0;
}

.float-card__confidence-track {
  flex: 1;
  height: 6px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 3px;
  overflow: hidden;
}

.float-card__confidence-fill {
  width: 92%;
  height: 100%;
  background: linear-gradient(90deg, #B83B1D, #D4694E);
  border-radius: 3px;
}

.float-card__confidence-value {
  font-size: 0.75rem;
  font-weight: 700;
  color: #B83B1D;
  flex-shrink: 0;
}

/* ---- Revenue Projection — TOP-right (was bottom-right; swapped with
   Data Triangulation). Reads as the slide's headline output. */
.landing-hero__float-card--revenue {
  /* Pulled DOWN from 14% to 30% (matches the Source Analysis card) so it
     clears the headline/CTA zone and frames the upper-right of the
     larger mockup. Nudged up 20px per design. */
  top: calc(42% - 20px);
  /* Pushed further off the right edge so the enlarged mockup has room and
     only a partial slice of the card reads in the periphery. */
  right: -115px;
  width: 280px;
  font-size: 0.85em;
  animation: heroCardLifecycleRevenue 15s ease infinite;
}

.float-card__growth-badge {
  margin-left: auto;
  font-size: 0.7rem;
  font-weight: 700;
  color: #16a34a;
  background: rgba(22, 163, 74, 0.08);
  padding: 0.2rem 0.5rem;
  border-radius: 100px;
}

.float-card__metric {
  font-size: 2.25rem;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.03em;
  line-height: 1;
}

.float-card__metric-sub {
  font-size: 0.72rem;
  font-weight: 450;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 1rem;
}

.float-card__chart-bars {
  display: flex;
  align-items: flex-end;
  gap: 0.4rem;
  height: 72px;
}

.float-card__chart-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  height: 100%;
  justify-content: flex-end;
}

.float-card__chart-bar {
  width: 100%;
  border-radius: 3px 3px 0 0;
  background: rgba(184, 59, 29, 0.15);
}

.float-card__chart-bar--1 { height: 30%; }
.float-card__chart-bar--2 { height: 38%; }
.float-card__chart-bar--3 { height: 45%; }
.float-card__chart-bar--4 { height: 52%; }
.float-card__chart-bar--5 { height: 60%; }
.float-card__chart-bar--6 { height: 68%; }
.float-card__chart-bar--highlight {
  height: 85%;
  background: linear-gradient(180deg, #B83B1D, #D4694E);
  box-shadow: 0 2px 8px rgba(184, 59, 29, 0.2);
}
.float-card__chart-bar--7 { height: 78%; }

.float-card__chart-month {
  font-size: 0.5rem;
  font-weight: 450;
  color: rgba(0, 0, 0, 0.35);
  text-transform: uppercase;
}

.float-card__chart-month--active {
  color: #B83B1D;
  font-weight: 600;
}

/* ---- Quality Check — BOTTOM-LEFT (renamed from "Slide Verification" in
   JSX; class name is still --triangulation to avoid renaming the long
   .float-tri__* descendant chain). Maps to Step 4 in the chat trail. */
.landing-hero__float-card--triangulation {
  /* Bottom card (Quality Check). Pushed well down per design so it frames
     the mockup's lower-left rather than its middle. */
  bottom: 5%;
  /* This class wraps the "Quality Check" JSX (renamed from Slide
     Verification). User wants this LESS visible — pulled ~20px further
     off the left edge from 30px → 10px so it sits more in the periphery.
     The data-validation card on the right gets the focus instead. */
  left: -60px;
  width: 220px;
  font-size: 0.85em;
  animation: heroCardLifecycleTriangulation 15s ease infinite;
}

.float-tri__metric-row {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  margin-bottom: 0.15rem;
}

.float-tri__metric {
  font-size: 1.85rem;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.03em;
  line-height: 1;
}

.float-tri__verified-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.65rem;
  font-weight: 600;
  color: #16a34a;
  background: rgba(22, 163, 74, 0.08);
  padding: 0.2rem 0.5rem;
  border-radius: 100px;
}

.float-tri__metric-label {
  font-size: 0.72rem;
  font-weight: 450;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 0.85rem;
}

.float-tri__sources {
  display: flex;
  flex-direction: column;
  gap: 0;
  border-left: 2px solid rgba(22, 163, 74, 0.2);
  padding-left: 0.85rem;
  margin-left: 0.25rem;
  margin-bottom: 0.85rem;
}

.float-tri__source-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  position: relative;
}

.float-tri__source-row:last-child {
  border-bottom: none;
}

.float-tri__source-dot {
  position: absolute;
  left: -1.15rem;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #16a34a;
  border: 2px solid #ffffff;
}

.float-tri__source-name {
  flex: 1;
  font-size: 0.68rem;
  font-weight: 450;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.float-tri__source-value {
  font-size: 0.72rem;
  font-weight: 600;
  color: #1a1a1a;
  flex-shrink: 0;
}

.float-tri__source-check {
  font-size: 0.6rem;
  font-weight: 700;
  color: #16a34a;
  flex-shrink: 0;
}

.float-tri__summary {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.68rem;
  font-weight: 500;
  color: #16a34a;
  background: rgba(22, 163, 74, 0.05);
  padding: 0.45rem 0.65rem;
  border-radius: 8px;
}

.float-tri__variance {
  color: rgba(0, 0, 0, 0.4);
  font-weight: 450;
  margin-left: auto;
}

/* Reduced motion: show static fully-visible state */
@media (prefers-reduced-motion: reduce) {
  .pptx-slide__title,
  .pptx-slide__subtitle,
  .pptx-slide__divider,
  .pptx-slide__bullet,
  .pptx-slide__chart-bar,
  .pptx-slide__sources,
  .pptx-slide__insight,
  .pptx-slide__chart-title,
  .pptx-slide__chart-cagr,
  .pptx-slide__methodology,
  .pptx-slide__page-num,
  .landing-hero__pptx-slide,
  .pptx-sidebar__msg,
  .pptx-sidebar__thinking,
  .pptx-sidebar__dot,
  .pptx-sidebar__step,
  .pptx-sidebar__source-item,
  .pptx-sidebar__analysis-fill,
  .pptx-sidebar__analysis-bar::after,
  .pptx-sidebar__crossref-item,
  .pptx-sidebar__quality-item,
  .pptx-sidebar__build-item,
  .pptx-sidebar__build-spinner,
  .pptx-slide__edit-cursor,
  .pptx-slide__edit-select,
  .pptx-sidebar__analysis-value,
  .pptx-slide__chart-wrapper,
  .landing-hero__pptx-thumb--active .landing-hero__pptx-thumb-slide {
    animation: none !important;
    opacity: 1 !important;
    transform: none !important;
    clip-path: none !important;
    max-height: none !important;
    width: 100% !important;
  }

  /* Hide edit interaction elements in reduced motion */
  .pptx-slide__edit-cursor,
  .pptx-slide__edit-select {
    display: none !important;
  }

  /* In reduced-motion, show all 5 step cards in their DONE state — a
     static checklist of completed steps with detail panels hidden. */
  .pptx-sidebar__step .pptx-sidebar__step-icon--spinner,
  .pptx-sidebar__step .pptx-sidebar__step-label--active {
    opacity: 0 !important;
  }
  .pptx-sidebar__step .pptx-sidebar__step-icon--check,
  .pptx-sidebar__step .pptx-sidebar__step-label--done {
    opacity: 1 !important;
  }
  .pptx-sidebar__step-detail {
    max-height: 0 !important;
    opacity: 0 !important;
    overflow: hidden !important;
  }

  /* Floating elements — static visible state */
  .landing-hero__float-card,
  .landing-hero__pptx-mockup-wrapper {
    animation: none !important;
    opacity: 1 !important;
    transform: scale(0.86) !important;
  }
}

/* Responsive - Large screens (1400px+) */
@media (min-width: 1400px) {
  .landing-hero__float-card--sources {
    /* "Source Analysis" = user's "Data Analysis" card. Moved ~20px
       INWARD from -76px → -56px so more of the table reads on the left
       edge. This is the card the user wanted more visible. */
    left: -56px;
  }
  .landing-hero__float-card--quality {
    /* This class wraps "Data Triangulation". Pushed further off the right
       edge to clear the enlarged mockup on wide screens. */
    right: -60px;
  }
  .landing-hero__float-card--triangulation {
    /* This class wraps "Quality Check". Nudged further off the left edge
       so it does not touch the wider mockup. */
    left: -16px;
  }
  .landing-hero__float-card--revenue {
    /* Further off the right edge so the larger mockup has room. */
    right: -76px;
  }
  .landing-hero__pptx-mockup-wrapper {
    max-width: 1100px;
  }
  .landing-hero__pptx-mockup {
    max-width: 760px;
    /* Wide viewports have room for the full ~15% bump. */
    transform: scale(1.38);
  }
}

/* Responsive - Medium large screens */
@media (max-width: 1200px) {
  .landing-hero__float-card--sources {
    left: -140px;
    transform: scale(0.9);
  }
  .landing-hero__float-card--quality {
    right: -60px;
    transform: scale(0.9);
  }
  .landing-hero__float-card--revenue {
    right: -160px;
    transform: scale(0.85);
  }
}

/* Responsive - Tablet: show cards as carousel, keep mockup centered */
@media (max-width: 1024px) {
  .landing-hero {
    height: auto;
    min-height: auto;
    max-height: none;
    overflow: visible;
    padding-top: 70px;
  }

  .landing-hero__content {
    padding: 2rem;
    order: 1;
  }

  .landing-hero__cta-row {
    order: 2;
  }

  .landing-hero__headline-wrapper {
    max-width: 100%;
    min-height: 80px;
  }

  .landing-hero__input-wrapper {
    max-width: 100%;
  }

  /* Hide decorative SVG */
  .landing-hero__deco-svg {
    display: none !important;
  }

  /* Tablet card carousel — order 4 puts artefacts BELOW the logo strip
     (which is order 3) so trust signals follow the animation directly. */
  .landing-hero__cards-scroll {
    display: flex !important;
    gap: 1rem;
    padding: 1rem 2rem;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    order: 5;
    width: 100%;
  }

  .landing-hero__cards-scroll::-webkit-scrollbar {
    display: none;
  }

  .landing-hero__float-card {
    position: relative !important;
    display: block !important;
    opacity: 1 !important;
    animation: none !important;
    top: auto !important;
    bottom: auto !important;
    left: auto !important;
    right: auto !important;
    flex-shrink: 0;
    width: 250px;
    scroll-snap-align: start;
  }

  .landing-hero__pptx-mockup-wrapper {
    padding: 1rem 2rem;
    order: 3;
  }

  .landing-hero__pptx-mockup {
    transform: none;
  }

  .landing-hero__logo-marquee {
    order: 4;
  }

  .landing-trusted-by {
    padding: 1.5rem 2rem;
  }
}

/* Tablet (601-1199px): shrink the PowerPoint frame so the Excel + Word side
   windows have room to flank it — all three Office surfaces show on tablet,
   not just desktop. Mobile (<=600) keeps its own sizing + fan-below layout. */
@media (min-width: 601px) and (max-width: 1199px) {
  .landing-hero__pptx-mockup {
    transform: scale(0.72);
    transform-origin: top center;
    margin-bottom: -100px;
  }
}

/* Responsive - Mobile */
@media (max-width: 600px) {
  .landing-header {
    padding: 0.75rem 1rem;
  }

  .landing-hero {
    height: auto !important;
    min-height: auto !important;
    max-height: none !important;
    padding-top: 112px;
    gap: 0;
    overflow: visible !important;
    width: 100% !important;
    margin-left: 0 !important;
  }

  .landing-hero::before {
    display: none !important;
  }

  .landing-hero__content {
    padding: 1.25rem 1.25rem 0;
    gap: 0.75rem !important;
  }

  .landing-hero__headline-wrapper {
    min-height: auto;
    max-width: 100%;
    opacity: 1 !important;
    animation: none !important;
    gap: 0.5rem;
  }

  .landing-hero__headline {
    font-size: clamp(1.5rem, 7vw, 2rem);
    line-height: 1.2;
    letter-spacing: -0.015em;
    white-space: normal;
    opacity: 1 !important;
  }

  .landing-hero__subtitle {
    font-size: 0.9375rem;
    line-height: 1.5;
    max-width: 32ch;
    white-space: normal;  /* override desktop's nowrap so it wraps on phones */
    margin-left: auto;
    margin-right: auto;
    opacity: 1 !important;
  }

  /* Hide decorative SVG on mobile */
  .landing-hero__deco-svg {
    display: none !important;
  }

  /* Mobile card carousel — horizontal scroll with edge fade hint.
     Order 4: cards now sit BELOW the logo strip on mobile, so the
     visual flow is title → animation → social proof (logos) → artefacts.
     This puts trust signals immediately under the hero and demotes
     the artefact cards (which are decorative on mobile anyway). */
  .landing-hero__cards-scroll {
    display: flex !important;
    gap: 0.75rem;
    padding: 0.75rem 1.25rem;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    scroll-padding-left: 1.25rem;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    order: 4;
    width: 100%;
    /* Fade the right edge to suggest there's more to scroll */
    -webkit-mask-image: linear-gradient(to right, #000 0, #000 calc(100% - 40px), rgba(0,0,0,0.35) 100%);
            mask-image: linear-gradient(to right, #000 0, #000 calc(100% - 40px), rgba(0,0,0,0.35) 100%);
  }

  .landing-hero__cards-scroll::-webkit-scrollbar {
    display: none;
  }

  .landing-hero__float-card {
    position: relative !important;
    display: block !important;
    opacity: 1 !important;
    animation: none !important;
    top: auto !important;
    bottom: auto !important;
    left: auto !important;
    right: auto !important;
    flex-shrink: 0;
    width: 230px;
    scroll-snap-align: start;
    font-size: 0.78em;
  }

  .landing-hero__float-card--sources {
    width: 250px;
  }

  .landing-hero__float-card--quality {
    width: 240px;
  }

  .landing-hero__float-card--revenue {
    width: 250px;
  }

  /* Don't wrap the card header on mobile */
  .landing-hero__float-card .float-card__header-title {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }
  .landing-hero__float-card .float-card__header-badge {
    white-space: nowrap;
    flex-shrink: 0;
  }

  /* Flex order for mobile layout flow:
       1 → headline content
       2 → main animation
       3 → trust signals (logo strip)
       4 → artefact cards (peripheral / decorative)
     The user explicitly asked for cards-AFTER-logos on mobile so the
     trust signals get priority real estate immediately under the hero. */
  .landing-hero__content {
    order: 1;
  }

  .landing-hero__cta-row {
    order: 2;
  }

  .landing-hero__pptx-mockup-wrapper {
    order: 3;
  }

  .landing-hero__logo-marquee {
    order: 4;
  }

  /* Mobile mockup — kept at the original 680px design width (so internal
     proportions and the sidebar layout don't break) but scaled down to
     fit the phone viewport. Removed the previous overflow:hidden +
     negative margin-bottom combination — those were clipping the bottom
     of the slide and bleeding into the logo strip below. New approach:
     wrapper has no clipping, and the scaled mockup's empty layout space
     is collapsed via a calc() margin-bottom that's mathematically correct
     for the chosen scale (rather than an empirically-tuned -175px). */
  .landing-hero__pptx-mockup-wrapper {
    padding: 0 !important;
    margin: 80px auto 24px !important;
    overflow: visible !important;
    display: flex !important;
    justify-content: center !important;
    align-items: flex-start !important;
    width: 100% !important;
    max-width: 100vw !important;
  }

  .landing-hero__pptx-mockup {
    width: 680px !important;
    max-width: none !important;
    flex-shrink: 0;
    /* Shrunk further than a full-width fit so the Excel + Word side windows
       can peek beside it — the same three-window flanking structure as
       desktop, just scaled down for the phone. */
    transform: scale(0.46);
    transform-origin: top center;
    margin-top: 0;
    /* For transform-origin: top center at scale(s), the scaled element
       visually occupies (s × original_height) starting at the top edge.
       The remaining (1 − s) × original_height of layout space is empty.
       Pull subsequent content up by exactly that amount so the next
       element sits flush against the visual bottom of the scaled mockup
       without overlap or trapped whitespace. */
    margin-bottom: calc(-54% + 8px);
    margin-left: auto;
    margin-right: auto;
  }

  /* Show floating artifact cards as a horizontal carousel on mobile.
     order: 4 — sits BELOW the logo strip (per user request) so trust
     signals immediately follow the hero animation, with the artefact
     cards demoted to peripheral/decorative status. */
  .landing-hero__cards-scroll {
    display: flex !important;
    gap: 0.75rem;
    padding: 0.75rem 1.25rem;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    scroll-padding-left: 1.25rem;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    order: 5;
    width: 100%;
    -webkit-mask-image: linear-gradient(to right, #000 0, #000 calc(100% - 40px), rgba(0,0,0,0.35) 100%);
            mask-image: linear-gradient(to right, #000 0, #000 calc(100% - 40px), rgba(0,0,0,0.35) 100%);
  }

  .landing-hero__pptx-sidebar {
    border-left: 1px solid #e0e0e0;
    border-top: none;
  }

  .landing-hero__cta-wrapper {
    padding: 0;
    opacity: 1 !important;
    animation: none !important;
    width: 100%;
  }

  .landing-hero__input-wrapper {
    max-width: 100%;
    width: 100%;
    opacity: 1 !important;
    animation: none !important;
    display: block !important;
  }

  .landing-hero__composer {
    width: 100%;
  }

  .landing-hero__logo-marquee {
    padding: 0.75rem 0;
  }

  .landing-hero__logo-marquee-label {
    font-size: 0.6rem;
    padding: 0 1rem;
    text-align: center;
  }

  /* Tighter slot sizing on mobile so each logo reads cleanly without
     dominating the strip. Combined with the now-correctly-animating
     marquee (heroLogoScroll keyframe was empty before), the mobile
     strip moves and feels balanced instead of static and oversized. */
  .landing-hero__logo-marquee-track {
    gap: 2.5rem;
    animation-duration: 60s;
  }
  .landing-hero__logo-marquee-slot {
    width: 96px;
    height: 28px;
  }

  .landing-trusted-by {
    padding: 1rem;
    width: 100% !important;
    margin-left: 0 !important;
  }
}

/* Small Mobile */
@media (max-width: 400px) {
  .landing-hero__headline {
    font-size: 1.35rem;
  }

  /* Smaller scale for narrow viewports. margin-bottom uses the same
     mathematical correction (1 − scale) × layout-height-as-percentage
     so the scaled mockup sits flush against the next element without
     clipping or trapped space. */
  .landing-hero__pptx-mockup {
    transform: scale(0.42) !important;
    margin-bottom: calc(-58% + 8px) !important;
  }
}
`;
